/**
 * Tab content for the tenant profile (Overview · Payments · Move-in ·
 * Move-out · Documents). Split out of TenantProfileDialog to keep the shell
 * readable. Visuals mirror the handoff prototype 1:1 via inline styles.
 */
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { formatDate, formatDateTime, formatEUR } from "@/lib/v2-format";
import { countryName, requiresResidencePermit } from "@/lib/nationality";
import { formatDateNumeric } from "@/lib/date-format";
import {
  docGroup,
  labelForDocType,
} from "@/components/admin/v2/tenant/tenant-document-labels";
import { ReferralSection } from "./ReferralSection";
import { useReferralCreditNotes } from "@/hooks/v2/useReferralCreditNotes";
import {
  referralCreditNoteText,
  type ReferralCreditNote,
} from "@/lib/referrals";
import {
  kickbackBaseCents,
  kickbackCents,
  kickbackGrossCents,
} from "@/lib/rent-kickback";
import { businessToday } from "@/lib/businessDate";
import {
  isBookingContractSigned,
  isBookingContractSignedIncludingManual,
} from "@/lib/leaseStatus";
import {
  isContractSentForSignature,
  isContractSignedForJourney,
  TENANT_JOURNEY_STAGES,
  type TenantJourney,
  type TenantJourneyStageId,
  isDepositEditLocked,
} from "@/lib/tenantJourney";
import { tenantInfoGapLabel } from "@/lib/tenantInfoGaps";
import {
  clampDueDayInMonth,
  getExpectedRentCentsForDueCalendarMonth,
  isPaymentEffectivelyPaid,
  ordinalLabel,
  isOpenRentStatus,
  resolvePaymentDisplayStatus,
  resolveRentSettlement,
  rentScheduleWindowEnd,
  splitRentRows,
} from "@/lib/rent";
import {
  computeCurrentStage,
  computeDaysOverdue,
  reminderScheduleLines,
  type ReminderStage,
} from "@/lib/rent-reminder-templates";
import {
  resolveEffectiveTemplateId,
  useContractTemplates,
} from "@/hooks/v2/useContractTemplates";
import {
  CONTRACT_DEPLOY_STEPS,
  useContractFlowReady,
} from "@/hooks/v2/useContractFlowReady";
import {
  CHIP,
  actionBtn,
  cardWrap,
  chip,
  Chevron,
  CollapsibleCard,
  innerBox,
  SectionIcon,
  UndoBanner,
  uppLabel,
  whitePanel,
  IcBank,
  IcBell,
  IcEdit,
  IcEye,
  IcNotes,
  IcPlus,
  IcProof,
  IcReceipt,
  IcTrash,
  IcUpload,
  RowActionsMenu,
  type RowAction,
} from "./tenant-profile-ui";
import type { TenantSepaStatus } from "@/hooks/v2/usePaymentsAdmin";
import { noteAttachmentHref } from "@/hooks/v2/useEntityNotes";
import { resolveTenantDocumentUrl } from "@/lib/tenant-document-url";
import { isOpenableUrl, openPendingDocumentTab } from "@/lib/open-document-tab";
import { useProtocolSummary } from "@/hooks/useProtocol";
import { type FiledTenantDocument } from "@/hooks/v2/useApartmentDocuments";
import { openStoredFile, resolveStoredFileUrl } from "@/lib/storage-url";
import { useSignedStoredUrl } from "@/hooks/useSignedStoredUrl";
import { StoredFileLink } from "@/components/shared/StoredFileLink";
import {
  computeMoveInWindowStatus,
  computeMoveOutWindowStatus,
} from "@/lib/protocol-window";
import { isAdminRecorded, isRealSubmission } from "@/lib/protocol";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AdminNotesSection } from "./AdminNotesSection";
import { RecordPaymentDialog } from "./RecordPaymentDialog";
import type { PaymentReceipt } from "@/hooks/v2/usePaymentReceipts";
import { PaymentReceiptsStrip } from "./PaymentReceiptsStrip";
import { DepositReturnCard } from "./DepositReturnCard";
import { LifecycleEmailsSection } from "./LifecycleEmailsSection";
import {
  CONTRACT_DOC_ID,
  DOC_NOTE_PREFIX,
  resolveDocTarget,
} from "./resolveDocTarget";
import {
  AUDIT_TITLE_KEYS,
  AUDIT_UPDATED_PREFIX_KEY,
  auditActionTitle,
  auditLabels,
  describeChangedFields,
  isReversalAction,
} from "./tenant-audit-titles";
import { rentDiscountOffEURForMonth } from "@/lib/rent-discount";
import { TenantDiscountNote } from "./TenantDiscountNote";
import { RoomBaseDriftNotice } from "./RoomBaseDriftNotice";
import type { SettlementRow } from "@/lib/depositSettlement";
import type { DepositDeductionRow } from "@/hooks/v2/useDepositSettlement";
import type { DepositReleaseLogEvent } from "@/lib/depositReleaseApi";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  accruedFeeCents,
  feeCarrierPaymentId,
  owedWithFees,
} from "@/lib/rent-owed-with-fees";

const money = (v: number | null | undefined) => formatEUR(v ?? 0) ?? "€0.00";
const centsToEUR = (c: number | null | undefined) =>
  formatEUR((c ?? 0) / 100) ?? "€0.00";

interface ChatAttachment {
  name: string;
  url: string;
  type?: string;
  size?: number;
}
/** Decode the `CHATENV:{…}` message envelope used by the support chat. */
function decodeChat(raw: string | null | undefined): {
  text: string;
  atts: ChatAttachment[];
} {
  const s = String(raw ?? "");
  if (s.startsWith("CHATENV:")) {
    try {
      const env = JSON.parse(s.slice(8));
      return { text: env.text ?? "", atts: env.attachments ?? [] };
    } catch {
      /* fall through */
    }
  }
  return { text: s, atts: [] };
}
function fmtSize(bytes?: number): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Open a note attachment held in the private `admin-notes` bucket.
 *
 * The bucket is admin-only and NOT public, so there is no stored URL to link
 * to — a five-minute link is minted per click. Same move the notes drawer makes
 * (NoteAttachment in note-ui.tsx); this is the copy for the surfaces that show
 * the same file outside the drawer.
 */
async function openNoteAttachment(
  path: string,
  opts?: { download?: boolean; fileName?: string | null }
) {
  // Open the tab inside the click: a window.open after the signing await is
  // a popup Safari blocks, silently.
  const tab = openPendingDocumentTab();
  try {
    const href = await noteAttachmentHref(
      { file_url: path, file_name: opts?.fileName ?? null },
      { download: opts?.download }
    );
    if (!href) throw new Error("This attachment is no longer available.");
    tab.navigate(href);
  } catch (e) {
    tab.cancel();
    toast.error(
      e instanceof Error ? e.message : "Could not open the attachment."
    );
  }
}

/**
 * Open a `tenant_documents.file_url` — a bare or bucket-qualified Storage
 * path (e.g. a handover-protocol PDF at "handover-protocols/pdfs/<id>.pdf"),
 * never a browser URL. Used directly as an `<a href>` it resolves against
 * the SPA route and 404s, so it has to be signed first. The tab opens
 * synchronously inside the click so the popup blocker doesn't eat it.
 */
async function openSignedTenantDocument(
  path: string,
  opts?: { download?: boolean; fileName?: string | null }
) {
  const tab = openPendingDocumentTab();
  try {
    const href = await resolveTenantDocumentUrl(
      path,
      opts?.download ? { download: opts.fileName || true } : undefined
    );
    if (!href) throw new Error("This document is no longer available.");
    tab.navigate(href);
  } catch (e) {
    tab.cancel();
    toast.error(
      e instanceof Error ? e.message : "Could not open the document."
    );
  }
}

/**
 * Open a file that lives in `contracts` unless its URL says otherwise: a
 * contract PDF (bookings.*contract*_url), a support-chat attachment, or a
 * filed apartment document. Same rule as the two helpers above — the bucket
 * is going private, so the link is minted on click (COL-436). `failText` is
 * the caller's translated "could not open".
 */
async function openContractsFile(
  value: string,
  failText: string,
  opts?: { download?: boolean; fileName?: string | null }
) {
  const ok = await openStoredFile(
    value,
    "contracts",
    opts?.download ? { download: opts.fileName || true } : undefined
  );
  if (!ok) toast.error(failText);
}

/** Which signer an image card's thumbnail goes through. */
type ThumbSource = "note" | "tenantDoc" | "contracts";

/**
 * A short-lived signed URL for an image card's thumbnail. Note files sign
 * through noteAttachmentHref, tenant documents through
 * resolveTenantDocumentUrl, everything else (chat photos, filed documents)
 * against the contracts bucket: none is a public link any more (COL-436).
 * Null until signed, or when signing fails (the card then shows its file
 * icon).
 */
function useSignedThumb(
  url: string | null,
  source: ThumbSource
): string | null {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    setSrc(null);
    if (!url) return;
    let live = true;
    (source === "note"
      ? noteAttachmentHref({ file_url: url })
      : source === "tenantDoc"
        ? resolveTenantDocumentUrl(url)
        : resolveStoredFileUrl(url, "contracts")
    ).then(
      (signed) => {
        if (live) setSrc(signed);
      },
      () => {}
    );
    return () => {
      live = false;
    };
  }, [url, source]);
  return src;
}

/** A single document/attachment card — shared by the Documents tab and the
 *  support chat so every file is arranged the same way. */
function FileCard({
  name,
  sub,
  badges,
  url,
  signThumb,
  privateNotePath,
  signedDocPath,
  note,
  noteMeta,
  onRename,
  onNote,
  onDelete,
  onEditLabel,
}: {
  name: string;
  sub?: string;
  badges?: ReactNode;
  url: string | null;
  /**
   * `url` is an image in a private (or going-private) bucket: sign it for the
   * thumbnail instead of using it as `<img src>`.
   */
  signThumb?: boolean;
  /**
   * True when `url` is an `admin_notes.file_url`: a path in the private
   * `admin-notes` bucket, or the public tenant-documents URL a handover note
   * stored (signed too, COL-436). Such a path
   * is not a link: as an href it resolves against the SPA route and 404s, and
   * the bucket has no public URL to fall back on. It has to be signed on click.
   */
  privateNotePath?: boolean;
  /**
   * True when `url` is a `tenant_documents.file_url` Storage path (bare, or
   * bucket-qualified like "handover-protocols/pdfs/<id>.pdf") rather than a
   * browser URL — same 404 risk as `privateNotePath`, resolved via
   * `resolveTenantDocumentUrl` instead of the admin-notes signer.
   */
  signedDocPath?: boolean;
  /** Saved document note — rendered on the card so it's visibly stored. */
  note?: string | null;
  /** "Sara Klein · 31 Jul 2026, 14:37" — who last saved the note, and when. */
  noteMeta?: string | null;
  onRename?: () => void;
  onNote?: () => void;
  onDelete?: () => void;
  /** Change the document's label (tenant_documents.document_type). */
  onEditLabel?: () => void;
}) {
  const { t } = useLanguage();
  const [thumbFailed, setThumbFailed] = useState(false);
  const thumbSrc = useSignedThumb(
    signThumb ? url : null,
    privateNotePath ? "note" : signedDocPath ? "tenantDoc" : "contracts"
  );
  const openFailed = t("admin.docs.openFailed", "Could not open the document.");
  const hasActions = !!(url || onRename || onNote || onDelete || onEditLabel);
  const noteText = note?.trim() || "";
  const hasNote = noteText.length > 0;
  const iconBtn: React.CSSProperties = {
    width: 32,
    height: 32,
    borderRadius: 9,
    border: "1px solid #E7E9EC",
    background: "#fff",
    color: "#64748B",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  };
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        padding: "14px 16px",
        border: "1px solid #EDEFF2",
        borderRadius: 14,
        background: "#fff",
      }}
    >
      {thumbSrc && !thumbFailed ? (
        <img
          src={thumbSrc}
          alt=""
          loading="lazy"
          onError={() => setThumbFailed(true)}
          style={{
            width: 38,
            height: 38,
            borderRadius: 10,
            objectFit: "cover",
            flexShrink: 0,
            border: "1px solid #EDEFF2",
          }}
        />
      ) : (
        <SectionIcon bg="#EAF1FE" color="#3B82F6" size={38}>
          <FileIcon size={18} />
        </SectionIcon>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13.5,
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {name}
        </div>
        {badges && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              flexWrap: "wrap",
              marginTop: 5,
            }}
          >
            {badges}
          </div>
        )}
        {sub && (
          <div
            style={{
              fontSize: 11.5,
              color: "#94A3B8",
              fontWeight: 500,
              marginTop: 5,
            }}
          >
            {sub}
          </div>
        )}
        {/* The saved note renders on the card itself — previously it was written
            to tenant_documents.notes and never shown, so admins had no way to
            tell where an added note had gone. */}
        {hasNote && (
          <div
            style={{
              display: "flex",
              gap: 7,
              alignItems: "flex-start",
              marginTop: 8,
              padding: "7px 9px",
              background: "#FFFBEB",
              border: "1px solid #F7E4BD",
              borderRadius: 9,
            }}
          >
            <span style={{ color: "#B45309", flexShrink: 0, marginTop: 1 }}>
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M15.5 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h9l7-7V5a2 2 0 0 0-2-2Z" />
                <path d="M14 21v-5a2 2 0 0 1 2-2h5" />
              </svg>
            </span>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  ...uppLabel,
                  fontSize: 9.5,
                  color: "#B45309",
                  marginBottom: 2,
                }}
              >
                {t("admin.tenantTabs.note", "Note")}
              </div>
              {/* Clamped to 4 lines so one long note can't stretch its card
                  out of line with the rest of the grid — full text on hover. */}
              <div
                title={noteText}
                style={{
                  fontSize: 12,
                  color: "#78350F",
                  fontWeight: 500,
                  whiteSpace: "pre-wrap",
                  overflowWrap: "anywhere",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 4,
                  overflow: "hidden",
                }}
              >
                {noteText}
              </div>
              {noteMeta && (
                <div
                  style={{
                    fontSize: 11,
                    color: "#B45309",
                    opacity: 0.85,
                    fontWeight: 600,
                    marginTop: 4,
                  }}
                >
                  {noteMeta}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {hasActions && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              title={t("admin.tenantTabs.actions", "Actions")}
              aria-label={`${t("admin.tenantTabs.actionsForPrefix", "Actions for")} ${name}`}
              style={{ ...iconBtn, cursor: "pointer" }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
                stroke="none"
              >
                <circle cx="12" cy="5" r="1.6" />
                <circle cx="12" cy="12" r="1.6" />
                <circle cx="12" cy="19" r="1.6" />
              </svg>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[168px]">
            {/* A private note attachment gets the same three actions as any
                other document; each link is just minted on click rather than
                stored — see openNoteAttachment. */}
            {url && privateNotePath && (
              <DropdownMenuItem
                onSelect={() =>
                  void openNoteAttachment(url, { fileName: name })
                }
              >
                {t("admin.tenantTabs.view", "View")}
              </DropdownMenuItem>
            )}
            {url && privateNotePath && (
              <DropdownMenuItem
                onSelect={() =>
                  void openNoteAttachment(url, {
                    download: true,
                    fileName: name,
                  })
                }
              >
                {t("admin.tenantTabs.download", "Download")}
              </DropdownMenuItem>
            )}
            {/* A tenant_documents row's file_url is a Storage path, not a
                link — same reasoning as the private note branch above, just
                signed against resolveTenantDocumentUrl's buckets instead of
                admin-notes. See openSignedTenantDocument. */}
            {url && signedDocPath && (
              <DropdownMenuItem
                onSelect={() =>
                  void openSignedTenantDocument(url, { fileName: name })
                }
              >
                {t("admin.tenantTabs.view", "View")}
              </DropdownMenuItem>
            )}
            {url && signedDocPath && (
              <DropdownMenuItem
                onSelect={() =>
                  void openSignedTenantDocument(url, {
                    download: true,
                    fileName: name,
                  })
                }
              >
                {t("admin.tenantTabs.download", "Download")}
              </DropdownMenuItem>
            )}
            {/* Everything else — the contract PDF, a chat attachment, a filed
                apartment document — is a reference into the contracts bucket,
                which is going private too. See openContractsFile. */}
            {url && !privateNotePath && !signedDocPath && (
              <DropdownMenuItem
                onSelect={() =>
                  void openContractsFile(url, openFailed, { fileName: name })
                }
              >
                {t("admin.tenantTabs.view", "View")}
              </DropdownMenuItem>
            )}
            {url && !privateNotePath && !signedDocPath && (
              <DropdownMenuItem
                onSelect={() =>
                  void openContractsFile(url, openFailed, {
                    download: true,
                    fileName: name,
                  })
                }
              >
                {t("admin.tenantTabs.download", "Download")}
              </DropdownMenuItem>
            )}
            {onRename && (
              <DropdownMenuItem onSelect={() => onRename()}>
                {t("admin.tenantTabs.rename", "Rename")}
              </DropdownMenuItem>
            )}
            {onEditLabel && (
              <DropdownMenuItem onSelect={() => onEditLabel()}>
                {t("admin.tenantTabs.editLabel", "Edit label")}
              </DropdownMenuItem>
            )}
            {onNote && (
              <DropdownMenuItem onSelect={() => onNote()}>
                {hasNote
                  ? t("admin.tenantTabs.editNote", "Edit note")
                  : t("admin.tenantTabs.addNote", "Add note")}
              </DropdownMenuItem>
            )}
            {onDelete && (
              <DropdownMenuItem
                onSelect={() => onDelete()}
                className="text-red-600 focus:text-red-600"
              >
                {t("admin.tenantTabs.delete", "Delete")}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

/* ================================================================== */
/* OVERVIEW                                                            */
/* ================================================================== */

const tlDensityBtn: React.CSSProperties = {
  background: "none",
  border: "none",
  padding: 0,
  cursor: "pointer",
  fontSize: 12,
  fontWeight: 600,
  color: "#64748B",
};

export function OverviewTab({
  name,
  email,
  phone,
  dob,
  gender = null,
  nationality = null,
  propertyTitle,
  roomTitle,
  propertyId,
  roomId,
  address,
  booking,
  depositPaid,
  journey,
  payments,
  proofByPayment,
  documents,
  documentsFailed = false,
  room,
  moveInProtocol,
  moveOutProtocol,
  issues,
  issueComments,
  isSuperAdmin,
  archived,
  busy,
  onArchiveToggle,
  paymentsActivity,
  depositPaidAt,
  depositStatementSentAt = null,
  adminNotes,
  auditLogs,
  paymentNotes,
  loading,
  onGoTab,
  canGenerateContract,
  canSendContract,
  onGenerateContract,
  onSendContract,
  onSaveContractInternalNote,
  contractInternalNote,
  onVerifyContractDraft,
  onMarkContractSigned,
  onUploadSignedContract,
  propertyDefaultTemplateId,
}: {
  name: string;
  email: string;
  phone: string;
  dob: string | null;
  /** COL-530: 'female' | 'male' | null (unknown). */
  gender?: string | null;
  /** COL-618: ISO 3166-1 alpha-2 code | null (unknown). Admin-only. */
  nationality?: string | null;
  propertyTitle: string;
  roomTitle: string;
  /** ids for deep-linking the property/room names (null when unknown). */
  propertyId: number | null;
  roomId: number | null;
  address: string;
  booking: Record<string, any>;
  depositPaid: boolean;
  journey: TenantJourney;
  payments: any[];
  /** Latest proof per payment id — needed to tell a corroborated "paid" row
   *  from an unattributed one when counting overdue payments. */
  proofByPayment: Record<string, any>;
  documents: any[];
  /** The documents read failed: an empty list is then an error, not "none". */
  documentsFailed?: boolean;
  room: Record<string, any> | null;
  moveInProtocol: any;
  moveOutProtocol: any;
  issues: any[];
  issueComments: any[];
  isSuperAdmin: boolean;
  archived: boolean;
  busy: boolean;
  onArchiveToggle: () => void;
  paymentsActivity: any[];
  depositPaidAt: string | null;
  /** deposit_settlements.statement_sent_at, for the email history entry. */
  depositStatementSentAt?: string | null;
  /** admin_notes rows (text notes + uploaded documents) for the timeline. */
  adminNotes: any[];
  /** audit_logs rows (manual edits) → "<topic> updated" timeline events. */
  auditLogs: any[];
  /** Payment-scoped admin notes — surfaced as their own timeline events. */
  paymentNotes: any[];
  loading: boolean;
  onGoTab: (
    t: "overview" | "payments" | "movein" | "moveout" | "documents"
  ) => void;
  /** false when contract generation isn't wired on this surface */
  canGenerateContract: boolean;
  /** false when RELEASING the draft isn't wired on this surface (separate callback) */
  canSendContract: boolean;
  /** builds the contract PDF; "draft" never notifies the tenant */
  onGenerateContract: (
    templateId: string | null,
    mode: "draft" | "send"
  ) => void;
  /** writes the admin-only internal contract note (super admins) */
  onSaveContractInternalNote?: (note: string) => Promise<void>;
  /** the admin-only contract note itself (admin_notes row, not on the booking) */
  contractInternalNote?: { body: string | null; created_at: string } | null;
  /** releases the verified draft to the tenant (email + status flip) */
  onSendContract: () => void;
  /** records the draft verification that unlocks sending */
  onVerifyContractDraft: (note: string) => Promise<void>;
  /** properties.default_contract_template_id, when known on this surface */
  propertyDefaultTemplateId?: string | null;
  onMarkContractSigned: () => void;
  /** opens the signed-contract PDF picker (manual upload) */
  onUploadSignedContract: () => void;
}) {
  const { t, language } = useLanguage();
  // Collapsed by default so the profile opens cleanly on every (re)open
  // (feedback #14). The Activity Timeline is one tap away.
  const [timelineOpen, setTimelineOpen] = useState(false);
  // Phone opens a copy/call modal rather than a raw tel: link (which triggers
  // FaceTime on desktop).
  const [phoneModalOpen, setPhoneModalOpen] = useState(false);
  // Expand/collapse-all signal for the per-event rows. The bumped `n` lets a
  // repeated click re-apply even after the user toggled individual rows. The
  // last choice is remembered for the session so power users don't re-click.
  const [timelineExpand, setTimelineExpand] = useState<{
    open: boolean;
    n: number;
  } | null>(() => {
    try {
      const v = sessionStorage.getItem("tp-timeline-expandAll");
      return v == null ? null : { open: v === "1", n: 1 };
    } catch {
      return null;
    }
  });
  const setTimelineExpandAll = (open: boolean) => {
    try {
      sessionStorage.setItem("tp-timeline-expandAll", open ? "1" : "0");
    } catch {
      /* storage unavailable — non-fatal */
    }
    setTimelineExpand((s) => ({ open, n: (s?.n ?? 0) + 1 }));
  };
  const rent = money(booking.monthly_rent);
  const deposit = money(booking.deposit_amount);
  // COL-618: a non-EU/EEA/CH applicant owes a residence permit. Not judged while
  // the documents are still loading or failed to load — an empty list would
  // read as "missing".
  const permitMissing =
    !loading &&
    !documentsFailed &&
    requiresResidencePermit(nationality) &&
    !documents.some((d: any) => d.document_type === "residence_permit");
  const genderLabel =
    gender === "female"
      ? t("admin.tenantEdit.genderFemale", "Woman")
      : gender === "male"
        ? t("admin.tenantEdit.genderMale", "Man")
        : "—";

  // Skip canceled duplicates — a deduped deposit row has paid_at null and
  // would wrongly report the deposit as unpaid.
  const depositRow = payments.find(
    (p: any) => p.type === "deposit" && p.status !== "canceled"
  );

  const timeline = useMemo(
    () =>
      buildTimeline(
        journey,
        booking,
        room,
        documents,
        email,
        t,
        {
          depositPaidAt: depositRow?.paid_at ?? null,
          moveInSubmittedAt: moveInProtocol?.submitted_at ?? null,
          moveOutSubmittedAt: moveOutProtocol?.submitted_at ?? null,
        },
        adminNotes,
        auditLogs,
        paymentNotes,
        payments,
        language
      ),
    [
      journey,
      booking,
      room,
      documents,
      email,
      t,
      depositRow,
      moveInProtocol,
      moveOutProtocol,
      adminNotes,
      auditLogs,
      paymentNotes,
      payments,
      language,
    ]
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {/* Attention strip — actionable gaps */}
      <AttentionStrip
        name={name}
        dob={dob}
        phone={phone}
        email={email}
        permitMissing={permitMissing}
        booking={booking}
        journey={journey}
        payments={payments}
        proofByPayment={proofByPayment}
        onGoTab={onGoTab}
      />

      {/* Referred application — renders only when a referral code was used.
          Above Personal Information on purpose: whether this applicant came in
          through a referral changes how the rest of the record reads. */}
      <ReferralSection bookingId={booking?.id ?? null} />

      {/* Personal Information */}
      <div style={cardWrap}>
        <SectionHead
          iconBg="#EAF1FE"
          iconColor="#3B82F6"
          icon={<UserIcon />}
          title={t(
            "admin.tenantTabs.personalInformation",
            "Personal Information"
          )}
        />
        {/* COL-618: the applicant said the Jobcenter pays their rent. Shown only
            for a "yes" — a flag for staff, not a field — and above the boxes so
            it reads before the record does. A "no", or a booking that predates
            the question, shows nothing; the edit panel is where the three states
            can be told apart. */}
        {booking?.jobcenter_pays_rent === true && (
          <div style={{ display: "flex", marginBottom: 14 }}>
            <span style={CHIP.cyan}>
              <LandmarkIcon size={13} />
              {t("admin.tenantTabs.jobcenter", "Jobcenter")}
            </span>
          </div>
        )}
        <div
          style={{
            ...whitePanel,
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
          }}
        >
          <InfoCell
            label={t("admin.tenantTabs.fullName", "Full name")}
            value={name}
            icon={<UserIcon size={14} />}
            borderRight
            borderBottom
          />
          <InfoCell
            label={t("admin.tenantTabs.dateOfBirth", "Date of birth")}
            value={dob ? (formatDate(dob) ?? "—") : "—"}
            icon={<CakeIcon size={14} />}
            borderBottom
          />
          <InfoCell
            label={t("admin.tenantTabs.email", "Email")}
            value={email}
            icon={<MailIcon size={14} />}
            href={email && email !== "—" ? `mailto:${email}` : undefined}
            borderRight
            borderBottom
          />
          <InfoCell
            label={t("admin.tenantTabs.phone", "Phone")}
            value={phone}
            icon={<PhoneIcon size={14} />}
            onClick={
              phone && phone !== "—" ? () => setPhoneModalOpen(true) : undefined
            }
            borderBottom
          />
          {/* Existing tenants have neither: neither was collected before the
              application form asked, so an empty box here is "not asked yet",
              not a gap to chase — hence optional, not the amber Missing chip. */}
          <InfoCell
            label={t("admin.tenantTabs.gender", "Gender")}
            value={genderLabel}
            icon={<GenderIcon size={14} />}
            optional
            borderRight
          />
          <InfoCell
            label={t("admin.tenantTabs.nationality", "Nationality")}
            value={nationality ? countryName(nationality, language) : "—"}
            icon={<FlagIcon size={14} />}
            optional
          />
        </div>
      </div>

      {phoneModalOpen && phone && phone !== "—" && (
        <ContactModal
          label={t("admin.tenantTabs.phoneNumber", "Phone number")}
          value={phone}
          onClose={() => setPhoneModalOpen(false)}
        />
      )}

      {/* Property & Lease */}
      <div style={cardWrap}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 18,
          }}
        >
          <SectionIcon bg="#E7F7EF" color="#10B981">
            <BuildingIcon />
          </SectionIcon>
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 17,
                fontWeight: 700,
                letterSpacing: "-.01em",
              }}
            >
              {t("admin.tenantTabs.propertyAndLease", "Property & Lease")}
            </h3>
            {address && (
              <p
                style={{
                  margin: "3px 0 0",
                  fontSize: 12.5,
                  color: "#94A3B8",
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                }}
              >
                <PinIcon />
                {address}
              </p>
            )}
          </div>
        </div>
        <div
          style={{
            ...whitePanel,
            display: "grid",
            gridTemplateColumns: "repeat(4,1fr)",
          }}
        >
          <LeaseCell
            label={t("admin.tenantTabs.property", "Property")}
            value={propertyTitle}
            href={propertyId ? `/admin/properties/${propertyId}` : undefined}
            borderRight
          />
          <LeaseCell
            label={t("admin.tenantTabs.room", "Room")}
            value={roomTitle}
            href={
              propertyId && roomId
                ? `/admin/properties/${propertyId}/rooms/${roomId}`
                : undefined
            }
            borderRight
          />
          <LeaseCell
            label={t("admin.tenantTabs.moveIn", "Move-in")}
            value={
              formatDate(
                booking.actual_move_in_date ||
                  booking.move_in_date ||
                  booking.start_date
              ) ?? "—"
            }
            borderRight
          />
          <LeaseCell
            // Without a recorded/confirmed move-out, end_date is only the
            // contract's earliest possible move-out (contracts auto-prolong)
            // — say so instead of presenting it as a real move-out (COL-399).
            label={
              booking.actual_move_out_date || booking.move_out_date
                ? t("admin.tenantTabs.moveOut", "Move-out")
                : t("admin.tenantTabs.earliestMoveOut", "Earliest move-out")
            }
            value={
              formatDate(
                booking.actual_move_out_date ||
                  booking.move_out_date ||
                  booking.end_date
              ) ?? "—"
            }
          />
        </div>
      </div>

      {/* Financial Summary */}
      <div style={cardWrap}>
        <SectionHead
          iconBg="#F1ECFE"
          iconColor="#8B5CF6"
          icon={<CardIcon />}
          title={t("admin.tenantTabs.financialSummary", "Financial Summary")}
        />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2,1fr)",
            gap: 14,
          }}
        >
          <div
            style={{
              background: "linear-gradient(135deg,#E4F7F4,#D3F1EC)",
              border: "1px solid #C4EDE6",
              borderRadius: 18,
              padding: 20,
            }}
          >
            <div style={{ ...uppLabel, color: "#0F766E" }}>
              {t("admin.tenantTabs.monthlyRent", "Monthly rent")}
            </div>
            <div
              style={{
                fontSize: 30,
                fontWeight: 800,
                color: "#0B7268",
                marginTop: 6,
                letterSpacing: "-.02em",
              }}
            >
              {rent}
            </div>
            {/* The headline is the base rent; the note says what this tenant
                actually pays and why (COL-360). */}
            <TenantDiscountNote booking={booking} className="mt-2" />
            {/* The room is the source of truth for that headline. If the
                booking's copy has drifted from the room it points at, flag it
                here and offer the repair (COL-360). */}
            {booking?.id != null && (
              <RoomBaseDriftNotice
                bookingId={Number(booking.id)}
                roomId={roomId}
                bookingRent={booking.monthly_rent}
                bookingDeposit={booking.deposit_amount}
                depositLocked={isDepositEditLocked(journey.stageId)}
                canRepair={!!isSuperAdmin}
                roomTitle={roomTitle}
                className="mt-2"
              />
            )}
          </div>
          <div style={{ ...whitePanel, padding: 20 }}>
            <div style={uppLabel}>
              {t("admin.tenantTabs.securityDeposit", "Security deposit")}
            </div>
            <div
              style={{
                fontSize: 30,
                fontWeight: 800,
                marginTop: 6,
                letterSpacing: "-.02em",
              }}
            >
              {deposit}
            </div>
            <div style={{ marginTop: 8 }}>
              <span style={depositPaid ? CHIP.green : CHIP.amber}>
                {depositPaid
                  ? t("admin.tenantTabs.paid", "Paid")
                  : t("admin.tenantTabs.pending", "Pending")}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Contract — always present; actions adapt to the contract state */}
      <ContractSection
        booking={booking}
        busy={busy}
        canGenerate={canGenerateContract}
        canSend={canSendContract}
        onGenerate={onGenerateContract}
        onSend={onSendContract}
        onSaveInternalNote={onSaveContractInternalNote}
        internalNoteBody={contractInternalNote?.body ?? null}
        internalNoteAt={contractInternalNote?.created_at ?? null}
        isSuperAdmin={isSuperAdmin}
        onVerifyDraft={onVerifyContractDraft}
        onMarkSigned={onMarkContractSigned}
        onUploadSigned={onUploadSignedContract}
        tenantName={name}
        tenantEmail={email}
        room={room}
        propertyTitle={propertyTitle}
        propertyDefaultTemplateId={propertyDefaultTemplateId}
      />

      {/* Email history — every triggered email, captured chronologically */}
      <EmailHistorySection
        booking={booking}
        journey={journey}
        email={email}
        depositPaidAt={depositPaidAt}
        paymentsActivity={paymentsActivity}
        depositStatementSentAt={depositStatementSentAt}
      />

      {/* Activity Timeline (collapsible, grouped by stage) */}
      <div
        style={{
          background: "#fff",
          border: "1px solid #ECEEF1",
          borderRadius: 24,
          overflow: "hidden",
        }}
      >
        <button
          onClick={() => setTimelineOpen((v) => !v)}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "18px 24px",
            background: "none",
            border: "none",
            cursor: "pointer",
            textAlign: "left",
          }}
        >
          <SectionIcon bg="#FFF2E7" color="#EA580C">
            <ClockIcon />
          </SectionIcon>
          <div style={{ flex: 1 }}>
            <h3
              style={{
                margin: 0,
                fontSize: 17,
                fontWeight: 700,
                letterSpacing: "-.01em",
              }}
            >
              {t("admin.tenantTabs.activityTimeline", "Activity Timeline")}
            </h3>
            <p
              style={{
                margin: "3px 0 0",
                fontSize: 13,
                color: "#94A3B8",
                fontWeight: 500,
              }}
            >
              {timeline.length}{" "}
              {timeline.length === 1
                ? t("admin.tenantTabs.event", "event")
                : t("admin.tenantTabs.events", "events")}{" "}
              · {t("admin.tenantTabs.newestFirst", "newest first")}
            </p>
          </div>
          <Chevron open={timelineOpen} />
        </button>
        {timelineOpen && (
          <div style={{ padding: "4px 24px 24px" }}>
            {timeline.length > 1 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 14,
                  borderTop: "1px solid #F0F1F3",
                  paddingTop: 14,
                }}
              >
                <button
                  onClick={() => setTimelineExpandAll(true)}
                  style={tlDensityBtn}
                >
                  {t("admin.tenantTabs.expandAll", "Expand all")}
                </button>
                <button
                  onClick={() => setTimelineExpandAll(false)}
                  style={tlDensityBtn}
                >
                  {t("admin.tenantTabs.collapseAll", "Collapse all")}
                </button>
              </div>
            )}
            <div
              style={{
                borderTop: timeline.length > 1 ? "none" : "1px solid #F0F1F3",
                paddingTop: timeline.length > 1 ? 14 : 18,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              {timeline.map((ev) => (
                <TimelineRow
                  key={ev.id}
                  ev={ev}
                  expandSignal={timelineExpand}
                />
              ))}
            </div>
            {timeline.length <= 1 && (
              <div
                style={{
                  fontSize: 12.5,
                  color: "#94A3B8",
                  fontWeight: 500,
                  marginTop: 4,
                  paddingLeft: 25,
                }}
              >
                {t(
                  "admin.tenantTabs.journeyJustStarted",
                  "Journey just started — more milestones appear here as the tenant progresses."
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Support & messages */}
      <SupportSection issues={issues} issueComments={issueComments} />

      {/* Archive booking */}
      {isSuperAdmin && (
        <div
          style={{
            background: "#fff",
            border: "1px solid #ECEEF1",
            borderRadius: 20,
            padding: "20px 24px",
            display: "flex",
            alignItems: "center",
            gap: 14,
          }}
        >
          <SectionIcon bg="#F1F3F6" color="#64748B" size={40}>
            <svg
              width="19"
              height="19"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="2" y="4" width="20" height="5" rx="1" />
              <path d="M4 9v10a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9M10 13h4" />
            </svg>
          </SectionIcon>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700 }}>
              {archived
                ? t("admin.tenantTabs.archivedBooking", "Archived booking")
                : t("admin.tenantTabs.archiveBooking", "Archive booking")}
            </div>
            <div
              style={{
                fontSize: 12.5,
                color: "#94A3B8",
                fontWeight: 500,
                marginTop: 2,
              }}
            >
              {archived
                ? t(
                    "admin.tenantTabs.archivedHint",
                    "Hidden from All / Active / Pending views. Restore to bring it back."
                  )
                : t(
                    "admin.tenantTabs.archiveHint",
                    "Hidden from All / Active / Pending views until restored."
                  )}
            </div>
          </div>
          <button
            onClick={onArchiveToggle}
            disabled={busy}
            style={{
              border: "1px solid #E7E9EC",
              background: "#fff",
              color: archived ? "#0B7268" : "#64748B",
              borderRadius: 11,
              padding: "10px 16px",
              fontSize: 13.5,
              fontWeight: 700,
              cursor: busy ? "default" : "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="2" y="4" width="20" height="5" rx="1" />
              <path d="M4 9v10a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9" />
            </svg>
            {archived
              ? t("admin.tenantTabs.restore", "Restore")
              : t("admin.tenantTabs.archive", "Archive")}
          </button>
        </div>
      )}
    </div>
  );
}

/** Tenant support tickets / messages, with raised & closed times. */
function SupportSection({
  issues,
  issueComments,
}: {
  issues: any[];
  issueComments: any[];
}) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const commentsByIssue = useMemo(() => {
    const m: Record<string, any[]> = {};
    for (const c of issueComments || []) (m[String(c.issue_id)] ??= []).push(c);
    return m;
  }, [issueComments]);
  return (
    <CollapsibleCard
      iconBg="#EAF1FE"
      iconColor="#3B82F6"
      icon={
        <svg
          width="19"
          height="19"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      }
      title={t("admin.tenantTabs.supportAndMessages", "Support & messages")}
      subtitle={`${issues.length} ${issues.length === 1 ? t("admin.tenantTabs.ticket", "ticket") : t("admin.tenantTabs.tickets", "tickets")}`}
      open={open}
      onToggle={() => setOpen((v) => !v)}
    >
      {issues.length === 0 ? (
        <div
          style={{
            padding: "16px 24px",
            fontSize: 13.5,
            color: "#94A3B8",
            fontWeight: 500,
          }}
        >
          {t(
            "admin.tenantTabs.noSupportMessages",
            "No support messages from this tenant."
          )}
        </div>
      ) : (
        issues.map((iss: any, i: number) => (
          <TicketRow
            key={iss.id}
            number={issues.length - i}
            issue={iss}
            comments={commentsByIssue[String(iss.id)] || []}
          />
        ))
      )}
    </CollapsibleCard>
  );
}

/** One support ticket — collapsible, numbered, with its message thread. */
function TicketRow({
  number,
  issue: iss,
  comments,
}: {
  number: number;
  issue: any;
  comments: any[];
}) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const closed = /closed|resolved|done/.test(String(iss.status || ""));
  const msgCount = comments.length + (iss.description ? 1 : 0);
  return (
    <div style={{ borderBottom: "1px solid #F5F6F7" }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "14px 24px",
          background: "none",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 26,
            height: 22,
            borderRadius: 7,
            background: "#F1F3F6",
            color: "#64748B",
            fontSize: 11.5,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          #{number}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>
            {iss.title ||
              iss.issue_type ||
              t("admin.tenantTabs.supportTicket", "Support ticket")}
          </div>
          <div
            style={{
              fontSize: 12,
              color: "#94A3B8",
              fontWeight: 500,
              marginTop: 2,
            }}
          >
            {t("admin.tenantTabs.raised", "Raised")}{" "}
            {formatDateTime(iss.created_at) ?? "—"}
            {closed && iss.updated_at
              ? ` · ${t("admin.tenantTabs.closedAt", "closed")} ${formatDateTime(iss.updated_at)}`
              : ""}
            {` · ${msgCount} ${msgCount === 1 ? t("admin.tenantTabs.message", "message") : t("admin.tenantTabs.messages", "messages")}`}
          </div>
        </div>
        <span style={closed ? CHIP.green : CHIP.amber}>
          {closed
            ? t("admin.tenantTabs.closed", "Closed")
            : String(iss.status || "open")}
        </span>
        <Chevron open={open} size={18} />
      </button>
      {open && (
        <div
          style={{
            padding: "0 24px 16px",
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          {iss.description && (
            <div
              style={{
                ...innerBox,
                padding: "11px 13px",
                fontSize: 13,
                color: "#334155",
                fontWeight: 500,
              }}
            >
              {iss.description}
            </div>
          )}
          {comments.map((c: any) => {
            const { text, atts } = decodeChat(c.comment);
            const isSystem =
              /Status updated|RESOLVED|CLOSED/i.test(text) && atts.length === 0;
            return (
              <div
                key={c.id}
                style={{
                  background: isSystem ? "#F0FAF8" : "#fff",
                  border: `1px solid ${isSystem ? "#CDECE7" : "#EDEFF2"}`,
                  borderRadius: 12,
                  padding: "11px 14px",
                }}
              >
                <div
                  style={{ fontSize: 11.5, color: "#94A3B8", fontWeight: 600 }}
                >
                  {formatDateTime(c.created_at) ?? ""}
                  {c.is_internal
                    ? ` · ${t("admin.tenantTabs.internalNote", "internal note")}`
                    : ""}
                </div>
                {text.trim() && (
                  <div
                    style={{
                      fontSize: 13,
                      color: isSystem ? "#0B5A52" : "#334155",
                      fontWeight: 500,
                      marginTop: 3,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {text}
                  </div>
                )}
                {atts.length > 0 && (
                  <div
                    style={{
                      marginTop: 8,
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 8,
                    }}
                  >
                    {atts.map((a, i) => (
                      <FileCard
                        key={i}
                        name={a.name}
                        sub={fmtSize(a.size)}
                        url={a.url}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Outline link button used for contract preview / download. */
function DocLink({
  url,
  label,
  download,
}: {
  url: string;
  label: string;
  download?: boolean;
}) {
  return (
    <a
      href={url}
      {...(download ? { download: true } : {})}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        border: "1px solid #E7E9EC",
        background: "#fff",
        color: "#334155",
        borderRadius: 11,
        padding: "10px 16px",
        fontSize: 13.5,
        fontWeight: 700,
        textDecoration: "none",
      }}
    >
      {download ? (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
        </svg>
      ) : (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      )}
      {label}
    </a>
  );
}

const contractBtn = (
  kind: "primary" | "outline" | "ghost",
  disabled: boolean
): React.CSSProperties => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  borderRadius: 11,
  padding: "10px 16px",
  fontSize: 13.5,
  fontWeight: 700,
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.55 : 1,
  border:
    kind === "primary"
      ? "1px solid #0F766E"
      : kind === "outline"
        ? "1px solid #E7E9EC"
        : "1px dashed #C4D0DC",
  background: kind === "primary" ? "#0F766E" : "#fff",
  color: kind === "primary" ? "#fff" : kind === "ghost" ? "#0E7490" : "#334155",
});

/**
 * The old one-click "Generate & send contract" button — generate the PDF AND
 * email the tenant a signing link in a single press.
 *
 * Kept in the code but switched off: it is exactly the accident the draft flow
 * exists to prevent (a wrong template or a wrong rent reached the tenant's
 * inbox before anyone could read the document). Flip to `true` only if you
 * deliberately want the unreviewed one-step path back.
 */
const SHOW_LEGACY_ONE_CLICK_SEND = false;

/**
 * Contract area — always present, state-driven.
 *
 * Four states, each with the actions that make sense there:
 *  - nothing on file → pick a template, preview it, generate a DRAFT (or upload
 *    a signed PDF you already have)
 *  - draft → the filled PDF opens inline next to the details it was built from;
 *    the admin verifies it with a note, which is what unlocks "Send to tenant"
 *  - sent / awaiting signature → mark signed, upload the signed PDF, resend
 *  - signed (electronically or manually) → signing details + replace the PDF
 *
 * Nothing here reaches the tenant except the explicit send, which goes through
 * a confirmation screen naming the recipient. Generating a draft is safe.
 */
export function ContractSection({
  booking,
  busy,
  canGenerate,
  // A surface that says nothing about sending keeps the historical behaviour
  // (both actions come from the same wiring); TenantProfileDialog passes the
  // real, separately-derived value.
  canSend = canGenerate,
  onGenerate,
  onSend,
  onVerifyDraft,
  internalNoteBody = null,
  internalNoteAt = null,
  onMarkSigned,
  onUploadSigned,
  onSaveInternalNote,
  isSuperAdmin = false,
  tenantName,
  tenantEmail,
  room,
  propertyTitle,
  propertyDefaultTemplateId,
}: {
  booking: Record<string, any>;
  busy: boolean;
  /** false when contract generation isn't wired on this surface */
  canGenerate: boolean;
  /**
   * false when releasing the draft isn't wired on this surface. Separate from
   * `canGenerate` because they are separate callbacks and separate actions —
   * sending rebuilds nothing, and the reason Send is unavailable is not the
   * reason Generate is.
   */
  canSend?: boolean;
  /**
   * Builds the PDF from the chosen template. `"draft"` (the only mode the UI
   * uses) emails nobody; `"send"` is the legacy one-step path kept behind
   * SHOW_LEGACY_ONE_CLICK_SEND.
   */
  onGenerate: (templateId: string | null, mode: "draft" | "send") => void;
  /**
   * Builds the merged document for this tenant from the given template and
   * resolves to an object URL. Saves nothing, sends nothing — the "see it
   * first" path. Undefined when the surface can't reach the edge function.
   */
  /** releases the verified draft to the tenant (status flip + email) */
  onSend: () => void;
  /** stamps the verification (note + who + when) that unlocks sending */
  onVerifyDraft: (note: string) => Promise<void>;
  /** body of the admin-only contract note (admin_notes, kind=contract_internal) */
  internalNoteBody?: string | null;
  /** when that note was last written */
  internalNoteAt?: string | null;
  onMarkSigned: () => void;
  /** opens the signed-PDF file picker (manual upload) */
  onUploadSigned: () => void;
  /** stores the admin-only internal note (super admins only) */
  onSaveInternalNote?: (note: string) => Promise<void>;
  /** gates writing the internal note */
  isSuperAdmin?: boolean;
  /**
   * Display name of the tenant. Comes from the profile — `bookings.name` is
   * often empty on bookings created through the tenant app, which is why the
   * confirmation screen used to say "the tenant".
   */
  tenantName?: string;
  /** address the send would go to — shown on the confirmation screen */
  tenantEmail?: string;
  room?: Record<string, any> | null;
  propertyTitle?: string;
  /** properties.default_contract_template_id, when the surface knows it */
  propertyDefaultTemplateId?: string | null;
}) {
  const { t } = useLanguage();
  const sentUrl = booking.contract_pdf_url || booking.contract_url || null;
  // Tenant-writable (signing upload): only ever an http(s) link here.
  const signedUrl =
    booking.signed_contract_url && isOpenableUrl(booking.signed_contract_url)
      ? booking.signed_contract_url
      : null;
  // Journey-strict: a status claiming "signed" with nothing on file is not a
  // signed contract, and the card must say so rather than contradict the stage
  // badge in the header.
  const signed = isContractSignedForJourney(booking);
  const contractStatusRaw = String(booking.contract_status || "")
    .trim()
    .toLowerCase();
  /** A generated PDF that has NOT been released to the tenant. */
  const isDraft = !signed && contractStatusRaw === "draft" && !!sentUrl;
  const sent = !isDraft && isContractSentForSignature(booking);
  const manual = !!booking.manual_contract_upload;
  /** Flag ticked, signed copy missing — the one case the strict signed
   *  predicate rejects that an admin is likely to find surprising. */
  const manualWithoutPdf = manual && !signedUrl;
  const contractStatus = String(booking.contract_status || "")
    .replace(/_/g, " ")
    .trim();

  const state: "none" | "draft" | "awaiting" | "signed" = signed
    ? "signed"
    : isDraft
      ? "draft"
      : sent
        ? "awaiting"
        : "none";

  const verifiedAt = booking.contract_draft_verified_at ?? null;
  const verificationNote = booking.contract_draft_verification_note ?? null;
  /** The send gate: a draft may only leave the building once verified. */
  const canSendDraft = state === "draft" && !!verifiedAt;

  const method = manual
    ? t("admin.tenantTabs.contractManuallyUploaded", "Manually uploaded")
    : booking.docusign_envelope_id || booking.docusign_status
      ? "DocuSign"
      : signed
        ? t(
            "admin.tenantTabs.contractElectronicSignature",
            "Electronic signature"
          )
        : isDraft
          ? t(
              "admin.tenantTabs.contractDraftNotSent",
              "Draft — not sent to the tenant"
            )
          : sent
            ? t(
                "admin.tenantTabs.contractAwaitingSignature",
                "Awaiting tenant signature"
              )
            : t("admin.tenantTabs.contractNotGenerated", "Not generated yet");
  const statusChip =
    state === "signed"
      ? CHIP.green
      : state === "awaiting"
        ? CHIP.amber
        : state === "draft"
          ? CHIP.blue
          : CHIP.slate2;
  const statusText =
    state === "signed"
      ? manual
        ? t("admin.tenantTabs.signedManualUpload", "Signed · manual upload")
        : t("admin.tenantTabs.signed", "Signed")
      : state === "awaiting"
        ? t("admin.tenantTabs.awaitingSignature", "Awaiting signature")
        : state === "draft"
          ? verifiedAt
            ? t(
                "admin.tenantTabs.draftVerifiedNotSent",
                "Draft verified · not sent"
              )
            : t("admin.tenantTabs.draftNotSent", "Draft · not sent")
          : t("admin.tenantTabs.noContractYet", "No contract yet");

  // Collapsed by default once the contract is signed — that's the settled
  // state and the header chip already says so. While something is still owed
  // (nothing on file, a draft to review, or awaiting a signature) the card
  // opens so the actions are in front of the admin.
  const [open, setOpen] = useState(state !== "signed");
  // Which copy the inline viewer is showing. Keyed by ROLE, not by URL — the
  // signed copy and the draft can point at the same file (a manual upload that
  // replaced the draft), and keying on the URL then toggled both rows at once.
  const [viewing, setViewing] = useState<"signed" | "draft" | null>(
    // A draft exists to be read, so open it without a click — the admin is
    // here to check the document, not to find the button that shows it.
    isDraft ? "draft" : null
  );
  const viewingUrl =
    viewing === "signed" ? signedUrl : viewing === "draft" ? sentUrl : null;

  // ── Can this environment run the flow at all? ─────────────────────────
  // Against a database that is a migration behind, "Generate draft" does not
  // fail safe: the older edge function ignores the draft flag, releases the
  // contract and emails the tenant. Probe first and refuse, rather than alarming
  // afterwards about an email that has already gone out.
  const { data: flowReady } = useContractFlowReady(state !== "signed");
  const environmentBehind = flowReady?.verification === false;
  const internalNoteUnavailable = flowReady?.internalNote === false;

  // ── Template picker ───────────────────────────────────────────────────
  // Only needed while a contract can still be (re)generated.
  const { data: templates = [], isLoading: templatesLoading } =
    useContractTemplates(state !== "signed");
  const effectiveTemplateId = resolveEffectiveTemplateId(
    templates,
    booking.contract_template_id ?? null,
    propertyDefaultTemplateId ?? null
  );
  // null = "follow the resolved default", so a newly-set property default is
  // picked up without the admin having to re-choose.
  const [templateOverride, setTemplateOverride] = useState<string | null>(null);
  const selectedTemplateId = templateOverride ?? effectiveTemplateId;
  const selectedTemplate =
    templates.find((tpl) => tpl.id === selectedTemplateId) ?? null;
  // The template the STORED PDF was actually built from — booking-first, and
  // never the dropdown. `selectedTemplate` is what the NEXT generate would use,
  // so naming it at verification/send time misdescribed the document in hand:
  // flip the dropdown after generating and the confirmation screen credited a
  // template the PDF had nothing to do with.
  const generatedTemplateId = effectiveTemplateId;
  const generatedTemplateName =
    templates.find((tpl) => tpl.id === generatedTemplateId)?.name ??
    t("admin.tenantTabs.systemDefault", "System default");
  // A stored PDF predating the picker (or one whose template was deleted) has no
  // honest name to show, so say that rather than guessing today's default.
  const generatedTemplateLabel = booking.contract_pdf_url
    ? booking.contract_template_id && !generatedTemplateId
      ? t(
          "admin.tenantTabs.templateNoLongerExists",
          "Unknown (template no longer exists)"
        )
      : generatedTemplateName
    : generatedTemplateName;

  // There is deliberately NO separate "preview" of an unsaved merge here.
  // "Generate draft" already produces the real document and opens it in the
  // viewer below without notifying the tenant, so a second way to render the
  // same merge added a mode that could return a full contract (name, DoB,
  // address, rent, deposit, lessor, rent-account IBAN) and nothing an admin
  // could not already see one click later.

  // ── Verification + send confirmation ──────────────────────────────────
  const [note, setNote] = useState("");
  const [checkedAgainstDetails, setCheckedAgainstDetails] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [confirmSendOpen, setConfirmSendOpen] = useState(false);

  // A regenerated draft clears the verification server-side; reset the local
  // form too so the admin starts from an empty note on the new document.
  useEffect(() => {
    if (!verifiedAt) {
      setNote("");
      setCheckedAgainstDetails(false);
    }
  }, [verifiedAt, booking.contract_pdf_updated_at]);

  // Follow the document: a fresh draft opens, and a draft that was just sent
  // stops hijacking the viewer.
  useEffect(() => {
    if (isDraft) setViewing("draft");
  }, [isDraft, booking.contract_pdf_updated_at]);

  const monthlyRent =
    booking.monthly_rent ?? (room as { price?: number } | null)?.price ?? null;
  // Same move_in/move_out fallbacks as create-contract's contract-dates
  // helpers — this checklist must show exactly what the PDF prints.
  const termStart = booking.move_in_date || booking.start_date;
  const termEnd = booking.move_out_date || booking.end_date;
  const termLine = [
    formatDate(termStart) ?? termStart ?? "—",
    termEnd
      ? (formatDate(termEnd) ?? termEnd)
      : t("admin.tenantTabs.openEnded", "open-ended"),
  ].join(" → ");

  /** What the draft PDF should be checked against, field by field. */
  const resolvedTenantName =
    (tenantName ?? "").trim() || String(booking.name ?? "").trim() || "—";

  const draftFacts: { label: string; value: string }[] = [
    { label: "Tenant", value: resolvedTenantName },
    { label: "Email", value: tenantEmail || String(booking.email ?? "—") },
    {
      label: "Room",
      value:
        [propertyTitle, (room as { title?: string } | null)?.title]
          .filter(Boolean)
          .join(" · ") || "—",
    },
    { label: "Term", value: termLine },
    {
      label: "Monthly rent",
      value: monthlyRent != null ? money(monthlyRent) : "—",
    },
    {
      label: "Deposit",
      value:
        booking.deposit_amount != null ? money(booking.deposit_amount) : "—",
    },
    {
      label: "Template",
      value: generatedTemplateLabel,
    },
    {
      label: "PDF version",
      value: `v${booking.contract_version ?? 1}${
        booking.contract_pdf_updated_at
          ? ` · ${formatDateTime(booking.contract_pdf_updated_at) ?? ""}`
          : ""
      }`,
    },
  ];

  // ── Internal note (admins only) ───────────────────────────────────────
  // Handed in, not read off the booking: it lives in admin_notes behind
  // admin-only RLS precisely so it is NOT on a row the tenant can select.
  const internalNote = internalNoteBody ?? null;
  const [noteDraftInternal, setNoteDraftInternal] = useState<string | null>(
    null
  );
  const [savingInternalNote, setSavingInternalNote] = useState(false);
  const [internalNoteError, setInternalNoteError] = useState<string | null>(
    null
  );
  const editingInternalNote = noteDraftInternal !== null;
  // No point offering the editor when the table cannot hold the note yet — the
  // save would fail after the admin had typed it out.
  const canWriteInternalNote =
    isSuperAdmin && !!onSaveInternalNote && !internalNoteUnavailable;

  async function saveInternalNote() {
    if (!onSaveInternalNote || noteDraftInternal === null || savingInternalNote)
      return;
    setSavingInternalNote(true);
    setInternalNoteError(null);
    try {
      await onSaveInternalNote(noteDraftInternal.trim());
      // Only clear the editor once the write actually landed — clearing on a
      // failed save destroyed what the admin typed and looked like a success.
      setNoteDraftInternal(null);
    } catch (e) {
      setInternalNoteError(
        e instanceof Error ? e.message : "The note was not saved."
      );
    } finally {
      setSavingInternalNote(false);
    }
  }

  async function submitVerification() {
    if (!note.trim() || !checkedAgainstDetails || verifying) return;
    setVerifying(true);
    setVerifyError(null);
    try {
      await onVerifyDraft(note.trim());
    } catch (e) {
      setVerifyError(
        e instanceof Error ? e.message : "The verification was not saved."
      );
    } finally {
      setVerifying(false);
    }
  }

  return (
    <CollapsibleCard
      iconBg="#EAF1FE"
      iconColor="#3B82F6"
      icon={<FileIcon />}
      title={t("admin.tenantTabs.contractCardTitle", "Contract")}
      subtitle={method}
      right={<span style={statusChip}>{statusText}</span>}
      open={open}
      onToggle={() => setOpen((v) => !v)}
    >
      <div style={{ padding: "18px 24px 20px" }}>
        {/* "Manual contract upload" was ticked but no signed PDF is stored.
            The flag alone is a claim, so the journey reads this tenant as
            unsigned — say exactly that, and exactly how to clear it, instead
            of leaving the admin to wonder why the stage won't advance. */}
        {manualWithoutPdf && (
          <div
            style={{
              display: "flex",
              gap: 10,
              alignItems: "flex-start",
              padding: "12px 14px",
              marginBottom: 14,
              background: "#FFFBEB",
              border: "1px solid #F7E4BD",
              borderRadius: 12,
            }}
          >
            <span style={{ color: "#B45309", flexShrink: 0, marginTop: 1 }}>
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
                <path d="M12 9v4M12 17h.01" />
              </svg>
            </span>
            <div>
              <div
                style={{ fontSize: 13.5, fontWeight: 700, color: "#78350F" }}
              >
                Marked as manually uploaded, but no signed PDF is stored
              </div>
              <div
                style={{
                  fontSize: 12.5,
                  color: "#92400E",
                  fontWeight: 500,
                  marginTop: 3,
                }}
              >
                The tenant counts as unsigned until the signed copy is on file.
                Upload it below to move the journey past Signature pending.
              </div>
            </div>
          </div>
        )}
        {/* This environment cannot run the review flow, so the actions that
            would misfire are switched off. Not a warning to read past: on a
            database this far behind, the deployed edge function ignores draft
            mode and a single click RELEASES the contract and emails the tenant a
            signing link. */}
        {environmentBehind && (
          <div
            role="alert"
            style={{
              padding: "13px 15px",
              marginBottom: 14,
              borderRadius: 12,
              background: "#FEF2F2",
              border: "1px solid #FBD5D5",
            }}
          >
            <div style={{ fontSize: 13.5, fontWeight: 700, color: "#B91C1C" }}>
              Contract actions are disabled — this environment is behind
            </div>
            <div
              style={{
                fontSize: 12.5,
                color: "#B91C1C",
                fontWeight: 500,
                marginTop: 4,
                lineHeight: 1.55,
              }}
            >
              This database has not run the contract review migration, so the
              review step has nowhere to be recorded — and a function old enough
              to match it ignores draft mode, releasing the contract and
              emailing the tenant a signing link on the first click. Nothing
              here can tell the two apart from the browser, so both are assumed.
              Run these, in this order, then reopen this tenant:
            </div>
            <div
              style={{
                marginTop: 8,
                display: "flex",
                flexDirection: "column",
                gap: 4,
              }}
            >
              {CONTRACT_DEPLOY_STEPS.map((cmd) => (
                <code
                  key={cmd}
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#7F1D1D",
                    background: "#fff",
                    border: "1px solid #FBD5D5",
                    borderRadius: 8,
                    padding: "5px 9px",
                    alignSelf: "flex-start",
                  }}
                >
                  {cmd}
                </code>
              ))}
            </div>
          </div>
        )}

        {/* How this works, in plain words. Three steps, with the one you are
            standing on highlighted — an admin should never have to guess
            whether pressing something here emails the tenant. Dropped once the
            contract is signed; there is nothing left to explain. */}
        {state !== "signed" && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(215px, 1fr))",
              gap: 10,
              marginBottom: 14,
            }}
          >
            {[
              {
                n: 1,
                title: "Generate the draft",
                body: "Makes the contract PDF. The tenant sees nothing.",
                active: state === "none",
              },
              {
                n: 2,
                title: "Read it and verify",
                body: "Open the PDF, check the details, write what you checked.",
                active: state === "draft" && !verifiedAt,
              },
              {
                n: 3,
                title: "Send it",
                body: "Only now does the tenant get an email to sign.",
                active: canSendDraft || state === "awaiting",
              },
            ].map((step) => (
              <div
                key={step.n}
                style={{
                  ...innerBox,
                  padding: "11px 13px",
                  display: "flex",
                  gap: 10,
                  alignItems: "flex-start",
                  background: step.active ? "#F0FAF7" : "#FAFBFC",
                  border: `1px solid ${step.active ? "#C6EEDB" : "#F0F1F3"}`,
                }}
              >
                <span
                  style={{
                    flexShrink: 0,
                    width: 20,
                    height: 20,
                    borderRadius: 999,
                    fontSize: 11.5,
                    fontWeight: 800,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: step.active ? "#0F766E" : "#E5E7EB",
                    color: step.active ? "#fff" : "#64748B",
                  }}
                >
                  {step.n}
                </span>
                <div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: step.active ? "#0B5F58" : "#334155",
                    }}
                  >
                    {step.title}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: step.active ? "#0F766E" : "#94A3B8",
                      marginTop: 2,
                    }}
                  >
                    {step.body}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Draft: the tenant knows nothing about this document yet. Say so
            plainly at the top of the card, because every action below reads
            differently once you know nothing has been sent. */}
        {state === "draft" && (
          <div
            style={{
              display: "flex",
              gap: 10,
              alignItems: "flex-start",
              padding: "12px 14px",
              marginBottom: 14,
              background: "#F5F8FF",
              border: "1px solid #D3E1FD",
              borderRadius: 12,
            }}
          >
            <span style={{ color: "#1D4ED8", flexShrink: 0, marginTop: 1 }}>
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 16v-4M12 8h.01" />
              </svg>
            </span>
            <div>
              <div
                style={{ fontSize: 13.5, fontWeight: 700, color: "#1E3A8A" }}
              >
                Draft only — nothing has been sent to the tenant
              </div>
              <div
                style={{
                  fontSize: 12.5,
                  color: "#1D4ED8",
                  fontWeight: 500,
                  marginTop: 3,
                }}
              >
                {verifiedAt
                  ? "Verified and ready. The tenant sees nothing until you press Send contract to tenant."
                  : "Read the document below, check it against the details, then submit the verification note. The send button unlocks after that."}
              </div>
            </div>
          </div>
        )}

        {/* Template picker + preview. "Preview contract" builds the MERGED
            document for this tenant server-side (saved nowhere) — the same
            bytes "Generate draft" would store, so what you read here is what
            gets verified and, eventually, sent. Preselected to whatever
            create-contract would have resolved on its own, so leaving the
            dropdown alone changes nothing. */}
        {state !== "signed" && (
          <div style={{ ...whitePanel, marginBottom: 14, overflow: "hidden" }}>
            <div style={{ padding: "14px 16px" }}>
              <div style={{ ...uppLabel, marginBottom: 7 }}>
                Contract template
              </div>
              {/* Kept as a row (not a bare field) so the description sits under
                  the whole thing rather than between control and hint. */}
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <div style={{ flex: 1, minWidth: 220 }}>
                  {/* Radix Select, not a native <select>: the native popup
                      renders as an OS menu that sits badly inside the profile
                      modal (and cannot show the "default" hint styling). */}
                  <Select
                    value={selectedTemplateId ?? ""}
                    onValueChange={(v) => setTemplateOverride(v || null)}
                    disabled={
                      busy || templatesLoading || templates.length === 0
                    }
                  >
                    <SelectTrigger
                      aria-label={t(
                        "admin.tenantTabs.contractTemplateAria",
                        "Contract template"
                      )}
                      className="h-11 rounded-xl border-[#E7E9EC] bg-white px-3.5 text-sm font-semibold text-[#0F172A] shadow-none focus:ring-2 focus:ring-[#0F766E]/25"
                    >
                      <SelectValue
                        placeholder={
                          templatesLoading
                            ? t(
                                "admin.tenantTabs.loadingTemplates",
                                "Loading templates…"
                              )
                            : templates.length === 0
                              ? t(
                                  "admin.tenantTabs.noTemplatesConfigured",
                                  "No templates configured"
                                )
                              : t(
                                  "admin.tenantTabs.systemDefault",
                                  "System default"
                                )
                        }
                      />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl">
                      {templates.map((tpl) => (
                        <SelectItem
                          key={tpl.id}
                          value={tpl.id}
                          className="text-sm"
                        >
                          <span style={{ fontWeight: 600 }}>{tpl.name}</span>
                          {tpl.is_default && (
                            <span
                              style={{
                                marginLeft: 8,
                                fontSize: 11,
                                fontWeight: 700,
                                color: "#0E7490",
                              }}
                            >
                              {t("admin.tenantTabs.defaultBadge", "default")}
                            </span>
                          )}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "#94A3B8",
                  fontWeight: 500,
                  marginTop: 7,
                }}
              >
                {selectedTemplate?.description
                  ? `${selectedTemplate.description} \u00b7 ${t(
                      "admin.tenantTabs.generateDraftHint",
                      '"Generate draft" builds the contract from this template and shows it below. Nothing is sent.'
                    )}`
                  : t(
                      "admin.tenantTabs.generateDraftHint",
                      '"Generate draft" builds the contract from this template and shows it below. Nothing is sent.'
                    )}
              </div>
            </div>
          </div>
        )}

        {state === "none" ? (
          <div
            style={{
              ...whitePanel,
              border: "1px dashed #D8DEE6",
              padding: "18px 20px",
              marginBottom: 14,
            }}
          >
            <div style={{ fontSize: 14.5, fontWeight: 700 }}>
              No contract on file
            </div>
            <div
              style={{
                fontSize: 13,
                color: "#64748B",
                fontWeight: 500,
                marginTop: 4,
              }}
            >
              {t(
                "admin.tenantProfile.contract.noContractOnFileHint",
                "Pick a template and generate the draft — it is built and shown here for you to read, and nothing reaches the tenant until you verify it and send. You can also upload a contract that was already signed off-platform. The tenant can't move past this step until a contract exists."
              )}
            </div>
          </div>
        ) : (
          <div
            style={{
              ...whitePanel,
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              marginBottom: 14,
            }}
          >
            <div
              style={{ padding: "16px 18px", borderRight: "1px solid #F0F1F3" }}
            >
              <div style={uppLabel}>
                {manual
                  ? t("admin.tenantProfile.uploaded", "Uploaded")
                  : t("admin.tenantProfile.signingDate", "Signing date")}
              </div>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  marginTop: 4,
                  color: booking.contract_signed_at ? "#0F172A" : "#CBD5E1",
                }}
              >
                {booking.contract_signed_at
                  ? (formatDateTime(booking.contract_signed_at) ?? "—")
                  : "Not signed yet"}
              </div>
            </div>
            <div
              style={{ padding: "16px 18px", borderRight: "1px solid #F0F1F3" }}
            >
              <div style={uppLabel}>
                {t("admin.tenantTabs.contractStatusLabel", "Contract status")}
              </div>
              <div style={{ fontSize: 15, fontWeight: 600, marginTop: 4 }}>
                {contractStatus || "—"}
              </div>
            </div>
            <div style={{ padding: "16px 18px" }}>
              <div style={uppLabel}>
                {t("admin.tenantTabs.storedPdfLabel", "Stored PDF")}
              </div>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  marginTop: 4,
                  color: signedUrl || sentUrl ? "#0F172A" : "#CBD5E1",
                }}
              >
                {signedUrl
                  ? t("admin.tenantProfile.signedCopy", "Signed copy")
                  : !sentUrl
                    ? t("admin.tenantProfile.notStored", "Not stored")
                    : state === "draft"
                      ? t(
                          "admin.tenantProfile.draftNotSent",
                          "Draft (not sent)"
                        )
                      : t("admin.tenantProfile.draftSent", "Draft sent")}
              </div>
            </div>
          </div>
        )}

        {/* The contract itself. Once it has been generated, signed or uploaded
            it surfaces right here — one row per stored copy, each with a View
            that opens the PDF inline in the card (no tab-hopping to check what
            was actually signed). */}
        {(signedUrl || sentUrl) && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
              marginBottom: 14,
            }}
          >
            {signedUrl && (
              <ContractFileRow
                url={signedUrl}
                label="Lease contract — signed copy"
                meta={
                  booking.contract_signed_at
                    ? `Signed ${formatDateTime(booking.contract_signed_at) ?? ""}`
                    : manual
                      ? "Uploaded by admin"
                      : "Signed"
                }
                accent
                viewing={viewing === "signed"}
                onToggleView={() =>
                  setViewing((v) => (v === "signed" ? null : "signed"))
                }
              />
            )}
            {sentUrl && (
              <ContractFileRow
                url={sentUrl}
                label={
                  state === "draft"
                    ? "Lease contract — draft (not sent)"
                    : signedUrl
                      ? "Lease contract — draft sent"
                      : "Lease contract"
                }
                meta={
                  booking.contract_pdf_updated_at
                    ? `Generated ${formatDateTime(booking.contract_pdf_updated_at) ?? ""}`
                    : state === "draft"
                      ? "Not sent to the tenant"
                      : "Sent for signature"
                }
                viewing={viewing === "draft"}
                onToggleView={() =>
                  setViewing((v) => (v === "draft" ? null : "draft"))
                }
              />
            )}
            {viewingUrl && <ContractViewer url={viewingUrl} />}
          </div>
        )}

        {/* Draft verification — the gate. The facts the PDF was built from sit
            next to the PDF itself so the admin can read one against the other,
            and the note records what was actually checked (it is shown back on
            the send confirmation and kept on the booking). */}
        {state === "draft" && (
          <div
            style={{
              ...whitePanel,
              border: verifiedAt ? "1px solid #C6EEDB" : "1px solid #EDEFF2",
              background: verifiedAt ? "#F4FCF8" : "#fff",
              padding: "16px 18px",
              marginBottom: 14,
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 700 }}>
              {verifiedAt
                ? "Draft verified"
                : "Check the draft against these details"}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
                gap: 12,
                marginTop: 12,
              }}
            >
              {draftFacts.map((f) => (
                <div
                  key={f.label}
                  style={{ ...innerBox, padding: "10px 12px" }}
                >
                  <div style={uppLabel}>{f.label}</div>
                  <div
                    style={{
                      fontSize: 13.5,
                      fontWeight: 600,
                      marginTop: 3,
                      wordBreak: "break-word",
                    }}
                  >
                    {f.value}
                  </div>
                </div>
              ))}
            </div>

            {verifiedAt ? (
              <div style={{ marginTop: 14 }}>
                <div
                  style={{ fontSize: 12.5, color: "#047857", fontWeight: 700 }}
                >
                  {t("admin.tenantTabs.verifiedPrefix", "Verified")}{" "}
                  {formatDateTime(verifiedAt) ?? ""}
                </div>
                {verificationNote && (
                  <div
                    style={{
                      fontSize: 13,
                      color: "#334155",
                      fontWeight: 500,
                      marginTop: 5,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    “{verificationNote}”
                  </div>
                )}
                <div
                  style={{
                    fontSize: 12,
                    color: "#64748B",
                    fontWeight: 500,
                    marginTop: 6,
                  }}
                >
                  Regenerating the draft clears this verification.
                </div>
              </div>
            ) : (
              <div style={{ marginTop: 14 }}>
                <label
                  htmlFor="contract-verify-note"
                  style={{ ...uppLabel, display: "block" }}
                >
                  Verification note
                </label>
                <textarea
                  id="contract-verify-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="What did you check? e.g. rent, deposit, dates, lessor and template all match the booking."
                  rows={3}
                  disabled={busy || verifying}
                  style={{
                    marginTop: 6,
                    width: "100%",
                    border: "1px solid #E7E9EC",
                    borderRadius: 12,
                    padding: "10px 12px",
                    fontSize: 13.5,
                    fontWeight: 500,
                    color: "#0F172A",
                    resize: "vertical",
                    fontFamily: "inherit",
                  }}
                />
                <label
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 9,
                    marginTop: 10,
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#334155",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checkedAgainstDetails}
                    onChange={(e) => setCheckedAgainstDetails(e.target.checked)}
                    disabled={busy || verifying}
                    style={{ marginTop: 2, width: 15, height: 15 }}
                  />
                  I opened the draft PDF above and it matches these details.
                </label>
                {verifyError && <WriteErrorNote message={verifyError} />}
                <button
                  onClick={() => void submitVerification()}
                  disabled={
                    busy ||
                    verifying ||
                    !note.trim() ||
                    !checkedAgainstDetails ||
                    environmentBehind
                  }
                  style={{
                    ...contractBtn(
                      "primary",
                      busy ||
                        verifying ||
                        !note.trim() ||
                        !checkedAgainstDetails ||
                        environmentBehind
                    ),
                    marginTop: 12,
                  }}
                >
                  {verifying ? "Submitting…" : "Submit verification"}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Internal note — admins only, and deliberately not part of the
            verification. Verification is about one PDF and dies with it; this
            is about the deal (why this template, what was negotiated, what to
            watch at renewal) and survives regeneration.
            Stored as an admin_notes row (kind = 'contract_internal'), NOT on the
            booking: the tenant dashboard reads its own booking with select("*"),
            so a column there reaches the tenant's browser no matter what this
            component renders. RLS is what enforces "super admins write, every
            admin reads" — the isSuperAdmin check below only hides the control. */}
        {(canWriteInternalNote ||
          internalNote ||
          (isSuperAdmin && internalNoteUnavailable)) && (
          <div
            style={{
              ...whitePanel,
              padding: "14px 16px",
              marginBottom: 14,
              background: "#FFFDF5",
              border: "1px solid #F1E7C6",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <span style={{ color: "#B45309", display: "flex" }}>
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M19 11H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2Z" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </span>
              <div
                style={{ fontSize: 13.5, fontWeight: 700, color: "#78350F" }}
              >
                {t("admin.tenantTabs.internalNoteLabel", "Internal note")}
              </div>
              <span style={CHIP.amber}>
                {t("admin.tenantTabs.adminsOnly", "Admins only")}
              </span>
              <div style={{ flex: 1 }} />
              {canWriteInternalNote && !editingInternalNote && (
                <button
                  onClick={() => setNoteDraftInternal(internalNote ?? "")}
                  disabled={busy}
                  style={{
                    ...actionBtn("#B45309"),
                    padding: "6px 12px",
                    fontSize: 12.5,
                    fontWeight: 700,
                    borderColor: "#F1E7C6",
                    background: "#fff",
                  }}
                >
                  {internalNote
                    ? t("admin.tenantTabs.editNote", "Edit note")
                    : t("admin.tenantTabs.addNote", "Add note")}
                </button>
              )}
            </div>

            {editingInternalNote ? (
              <div style={{ marginTop: 10 }}>
                <textarea
                  aria-label={t(
                    "admin.tenantTabs.internalNoteAria",
                    "Internal note"
                  )}
                  value={noteDraftInternal ?? ""}
                  onChange={(e) => setNoteDraftInternal(e.target.value)}
                  placeholder={t(
                    "admin.tenantTabs.internalNotePlaceholder",
                    "Context the tenant must never see — e.g. why this template, what was agreed on the phone, what to check at renewal."
                  )}
                  rows={3}
                  disabled={savingInternalNote}
                  style={{
                    width: "100%",
                    border: "1px solid #EADFBE",
                    borderRadius: 12,
                    padding: "10px 12px",
                    fontSize: 13.5,
                    fontWeight: 500,
                    color: "#0F172A",
                    resize: "vertical",
                    fontFamily: "inherit",
                    background: "#fff",
                  }}
                />
                {internalNoteError && (
                  <WriteErrorNote message={internalNoteError} />
                )}
                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  <button
                    onClick={() => void saveInternalNote()}
                    disabled={savingInternalNote}
                    style={contractBtn("primary", savingInternalNote)}
                  >
                    {savingInternalNote ? "Saving…" : "Save note"}
                  </button>
                  <button
                    onClick={() => setNoteDraftInternal(null)}
                    disabled={savingInternalNote}
                    style={contractBtn("outline", savingInternalNote)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : internalNote ? (
              <>
                <div
                  style={{
                    fontSize: 13.5,
                    color: "#3F2D13",
                    fontWeight: 500,
                    marginTop: 8,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {internalNote}
                </div>
                {internalNoteAt && (
                  <div
                    style={{
                      fontSize: 11.5,
                      color: "#A98A4B",
                      fontWeight: 600,
                      marginTop: 6,
                    }}
                  >
                    Last updated {formatDateTime(internalNoteAt) ?? ""}
                  </div>
                )}
              </>
            ) : internalNoteUnavailable ? (
              <div
                style={{
                  fontSize: 12.5,
                  color: "#92400E",
                  fontWeight: 500,
                  marginTop: 6,
                }}
              >
                Internal notes are unavailable on this environment until{" "}
                <code style={{ fontWeight: 700 }}>supabase db push</code> has
                run — they are stored in an admin-only table that does not exist
                here yet.
              </div>
            ) : (
              <div
                style={{
                  fontSize: 12.5,
                  color: "#92400E",
                  fontWeight: 500,
                  marginTop: 6,
                }}
              >
                Nothing recorded. Notes here stay inside the admin panel — they
                are never shown to the tenant or included in the contract.
              </div>
            )}
          </div>
        )}

        {/* Actions — dynamic per state. */}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {/* The send button. Hidden entirely until the draft is verified, so
              there is no one-click path from "generated" to "in the tenant's
              inbox" — and it never rebuilds the PDF, so what goes out is the
              document that was actually verified. */}
          {(canSendDraft || state === "awaiting") && (
            <button
              onClick={() => setConfirmSendOpen(true)}
              disabled={busy || !canSend || environmentBehind}
              title={
                environmentBehind
                  ? "This environment is missing the contract review migration — see the note above."
                  : canSend
                    ? undefined
                    : state === "awaiting"
                      ? "Resending isn't available on this screen."
                      : "Releasing the contract isn't available on this screen."
              }
              style={contractBtn(
                "primary",
                busy || !canSend || environmentBehind
              )}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m22 2-7 20-4-9-9-4Z" />
                <path d="M22 2 11 13" />
              </svg>
              {state === "awaiting"
                ? "Resend to tenant"
                : "Send contract to tenant"}
            </button>
          )}
          {state !== "signed" && (
            <button
              onClick={() => onGenerate(selectedTemplateId, "draft")}
              disabled={busy || !canGenerate || environmentBehind}
              title={
                environmentBehind
                  ? "This environment is missing the contract review migration — see the note above."
                  : canGenerate
                    ? undefined
                    : "Contract generation isn't available on this screen."
              }
              style={contractBtn(
                state === "none" ? "primary" : "outline",
                busy || !canGenerate || environmentBehind
              )}
            >
              {state === "none" ? "Generate draft" : "Regenerate draft"}
            </button>
          )}
          {/* Legacy one-step generate-and-send — off by default; see
              SHOW_LEGACY_ONE_CLICK_SEND. */}
          {SHOW_LEGACY_ONE_CLICK_SEND && state !== "signed" && (
            <button
              onClick={() => onGenerate(selectedTemplateId, "send")}
              disabled={busy || !canGenerate || environmentBehind}
              style={contractBtn(
                "outline",
                busy || !canGenerate || environmentBehind
              )}
            >
              {state === "awaiting"
                ? "Regenerate & resend contract"
                : "Generate & send contract"}
            </button>
          )}
          {state === "awaiting" && (
            <button
              onClick={onMarkSigned}
              disabled={busy}
              style={contractBtn("outline", busy)}
            >
              Mark contract signed
            </button>
          )}
          <button
            onClick={onUploadSigned}
            disabled={busy}
            style={contractBtn(state === "signed" ? "outline" : "ghost", busy)}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />
            </svg>
            {state === "signed" ? "Replace signed PDF" : "Upload signed PDF"}
          </button>
        </div>
      </div>

      <SendContractConfirmDialog
        open={confirmSendOpen}
        onOpenChange={setConfirmSendOpen}
        busy={busy}
        resend={state === "awaiting"}
        tenantName={
          resolvedTenantName === "—" ? "this tenant" : resolvedTenantName
        }
        tenantEmail={tenantEmail || String(booking.email ?? "")}
        facts={draftFacts}
        verificationNote={verificationNote}
        verifiedAt={verifiedAt}
        pdfUrl={sentUrl}
        onConfirm={() => {
          setConfirmSendOpen(false);
          onSend();
        }}
      />
    </CollapsibleCard>
  );
}

/**
 * A write that did not land, said in place.
 *
 * Toasts render BEHIND the tenant-profile overlay, so a failed save was
 * invisible: the form appeared to submit and nothing was stored. This keeps the
 * reason next to the input the admin is still holding.
 */
function WriteErrorNote({ message }: { message: string }) {
  return (
    <div
      role="alert"
      style={{
        marginTop: 10,
        padding: "10px 12px",
        borderRadius: 12,
        background: "#FEF2F2",
        border: "1px solid #FBD5D5",
        color: "#B91C1C",
        fontSize: 12.5,
        fontWeight: 600,
        lineHeight: 1.5,
      }}
    >
      Not saved: {message}
    </div>
  );
}

/**
 * The last stop before a contract reaches a tenant.
 *
 * Names the recipient's actual email address (not "the tenant"), repeats the
 * facts the document was built from, shows the verification note back, and
 * requires one more explicit acknowledgement. This exists because sending used
 * to be a single click on a button that also generated the PDF — there was no
 * moment where an admin could stop.
 */
function SendContractConfirmDialog({
  open,
  onOpenChange,
  busy,
  resend,
  tenantName,
  tenantEmail,
  facts,
  verificationNote,
  verifiedAt,
  pdfUrl,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  busy: boolean;
  /** true when the contract was already sent once (resend wording) */
  resend: boolean;
  tenantName: string;
  tenantEmail: string;
  facts: { label: string; value: string }[];
  verificationNote: string | null;
  verifiedAt: string | null;
  pdfUrl: string | null;
  onConfirm: () => void;
}) {
  const { t } = useLanguage();
  const [ack, setAck] = useState(false);
  // Every open starts from an unticked box — an acknowledgement carried over
  // from the last time would defeat the point.
  useEffect(() => {
    if (open) setAck(false);
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Capped height with the facts scrolling inside, so the acknowledgement
          and the Send button are always on screen — on a short viewport the
          footer used to sit below the fold. */}
      <DialogContent className="flex max-h-[85vh] max-w-lg flex-col gap-4 overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>
            {resend
              ? t(
                  "admin.tenantTabs.resendContractQuestion",
                  "Resend the contract?"
                )
              : t(
                  "admin.tenantTabs.sendContractQuestion",
                  "Send this contract to the tenant?"
                )}
          </DialogTitle>
          <DialogDescription>
            {t(
              "admin.tenantTabs.sendContractDescription",
              "This is the step that makes the contract visible to the tenant. Nothing has reached them yet."
            )}
          </DialogDescription>
        </DialogHeader>

        <div
          className="min-h-0 flex-1 overflow-y-auto"
          style={{ display: "flex", flexDirection: "column", gap: 14 }}
        >
          <div
            style={{
              ...whitePanel,
              padding: "14px 16px",
              background: "#F5F8FF",
              border: "1px solid #D3E1FD",
            }}
          >
            <div style={uppLabel}>
              {t("admin.tenantTabs.goesToLabel", "Goes to")}
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, marginTop: 4 }}>
              {tenantName}
            </div>
            <div
              style={{
                fontSize: 13.5,
                fontWeight: 600,
                color: "#1D4ED8",
                marginTop: 2,
                wordBreak: "break-all",
              }}
            >
              {tenantEmail ||
                t("admin.tenantTabs.noEmailOnFile", "no email on file")}
            </div>
            <div
              style={{
                fontSize: 12.5,
                color: "#475569",
                fontWeight: 500,
                marginTop: 8,
              }}
            >
              {t(
                "admin.tenantTabs.sendContractExplainer",
                "They get an email with a link to sign on their dashboard, and the contract becomes visible to them immediately. The PDF is sent exactly as verified — nothing is regenerated."
              )}
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 10,
            }}
          >
            {facts.map((f) => (
              <div key={f.label} style={{ ...innerBox, padding: "9px 11px" }}>
                <div style={uppLabel}>{f.label}</div>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    marginTop: 3,
                    wordBreak: "break-word",
                  }}
                >
                  {f.value}
                </div>
              </div>
            ))}
          </div>

          {verifiedAt && (
            <div
              style={{
                ...whitePanel,
                padding: "12px 14px",
                background: "#F4FCF8",
                border: "1px solid #C6EEDB",
              }}
            >
              <div
                style={{ fontSize: 12.5, fontWeight: 700, color: "#047857" }}
              >
                {t("admin.tenantTabs.verifiedPrefix", "Verified")}{" "}
                {formatDateTime(verifiedAt) ?? ""}
              </div>
              {verificationNote && (
                <div
                  style={{
                    fontSize: 13,
                    color: "#334155",
                    fontWeight: 500,
                    marginTop: 4,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  “{verificationNote}”
                </div>
              )}
            </div>
          )}

          {pdfUrl && (
            <StoredFileLink
              value={pdfUrl}
              bucket="contracts"
              errorText={t(
                "admin.docs.openFailed",
                "Could not open the document."
              )}
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: "#0E7490",
                textDecoration: "none",
              }}
            >
              {t(
                "admin.tenantTabs.openPdfOneMoreTime",
                "Open the PDF one more time ↗"
              )}
            </StoredFileLink>
          )}
        </div>

        <label
          className="shrink-0"
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 9,
            fontSize: 13,
            fontWeight: 600,
            color: "#334155",
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={ack}
            onChange={(e) => setAck(e.target.checked)}
            style={{ marginTop: 2, width: 15, height: 15 }}
          />
          {t("admin.tenantTabs.sendContractToPrefix", "Send this contract to")}{" "}
          {tenantEmail || tenantName}{" "}
          {t("admin.tenantTabs.sendContractToSuffix", "now.")}
        </label>

        <DialogFooter className="shrink-0 border-t border-[#F0F1F3] pt-3">
          <button
            onClick={() => onOpenChange(false)}
            disabled={busy}
            style={contractBtn("outline", busy)}
          >
            {t("admin.tenantTabs.cancelButton", "Cancel")}
          </button>
          <button
            onClick={onConfirm}
            disabled={busy || !ack || !tenantEmail}
            title={
              tenantEmail
                ? undefined
                : t(
                    "admin.tenantTabs.noTenantEmailAddress",
                    "No tenant email address on file."
                  )
            }
            style={contractBtn("primary", busy || !ack || !tenantEmail)}
          >
            {resend
              ? t("admin.tenantTabs.resendContractButton", "Resend contract")
              : t("admin.tenantTabs.sendContractButton", "Send contract")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * One stored contract copy: name + meta on the left, View / Download on the
 * right. "View" toggles the inline `ContractViewer` below the list rather than
 * opening a tab, so the admin can read the actual document without leaving the
 * profile.
 */
function ContractFileRow({
  url,
  label,
  meta,
  accent,
  viewing,
  onToggleView,
}: {
  url: string;
  label: string;
  meta: string;
  /** the signed copy gets the green treatment */
  accent?: boolean;
  viewing: boolean;
  onToggleView: () => void;
}) {
  const { t } = useLanguage();
  const openFailed = t("admin.docs.openFailed", "Could not open the document.");
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "12px 14px",
        border: `1px solid ${accent ? "#C6EEDB" : "#EDEFF2"}`,
        background: accent ? "#F4FCF8" : "#fff",
        borderRadius: 14,
        flexWrap: "wrap",
      }}
    >
      <SectionIcon
        bg={accent ? "#E7F7EF" : "#EAF1FE"}
        color={accent ? "#10B981" : "#3B82F6"}
        size={36}
        radius={10}
      >
        <FileIcon size={17} />
      </SectionIcon>
      <div style={{ flex: 1, minWidth: 140 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700 }}>{label}</div>
        <div style={{ fontSize: 12, color: "#94A3B8", fontWeight: 500 }}>
          {meta}
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button
          onClick={onToggleView}
          aria-expanded={viewing}
          style={{
            ...actionBtn(viewing ? "#0B7268" : "#0E7490"),
            padding: "8px 14px",
            fontSize: 12.5,
            fontWeight: 700,
            borderColor: viewing ? "#C6EEDB" : "#E7E9EC",
            background: viewing ? "#E4F7F4" : "#fff",
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
          {viewing ? "Hide" : "View"}
        </button>
        {/* Signed on click: the contracts bucket is going private (COL-436). */}
        <StoredFileLink
          value={url}
          bucket="contracts"
          errorText={openFailed}
          style={{
            ...actionBtn("#64748B"),
            padding: "8px 14px",
            fontSize: 12.5,
            fontWeight: 700,
            textDecoration: "none",
          }}
        >
          Open
        </StoredFileLink>
        <StoredFileLink
          value={url}
          bucket="contracts"
          download
          errorText={openFailed}
          style={{
            ...actionBtn("#64748B"),
            padding: "8px 14px",
            fontSize: 12.5,
            fontWeight: 700,
            textDecoration: "none",
          }}
        >
          Download
        </StoredFileLink>
      </div>
    </div>
  );
}

/**
 * Inline document preview. Contracts are PDFs in the `contracts` bucket,
 * which is going private, so the iframe gets a short-lived signed URL rather
 * than the stored public one (COL-436) and the browser's own viewer renders
 * it. Anything it refuses to render still has Open / Download next to the
 * View button, and the fallback link inside covers the sandboxed case.
 */
function ContractViewer({
  url,
  title,
  subtitle,
  onClose,
}: {
  url: string;
  /** header label; omit for the bare framed viewer used by the file rows */
  title?: string;
  subtitle?: string;
  /** shows a Close control in the header when provided */
  onClose?: () => void;
}) {
  const { t } = useLanguage();
  const signed = useSignedStoredUrl(url, "contracts");
  const openFailed = t("admin.docs.openFailed", "Could not open the document.");
  return (
    <div
      style={{
        border: "1px solid #E2E8F0",
        borderRadius: 16,
        overflow: "hidden",
        background: "#fff",
        boxShadow: "0 1px 2px rgba(15,23,42,.04)",
      }}
    >
      {(title || onClose) && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "11px 14px",
            borderBottom: "1px solid #EDEFF2",
            background: "#FAFBFC",
          }}
        >
          <SectionIcon bg="#EAF1FE" color="#3B82F6" size={30} radius={9}>
            <FileIcon size={15} />
          </SectionIcon>
          <div style={{ flex: 1, minWidth: 0 }}>
            {title && (
              <div style={{ fontSize: 13.5, fontWeight: 700 }}>{title}</div>
            )}
            {subtitle && (
              <div
                style={{
                  fontSize: 11.5,
                  color: "#94A3B8",
                  fontWeight: 500,
                  marginTop: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {subtitle}
              </div>
            )}
          </div>
          <StoredFileLink
            value={url}
            bucket="contracts"
            errorText={openFailed}
            style={{
              ...actionBtn("#64748B"),
              padding: "6px 11px",
              fontSize: 12,
              fontWeight: 700,
              textDecoration: "none",
            }}
          >
            {t("admin.tenantTabs.contractViewerOpen", "Open")}
          </StoredFileLink>
          <StoredFileLink
            value={url}
            bucket="contracts"
            download
            errorText={openFailed}
            style={{
              ...actionBtn("#64748B"),
              padding: "6px 11px",
              fontSize: 12,
              fontWeight: 700,
              textDecoration: "none",
            }}
          >
            {t("admin.tenantTabs.contractViewerDownload", "Download")}
          </StoredFileLink>
          {onClose && (
            <button
              onClick={onClose}
              aria-label={t(
                "admin.tenantTabs.closePreviewAria",
                "Close preview"
              )}
              style={{
                ...actionBtn("#64748B"),
                padding: "6px 9px",
                fontSize: 12,
                fontWeight: 700,
                lineHeight: 1,
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      )}
      {signed.failed ? (
        <div
          role="alert"
          style={{
            padding: "28px 16px",
            fontSize: 13,
            fontWeight: 600,
            color: "#B91C1C",
            background: "#F8FAFC",
          }}
        >
          {openFailed}
        </div>
      ) : (
        <iframe
          src={signed.url ?? undefined}
          title={t(
            "admin.tenantTabs.leaseContractIframeTitle",
            "Lease contract"
          )}
          style={{
            width: "100%",
            height: 620,
            border: "none",
            display: "block",
            background: "#F8FAFC",
          }}
        />
      )}
      <div
        style={{
          padding: "8px 12px",
          borderTop: "1px solid #EDEFF2",
          fontSize: 11.5,
          color: "#94A3B8",
          fontWeight: 500,
        }}
      >
        {t("admin.tenantTabs.cantSeeDocument", "Can\u2019t see the document?")}{" "}
        <StoredFileLink
          value={url}
          bucket="contracts"
          errorText={openFailed}
          style={{ color: "#0E7490", fontWeight: 700 }}
        >
          {t("admin.tenantTabs.openInNewTab", "Open it in a new tab")}
        </StoredFileLink>
        .
      </div>
    </div>
  );
}

/** Shimmer skeleton block. */
function Skeleton({
  h = 14,
  w = "100%",
  style,
}: {
  h?: number;
  w?: number | string;
  style?: React.CSSProperties;
}) {
  return <div className="tp-skel" style={{ height: h, width: w, ...style }} />;
}

/** A compact banner surfacing the tenant's actionable gaps. */
function AttentionStrip({
  name,
  dob,
  phone,
  email,
  permitMissing,
  booking,
  journey,
  payments,
  proofByPayment,
  onGoTab,
}: {
  name: string;
  dob: string | null;
  phone: string;
  email: string;
  /** COL-618: a non-EU/EEA/CH applicant with no residence permit on file. */
  permitMissing: boolean;
  booking: Record<string, any>;
  journey: TenantJourney;
  payments: any[];
  proofByPayment: Record<string, any>;
  onGoTab: (
    t: "overview" | "payments" | "movein" | "moveout" | "documents"
  ) => void;
}) {
  const { t } = useLanguage();
  const items: { label: string; tab?: "payments" | "movein" | "documents" }[] =
    [];
  // Shared with the tenant list and the room view's current-tenant card, so
  // the three surfaces name the same gaps in the same order.
  const gapLabel = tenantInfoGapLabel(
    {
      name,
      dateOfBirth: dob,
      email,
      phone,
    },
    Number.POSITIVE_INFINITY,
    t
  );
  if (gapLabel) items.push({ label: gapLabel });

  if (permitMissing)
    items.push({
      label: t(
        "admin.tenantTabs.attentionResidencePermit",
        "Residence permit missing"
      ),
      tab: "documents",
    });

  const today = businessToday();
  // Corroborated-paid only, matching the Payments tab's per-row overdue flag —
  // a row marked paid with no proof and no attestation is money we haven't
  // confirmed, so it belongs in this count rather than hiding behind `status`.
  // Canceled is the only voided state: a `failed` collection bounced and the
  // money is still owed, so it belongs in this count too — that row is the one
  // most in need of attention, not the one to leave out.
  const overdue = payments.filter(
    (p: any) =>
      !effectivelyPaid(p, proofByPayment) &&
      String(p.status ?? "").toLowerCase() !== "canceled" &&
      p.due_date &&
      p.due_date <= today
  );
  if (overdue.length)
    items.push({
      label: `${overdue.length} overdue payment${overdue.length === 1 ? "" : "s"}`,
      tab: "payments",
    });

  // Only nag about signing while it's still an open step — before the tenant
  // reaches Active. Once active/closed the signing phase is behind us.
  if (
    !isBookingContractSigned(booking) &&
    journey.stageId !== "applied" &&
    !journey.reached("active") &&
    !journey.isClosed
  )
    items.push({ label: "Contract not signed yet", tab: "movein" });

  if (items.length === 0) return null;

  return (
    <div
      style={{
        background: "#FFFBEB",
        border: "1px solid #F7E4BD",
        borderRadius: 18,
        padding: "14px 18px",
        display: "flex",
        alignItems: "flex-start",
        gap: 13,
      }}
    >
      <SectionIcon bg="#FEF3C7" color="#B45309" size={38} radius={11}>
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m21.7 18-8-14a2 2 0 0 0-3.5 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.7-3Z" />
          <path d="M12 9v4M12 17h.01" />
        </svg>
      </SectionIcon>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: "#8A5A0B" }}>
          Needs attention
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 5,
            marginTop: 6,
          }}
        >
          {items.map((it, i) => (
            <div
              key={i}
              style={{ display: "flex", alignItems: "center", gap: 10 }}
            >
              <span style={{ fontSize: 13, fontWeight: 500, color: "#92400E" }}>
                • {it.label}
              </span>
              {it.tab && (
                <button
                  onClick={() => onGoTab(it.tab!)}
                  style={{
                    border: "1px solid #E7D3A6",
                    background: "#fff",
                    color: "#B45309",
                    borderRadius: 8,
                    padding: "3px 10px",
                    fontSize: 11.5,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  {it.tab === "documents"
                    ? t("admin.tenantTabs.goToDocuments", "Go to Documents")
                    : `Go to ${it.tab === "payments" ? "Payments" : "Move-in"}`}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

interface EmailHistoryEntry {
  title: string;
  desc: string;
  recipient: string;
  sent: boolean;
  ts: string | null;
  kind: "lifecycle" | "dunning";
}

function buildEmailHistory(
  booking: Record<string, any>,
  journey: TenantJourney,
  email: string,
  depositPaidAt: string | null,
  paymentsActivity: any[],
  /** deposit_settlements.statement_sent_at — the only evidence the deposit
   *  statement actually went out. Absent on a database without the settlement
   *  migration, which reads as "not sent" rather than inventing a send. */
  depositStatementSentAt: string | null = null
): EmailHistoryEntry[] {
  const out: EmailHistoryEntry[] = [];
  const push = (
    title: string,
    desc: string,
    sent: boolean,
    ts: string | null,
    kind: EmailHistoryEntry["kind"] = "lifecycle",
    recipient = email
  ) => out.push({ title, desc, sent, ts, kind, recipient });

  push(
    "Application received",
    "Welcome & next steps",
    true,
    booking.created_at ?? null
  );
  push(
    "Contract ready to sign",
    "DocuSign link sent to tenant",
    isContractSentForSignature(booking),
    booking.contract_signed_at ?? null
  );
  push(
    "Deposit payment request",
    `${money(booking.deposit_amount)} · SEPA / bank transfer`,
    isBookingContractSigned(booking),
    booking.contract_signed_at ?? null
  );
  push(
    "Move-in & key pickup details",
    "KeyNest address, codes & directions",
    journey.reached("key_pickup_pending"),
    depositPaidAt
  );
  push(
    "Move-in protocol reminder",
    "Complete the handover walkthrough",
    !!booking.move_in_activation_email_sent_at ||
      journey.reached("handover_pending"),
    booking.move_in_activation_email_sent_at || booking.key_picked_up_at || null
  );

  if (journey.reached("moveout_scheduled")) {
    push("Move-out initiated", "Notice acknowledged to tenant", true, null);
    push(
      "Move-out date confirmed",
      "Confirmation with final date",
      journey.reached("moveout_protocol_pending"),
      booking.move_out_date ?? null
    );
    push(
      "Move-out protocol reminder",
      "Book the final walkthrough",
      !!booking.move_out_reminder_48h_sent_at,
      booking.move_out_reminder_48h_sent_at ?? null
    );
    push(
      "Key return instructions",
      "Where & how to return keys",
      journey.reached("key_return_pending"),
      null
    );
    // Reaching a stage was never evidence this went out, and the key-return
    // timestamp is not when it was sent. The settlement's own stamp is the only
    // thing that is — same correction as the move-out tab's email row.
    push(
      "Deposit return summary",
      "Deposit settlement statement",
      !!depositStatementSentAt,
      depositStatementSentAt
    );
  }

  for (const a of paymentsActivity || []) {
    if (
      /reminder_sent|admin_email_sent|inkasso_referred/.test(
        String(a.event_type || "")
      )
    ) {
      push(
        a.email_subject || "Payment reminder",
        a.stage != null ? `Dunning stage ${a.stage}` : "Sent to tenant",
        true,
        a.created_at ?? null,
        "dunning",
        a.email_to || email
      );
    }
  }

  const t = (v: string) => new Date(v).getTime();
  const withTs = out
    .filter((e) => e.sent && e.ts)
    .sort((a, b) => t(b.ts!) - t(a.ts!));
  const sentNoTs = out.filter((e) => e.sent && !e.ts);
  const pending = out.filter((e) => !e.sent);
  return [...withTs, ...sentNoTs, ...pending];
}

/** Complete chronological history of every email triggered for this tenant. */
function EmailHistorySection({
  booking,
  journey,
  email,
  depositPaidAt,
  paymentsActivity,
  depositStatementSentAt = null,
}: {
  booking: Record<string, any>;
  journey: TenantJourney;
  email: string;
  depositPaidAt: string | null;
  paymentsActivity: any[];
  depositStatementSentAt?: string | null;
}) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const entries = useMemo(
    () =>
      buildEmailHistory(
        booking,
        journey,
        email,
        depositPaidAt,
        paymentsActivity,
        depositStatementSentAt
      ),
    [
      booking,
      journey,
      email,
      depositPaidAt,
      paymentsActivity,
      depositStatementSentAt,
    ]
  );
  const sentCount = entries.filter((e) => e.sent).length;
  return (
    <CollapsibleCard
      iconBg="#EAF1FE"
      iconColor="#3B82F6"
      icon={<MailIcon size={19} />}
      title={t("admin.tenantTabs.emailHistoryTitle", "Email history")}
      subtitle={`${sentCount} ${t("admin.tenantTabs.sentCountUnit", "sent")} \u00b7 ${entries.length - sentCount} ${t("admin.tenantTabs.pendingCountUnit", "pending")} \u00b7 ${t("admin.tenantTabs.newestFirst", "newest first")}`}
      open={open}
      onToggle={() => setOpen((v) => !v)}
    >
      {entries.map((e, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 13,
            padding: "13px 24px",
            borderBottom: "1px solid #F5F6F7",
          }}
        >
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: 9,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: e.sent ? "#E6F7EF" : "#F1F3F6",
              color: e.sent ? "#10B981" : "#94A3B8",
              flexShrink: 0,
            }}
          >
            {e.sent ? (
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
            ) : (
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="9" />
                <path d="M12 8v4" />
              </svg>
            )}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              {e.title}
              {e.kind === "dunning" && (
                <span style={chip("#FEECEC", "#B91C1C", "#F7CFCF")}>
                  Dunning
                </span>
              )}
            </div>
            <div
              style={{
                fontSize: 12,
                color: "#94A3B8",
                fontWeight: 500,
                marginTop: 1,
              }}
            >
              {e.desc} · {e.recipient}
            </div>
          </div>
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <span style={e.sent ? CHIP.green : CHIP.slate}>
              {e.sent ? "Sent" : "Pending"}
            </span>
            <div
              style={{
                fontSize: 11.5,
                color: "#94A3B8",
                fontWeight: 500,
                marginTop: 4,
              }}
            >
              {e.sent
                ? e.ts
                  ? (formatDateTime(e.ts) ?? "Sent")
                  : "Sent"
                : "Not sent yet"}
            </div>
          </div>
        </div>
      ))}
    </CollapsibleCard>
  );
}

interface TimelineDoc {
  name: string;
  meta: string;
  url: string | null;
  /**
   * True when `url` is a path in the private `admin-notes` bucket rather than
   * something a browser can open. Those need a signed link, minted on click.
   */
  noteAttachment?: boolean;
  /**
   * True when `url` is a `tenant_documents.file_url` (a Storage path or the
   * public URL stored at upload). Signed on click via resolveTenantDocumentUrl,
   * never used as a link: the passport bucket is going private (COL-436).
   */
  tenantDocument?: boolean;
}
interface TimelineEvent {
  id: string;
  title: string;
  time: string;
  dot: string;
  rows: [string, string][];
  docs: TimelineDoc[];
}

function buildTimeline(
  journey: TenantJourney,
  booking: Record<string, any>,
  room: Record<string, any> | null,
  documents: any[],
  email: string,
  tr: (key: string, fallback?: string) => string,
  extra: {
    depositPaidAt: string | null;
    moveInSubmittedAt: string | null;
    moveOutSubmittedAt: string | null;
  },
  // Admin notes + uploaded documents (admin_notes rows). Each row may carry a
  // free-text `body` and/or an attached file — they surface as "Note added" /
  // "Document uploaded" events merged chronologically into the journey timeline.
  adminNotes: any[] = [],
  // Audit-log rows (manual edits) → "<what changed> updated" events, showing
  // which fields changed (old → new), the editor, and the timestamp.
  auditLogs: any[] = [],
  // Payment-scoped admin notes → "Payment note" events. These live on
  // admin_notes rows carrying a payment_id, which the tenant-scoped notes feed
  // deliberately filters out — without this they were invisible outside the
  // payment row that owns them.
  paymentNotes: any[] = [],
  // Payment rows, so a payment note can name the month it belongs to.
  payments: any[] = [],
  // Active UI language, so month names in payment-note scopes localize too.
  language?: string
): TimelineEvent[] {
  const fmtTs = (v: string | null | undefined) =>
    v ? (formatDateTime(v) ?? "—") : "—";
  const parseMs = (v: string | null | undefined): number | null => {
    if (!v) return null;
    const n = Date.parse(v);
    return Number.isNaN(n) ? null : n;
  };
  const byType = (type: string) =>
    documents.filter((d: any) => d.document_type === type);
  const docOf = (d: any): TimelineDoc => ({
    name:
      d.file_name ||
      d.title ||
      tr("admin.tenantTabs.timeline.document", "Document"),
    meta: [
      labelForDocType(d.document_type, undefined, tr),
      d.created_at ? formatDate(d.created_at) : null,
    ]
      .filter(Boolean)
      .join(" · "),
    url: d.file_url ?? null,
    tenantDocument: true,
  });

  const idDocs = byType("id_passport");
  const incomeDocs = byType("proof_of_income");
  const contractSentUrl =
    booking.contract_pdf_url || booking.contract_url || null;
  const contractSignedUrl =
    booking.signed_contract_url && isOpenableUrl(booking.signed_contract_url)
      ? booking.signed_contract_url
      : null;

  const DEFS: {
    id: TenantJourneyStageId;
    title: string;
    time: string;
    /** raw timestamp backing `time`, used to order the merged timeline. */
    iso: string | null;
    rows: [string, string][];
    docs: TimelineDoc[];
  }[] = [
    {
      id: "applied",
      title: tr(
        "admin.tenantTabs.timeline.applicationReceived",
        "Application received"
      ),
      time: fmtTs(booking.created_at),
      iso: booking.created_at ?? null,
      rows: [
        [
          tr("admin.tenantTabs.timeline.channel", "Channel"),
          tr(
            "admin.tenantTabs.timeline.websiteApplicationForm",
            "Website application form"
          ),
        ],
        ...(booking.description
          ? ([
              [
                tr(
                  "admin.tenantTabs.timeline.applicantMessage",
                  "Applicant message"
                ),
                `“${String(booking.description).trim()}”`,
              ],
            ] as [string, string][])
          : []),
        ...(idDocs[0]?.created_at
          ? ([
              [
                tr("admin.tenantTabs.timeline.idUploaded", "ID uploaded"),
                fmtTs(idDocs[0].created_at),
              ],
            ] as [string, string][])
          : []),
        ...(incomeDocs[0]?.created_at
          ? ([
              [
                tr(
                  "admin.tenantTabs.timeline.incomeVerified",
                  "Income verified"
                ),
                fmtTs(incomeDocs[0].created_at),
              ],
            ] as [string, string][])
          : []),
      ],
      docs: [...idDocs, ...incomeDocs].map(docOf),
    },
    {
      id: "signature_pending",
      title: tr(
        "admin.tenantTabs.timeline.contractGeneratedSent",
        "Contract generated & sent"
      ),
      // No dedicated "sent" column exists; the closest real signal is when the
      // contract PDF was last (re)generated.
      time: fmtTs(booking.contract_pdf_updated_at),
      iso: booking.contract_pdf_updated_at ?? null,
      rows: [
        [
          tr("admin.tenantTabs.timeline.sentTo", "Sent to"),
          email || booking.email || "—",
        ],
        [
          tr("admin.tenantTabs.timeline.method", "Method"),
          booking.docusign_envelope_id
            ? `DocuSign envelope #${booking.docusign_envelope_id}`
            : booking.manual_contract_upload
              ? tr("admin.tenantTabs.timeline.manualUpload", "Manual upload")
              : "DocuSign",
        ],
        [
          tr("admin.tenantTabs.contractStatusLabel", "Contract status"),
          String(booking.contract_status || "—").replace(/_/g, " "),
        ],
      ],
      docs: contractSentUrl
        ? [
            {
              name: tr(
                "admin.tenantTabs.timeline.leaseContractFileName",
                "Lease contract.pdf"
              ),
              meta: `${tr("admin.tenantTabs.timeline.contract", "Contract")} · ${tr("admin.tenantTabs.timeline.sent", "sent")}`,
              url: contractSentUrl,
            },
          ]
        : [],
    },
    {
      id: "deposit_pending",
      title: tr("admin.tenantTabs.timeline.contractSigned", "Contract signed"),
      time: fmtTs(booking.contract_signed_at),
      iso: booking.contract_signed_at ?? null,
      rows: [
        [
          tr("admin.tenantTabs.timeline.signedBy", "Signed by"),
          booking.name || "—",
        ],
        [
          tr("admin.tenantTabs.timeline.method", "Method"),
          booking.manual_contract_upload
            ? tr("admin.tenantTabs.timeline.manualUpload", "Manual upload")
            : tr(
                "admin.tenantTabs.contractElectronicSignature",
                "Electronic signature"
              ),
        ],
      ],
      docs: contractSignedUrl
        ? [
            {
              name: tr(
                "admin.tenantTabs.timeline.leaseContractSignedFileName",
                "Lease contract (signed).pdf"
              ),
              meta: `${tr("admin.tenantTabs.timeline.contract", "Contract")} · ${tr("admin.tenantTabs.timeline.signed", "signed")}`,
              url: contractSignedUrl,
            },
          ]
        : [],
    },
    {
      id: "key_pickup_pending",
      title: tr(
        "admin.tenantTabs.timeline.securityDepositPaid",
        "Security deposit paid"
      ),
      time: fmtTs(extra.depositPaidAt),
      iso: extra.depositPaidAt,
      rows: [
        [
          tr("admin.tenantTabs.timeline.amount", "Amount"),
          money(booking.deposit_amount),
        ],
        [
          tr("admin.tenantTabs.timeline.method", "Method"),
          tr(
            "admin.tenantTabs.timeline.sepaBankTransfer",
            "SEPA / bank transfer"
          ),
        ],
        [
          tr("admin.tenantTabs.timeline.reconciled", "Reconciled"),
          fmtTs(extra.depositPaidAt),
        ],
      ],
      docs: [],
    },
    {
      id: "handover_pending",
      title: tr("admin.tenantTabs.timeline.keyPickedUp", "Key picked up"),
      time: fmtTs(booking.key_picked_up_at),
      iso: booking.key_picked_up_at ?? null,
      rows: [
        [
          tr("admin.tenantTabs.timeline.pickupPoint", "Pickup point"),
          room?.keynest_partner_shop_name || "—",
        ],
        [
          tr("admin.tenantTabs.timeline.pickupCode", "Pickup code"),
          booking.keynest_collection_code || room?.keynest_pickup_code || "—",
        ],
        [
          tr("admin.tenantTabs.timeline.keySet", "Key set"),
          room?.keynest_key_label || "—",
        ],
        [
          tr("admin.tenantTabs.timeline.marked", "Marked"),
          fmtTs(booking.key_picked_up_at),
        ],
      ],
      docs: [],
    },
    {
      id: "active",
      title: tr(
        "admin.tenantTabs.timeline.moveInConfirmed",
        "Move-in confirmed — tenant active"
      ),
      // The protocol submission is the richer signal; `move_in_confirmed_at`
      // covers admin-confirmed move-ins with no protocol on file.
      time: fmtTs(extra.moveInSubmittedAt ?? booking.move_in_confirmed_at),
      iso: extra.moveInSubmittedAt ?? booking.move_in_confirmed_at ?? null,
      rows: [
        [
          extra.moveInSubmittedAt
            ? tr(
                "admin.tenantTabs.timeline.protocolSubmitted",
                "Protocol submitted"
              )
            : tr("admin.tenantTabs.timeline.confirmed", "Confirmed"),
          fmtTs(extra.moveInSubmittedAt ?? booking.move_in_confirmed_at),
        ],
        [
          tr("admin.tenantTabs.timeline.status", "Status"),
          tr("admin.tenantTabs.timeline.active", "Active"),
        ],
      ],
      docs: [],
    },
    {
      id: "moveout_scheduled",
      title: tr(
        "admin.tenantTabs.timeline.moveOutInitiated",
        "Move-out initiated"
      ),
      time: fmtTs(booking.move_out_notice_at),
      iso: booking.move_out_notice_at ?? null,
      rows: [
        [
          tr("admin.tenantTabs.timeline.noticeRecorded", "Notice recorded"),
          fmtTs(booking.move_out_notice_at),
        ],
      ],
      docs: [],
    },
    {
      id: "moveout_protocol_pending",
      title: tr(
        "admin.tenantTabs.timeline.moveOutDateConfirmed",
        "Move-out date confirmed"
      ),
      time: booking.move_out_date
        ? (formatDate(booking.move_out_date) ?? "—")
        : "—",
      iso: booking.move_out_date ?? null,
      rows: [
        [
          tr("admin.tenantTabs.timeline.confirmedDate", "Confirmed date"),
          formatDate(booking.move_out_date) ?? "—",
        ],
      ],
      docs: [],
    },
    {
      id: "key_return_pending",
      title: tr(
        "admin.tenantTabs.timeline.moveOutProtocolSubmitted",
        "Move-out protocol submitted"
      ),
      time: fmtTs(extra.moveOutSubmittedAt),
      iso: extra.moveOutSubmittedAt,
      rows: [
        [
          tr("admin.tenantTabs.timeline.submitted", "Submitted"),
          fmtTs(extra.moveOutSubmittedAt),
        ],
      ],
      docs: [],
    },
    {
      id: "moved_out",
      title: tr(
        "admin.tenantTabs.timeline.keyReturnedMovedOut",
        "Key returned — moved out"
      ),
      time: fmtTs(booking.key_returned_at),
      iso: booking.key_returned_at ?? null,
      rows: [
        [
          tr("admin.tenantTabs.timeline.returned", "Returned"),
          fmtTs(booking.key_returned_at),
        ],
        [
          tr("admin.tenantTabs.timeline.keySet", "Key set"),
          room?.keynest_key_label || "—",
        ],
      ],
      docs: [],
    },
  ];
  const stageIndex = (id: TenantJourneyStageId) =>
    TENANT_JOURNEY_STAGES.findIndex((s) => s.id === id);

  type SortableEvent = TimelineEvent & { sortMs: number };

  // Journey-stage events, in journey order. Stages with no real timestamp
  // (e.g. "Contract generated & sent" before a PDF exists) inherit the last
  // known real time so they stay slotted between their neighbours rather than
  // jumping to the epoch when the whole list is time-sorted below.
  let running = parseMs(booking.created_at) ?? 0;
  const stageEvents: SortableEvent[] = DEFS.filter((d) =>
    journey.reached(d.id)
  ).map((d, i) => {
    const ms = parseMs(d.iso);
    if (ms != null) running = ms;
    return {
      id: d.id,
      title: d.title,
      time: d.time,
      dot: TENANT_JOURNEY_STAGES[stageIndex(d.id)].dot,
      rows: d.rows,
      docs: d.docs,
      sortMs: ms ?? running + i,
    };
  });

  // Admin notes as their own events, split by which surface wrote them.
  //
  //   kind 'general'  → "Internal note": what an operator typed in the notes
  //                     drawer, thread and all. Its own lane, because a remark
  //                     somebody chose to write is not the same event as a file
  //                     arriving, and the drawer is where it can be answered.
  //   kind 'handover' → "Document uploaded", the audit row the key/contract
  //   or legacy null    upload flow records. Unchanged: a row with a file is an
  //                     upload, a text-only row is a "Note added".
  //
  // Payment-scoped notes are handled separately below.
  const noteEvents: SortableEvent[] = [];
  const nameOf = (n: any): string | null =>
    n?.author?.name || n?.author?.email || null;
  const bodyOf = (n: any): string =>
    n?.body != null ? String(n.body).trim() : "";
  const attachmentOf = (n: any, label: string): TimelineDoc[] =>
    n?.file_url
      ? [
          {
            name:
              n.file_name ||
              tr("admin.tenantTabs.timeline.document", "Document"),
            meta: [label, n.created_at ? formatDate(n.created_at) : null]
              .filter(Boolean)
              .join(" · "),
            url: n.file_url ?? null,
            /** Signed on click — see resolveTimelineDocHref. */
            noteAttachment: true,
          },
        ]
      : [];

  const internalNotes = adminNotes.filter((n: any) => n?.kind === "general");
  const otherNotes = adminNotes.filter((n: any) => n?.kind !== "general");

  // Replies belong to the note they answer, not to the timeline: three replies
  // to one remark is one thing that happened, and printing them as three
  // top-level "Internal note" events buries everything else on a busy tenant.
  const repliesByRoot = new Map<string, any[]>();
  const internalRootIds = new Set(
    internalNotes
      .filter((n: any) => !n?.parent_id)
      .map((n: any) => String(n.id))
  );
  for (const n of internalNotes) {
    const parent = n?.parent_id ? String(n.parent_id) : null;
    // An orphan (root retracted, or a reply whose root this scope cannot see)
    // still gets its own event — dropping it would lose the text entirely.
    if (!parent || !internalRootIds.has(parent)) continue;
    const list = repliesByRoot.get(parent);
    if (list) list.push(n);
    else repliesByRoot.set(parent, [n]);
  }

  for (const n of internalNotes) {
    const parent = n?.parent_id ? String(n.parent_id) : null;
    if (parent && internalRootIds.has(parent)) continue; // folded into its root

    const body = bodyOf(n);
    const docs = attachmentOf(
      n,
      tr("admin.tenantTabs.timeline.noteAttachment", "Note attachment")
    );
    if (!body && !docs.length) continue;

    const replies = (repliesByRoot.get(String(n.id)) ?? []).sort((a, b) =>
      String(a.created_at).localeCompare(String(b.created_at))
    );
    const author = nameOf(n);
    const rows: [string, string][] = [];
    if (body)
      rows.push([tr("admin.tenantTabs.timeline.note", "Note"), `“${body}”`]);
    if (author) rows.push([tr("admin.tenantTabs.timeline.by", "By"), author]);
    if (n?.edited_at)
      rows.push([
        tr("admin.tenantTabs.timeline.edited", "Edited"),
        fmtTs(n.edited_at),
      ]);
    // Each reply reads on its own line, so the conversation survives the fold.
    for (const r of replies) {
      const rBody = bodyOf(r);
      const rWho = nameOf(r);
      const when = fmtTs(r.created_at);
      rows.push([
        tr("admin.tenantTabs.timeline.reply", "Reply"),
        [
          rBody
            ? `“${rBody}”`
            : tr("admin.tenantTabs.timeline.attachment", "Attachment"),
          rWho,
          when,
        ]
          .filter(Boolean)
          .join(" · "),
      ]);
      docs.push(
        ...attachmentOf(
          r,
          tr("admin.tenantTabs.timeline.replyAttachment", "Reply attachment")
        )
      );
    }
    if (n?.resolved_at) {
      const resolver = n?.resolver?.name || n?.resolver?.email || null;
      rows.push([
        tr("admin.tenantTabs.timeline.resolved", "Resolved"),
        [resolver, fmtTs(n.resolved_at)].filter(Boolean).join(" · "),
      ]);
    }

    noteEvents.push({
      id: `internal-note-${n.id}`,
      title: tr("admin.tenantTabs.internalNoteLabel", "Internal note"),
      time: fmtTs(n.created_at),
      dot: "#7C3AED",
      rows,
      docs,
      // The thread sorts by when it STARTED. A reply is part of the same event,
      // so it must not drag the note back to the top months later.
      sortMs: parseMs(n?.created_at) ?? 0,
    });
  }

  for (const n of otherNotes) {
    const ms = parseMs(n?.created_at);
    const author = nameOf(n);
    const hasFile = !!n?.file_url;
    const body = bodyOf(n);
    const hasBody = body.length > 0;
    if (!hasFile && !hasBody) continue;
    const byRow: [string, string][] = author
      ? [[tr("admin.tenantTabs.timeline.by", "By"), author]]
      : [];
    if (hasFile) {
      noteEvents.push({
        id: `doc-${n.id}`,
        title: tr(
          "admin.tenantTabs.timeline.documentUploaded",
          "Document uploaded"
        ),
        time: fmtTs(n.created_at),
        dot: "#0E7490",
        rows: [
          ...(hasBody
            ? ([
                [tr("admin.tenantTabs.timeline.note", "Note"), `“${body}”`],
              ] as [string, string][])
            : []),
          ...byRow,
        ],
        docs: [
          {
            name:
              n.file_name ||
              tr("admin.tenantTabs.timeline.document", "Document"),
            meta: [
              tr("admin.tenantTabs.timeline.upload", "Upload"),
              n.created_at ? formatDate(n.created_at) : null,
            ]
              .filter(Boolean)
              .join(" · "),
            url: n.file_url ?? null,
          },
        ],
        sortMs: ms ?? 0,
      });
    } else {
      noteEvents.push({
        id: `note-${n.id}`,
        title: tr("admin.tenantTabs.timeline.noteAdded", "Note added"),
        time: fmtTs(n.created_at),
        dot: "#64748B",
        rows: [
          [tr("admin.tenantTabs.timeline.note", "Note"), `“${body}”`],
          ...byRow,
        ],
        docs: [],
        sortMs: ms ?? 0,
      });
    }
  }

  // Payment-scoped notes → their own events, labelled with the payment they
  // hang off ("Payment note · Rent July 2026") so the timeline says WHERE the
  // note lives, not just that one exists.
  const paymentById = new Map<string, any>(
    (payments || []).map((p: any) => [String(p.id), p])
  );
  for (const n of paymentNotes || []) {
    const body = n?.body != null ? String(n.body).trim() : "";
    const hasFile = !!n?.file_url;
    if (!body && !hasFile) continue;
    const p = paymentById.get(String(n.payment_id));
    const scope = p
      ? p.type === "deposit"
        ? tr("admin.tenantTabs.timeline.securityDeposit", "Security deposit")
        : `${tr("admin.tenantTabs.timeline.rentPrefix", "Rent")} ${monthLabel(p.due_date, language)}`
      : null;
    const author: string | null = n?.author?.name || n?.author?.email || null;
    noteEvents.push({
      id: `pay-note-${n.id}`,
      title: scope
        ? `${tr("admin.tenantTabs.timeline.paymentNote", "Payment note")} · ${scope}`
        : tr("admin.tenantTabs.timeline.paymentNote", "Payment note"),
      time: fmtTs(n.created_at),
      dot: "#10B981",
      rows: [
        ...(body
          ? ([[tr("admin.tenantTabs.timeline.note", "Note"), `“${body}”`]] as [
              string,
              string,
            ][])
          : []),
        ...(p?.amount_cents != null
          ? ([
              [
                tr("admin.tenantTabs.timeline.amount", "Amount"),
                centsToEUR(p.amount_cents),
              ],
            ] as [string, string][])
          : []),
        ...(author
          ? ([[tr("admin.tenantTabs.timeline.by", "By"), author]] as [
              string,
              string,
            ][])
          : []),
      ],
      docs: hasFile
        ? [
            {
              name:
                n.file_name ||
                tr("admin.tenantTabs.timeline.attachment", "Attachment"),
              meta: [
                tr("admin.tenantTabs.timeline.paymentNote", "Payment note"),
                formatDate(n.created_at),
              ]
                .filter(Boolean)
                .join(" · "),
              url: n.file_url ?? null,
            },
          ]
        : [],
      sortMs: parseMs(n?.created_at) ?? 0,
    });
  }

  // Manual edits from the audit log → "<topic> updated" events. Each shows the
  // changed fields (old → new), who edited, and when.
  const AUDIT_NOISE = new Set(["updated_at", "name"]); // `name` mirrors full_name
  // Keys and English copy live in tenant-audit-titles.ts, where the German
  // fallback guard test derives its key set from the same map.
  const AUDIT_LABELS: Record<string, string> = auditLabels(tr);
  const humanize = (k: string) =>
    k.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
  const auditLabel = (k: string) => AUDIT_LABELS[k] ?? humanize(k);
  // Value formatter is key-aware: cents → €, date/timestamp columns → a date.
  const auditVal = (k: string, v: any): string => {
    if (v == null || v === "") return "—";
    if (k === "amount_cents") {
      const n = Number(v);
      return Number.isFinite(n) ? money(n / 100) : String(v);
    }
    // rent_due_date is a day of the month (1–31), not a calendar date.
    if ((k.endsWith("_date") && k !== "rent_due_date") || k.endsWith("_at"))
      return formatDate(String(v)) ?? String(v);
    if (k === "notes") return `“${String(v).trim()}”`;
    return String(v);
  };
  const editEvents: SortableEvent[] = [];
  for (const a of auditLogs) {
    const ms = parseMs(a?.created_at);
    const actor: string =
      a?.actor?.name ||
      a?.actor?.email ||
      tr("admin.tenantTabs.timeline.adminFallback", "Admin");
    const action = String(a?.action ?? "");
    const meta =
      a?.metadata && typeof a.metadata === "object" ? a.metadata : {};
    const nv =
      a?.new_values && typeof a.new_values === "object" ? a.new_values : {};
    const ov =
      a?.old_values && typeof a.old_values === "object" ? a.old_values : {};
    const keys = Object.keys(nv).filter((k) => !AUDIT_NOISE.has(k));
    const changeRows: [string, string][] = keys.map((k) => [
      auditLabel(k),
      `${auditVal(k, (ov as any)[k])} → ${auditVal(k, (nv as any)[k])}`,
    ]);
    // Rows prepended before the change list (document name, deletion reason…).
    const contextRows: [string, string][] = [];
    let title: string;
    let dot = "#6366F1";
    if (action === "payment_proof_deleted") {
      title = tr(
        "admin.tenantTabs.timeline.paymentProofDeleted",
        "Payment proof deleted"
      );
    } else if (a?.resource_type === "tenant_document") {
      // Document-scoped edits (note / rename / delete) get their own titles —
      // otherwise a saved note surfaced only as a bare "Details updated".
      const docName =
        (meta as any).file_name ||
        (nv as any).file_name ||
        (ov as any).file_name ||
        null;
      title =
        action === "tenant_document_note_added"
          ? tr(
              "admin.tenantTabs.timeline.documentNoteAdded",
              "Document note added"
            )
          : action === "tenant_document_note_updated"
            ? tr(
                "admin.tenantTabs.timeline.documentNoteUpdated",
                "Document note updated"
              )
            : action === "tenant_document_note_cleared"
              ? tr(
                  "admin.tenantTabs.timeline.documentNoteRemoved",
                  "Document note removed"
                )
              : action === "tenant_document_renamed"
                ? tr(
                    "admin.tenantTabs.timeline.documentRenamed",
                    "Document renamed"
                  )
                : action === "tenant_document_relabelled"
                  ? tr(
                      "admin.tenantTabs.timeline.documentLabelUpdated",
                      "Document label updated"
                    )
                  : action === "tenant_document_deleted"
                    ? tr(
                        "admin.tenantTabs.timeline.documentDeleted",
                        "Document deleted"
                      )
                    : tr(
                        "admin.tenantTabs.timeline.documentUpdated",
                        "Document updated"
                      );
      dot = action === "tenant_document_deleted" ? "#DC2626" : "#0E7490";
      if (docName)
        contextRows.push([
          tr("admin.tenantTabs.timeline.document", "Document"),
          String(docName),
        ]);
    } else if (a?.resource_type === "payment" || (meta as any).payment_type) {
      const pt = (meta as any).payment_type;
      if (action === "payment_scheduled") {
        title = tr(
          "admin.tenantTabs.timeline.upcomingRentScheduled",
          "Upcoming rent scheduled"
        );
      } else {
        title = pt
          ? `${tr("admin.tenantTabs.timeline.paymentUpdated", "Payment updated")} · ${pt}`
          : tr("admin.tenantTabs.timeline.paymentUpdated", "Payment updated");
      }
    } else if (auditActionTitle(action)) {
      const titleKey = AUDIT_TITLE_KEYS[action];
      title = titleKey
        ? tr(titleKey, auditActionTitle(action)!)
        : auditActionTitle(action)!;
      // A reversal is amber, not the neutral edit indigo — scanning the log for
      // "what got taken back" is the reason reversals are recorded at all.
      if (isReversalAction(action)) dot = "#D97706";
    } else if (keys.length === 1 && AUDIT_LABELS[keys[0]]) {
      // One column with its own translated label: "Updated Monthly rent".
      title = `${tr(AUDIT_UPDATED_PREFIX_KEY, "Updated")} ${auditLabel(keys[0])}`;
    } else {
      // Several columns (an edit-form save, or a system job), or one with no
      // label of its own: name the topics they fall under instead of the old
      // catch-all "Details updated" (or a raw English column name).
      // `name` is filtered out of the row list (it mirrors full_name), but a
      // row that changed nothing else is still a name change.
      const titleKeys =
        keys.length === 0 && "name" in nv ? ["full_name"] : keys;
      title = describeChangedFields(titleKeys, tr, language !== "de");
    }
    // Any audited action may carry a written reason — a document deletion, a
    // rent change, a tenant discount. It leads the row list because it is what
    // a reader needs first: the diff says what moved, the reason says why
    // (COL-360; before this only document rows rendered it).
    if ((meta as any).reason) {
      contextRows.unshift([
        tr("admin.tenantTabs.timeline.reason", "Reason"),
        `“${String((meta as any).reason)}”`,
      ]);
    }
    editEvents.push({
      id: `audit-${a.id}`,
      title,
      time: fmtTs(a.created_at),
      dot,
      rows: [
        ...contextRows,
        ...changeRows,
        [tr("admin.tenantTabs.timeline.by", "By"), actor],
      ],
      docs: [],
      sortMs: ms ?? 0,
    });
  }

  // Merge and order newest-first by real timestamp.
  return [...stageEvents, ...noteEvents, ...editEvents]
    .sort((a, b) => b.sortMs - a.sortMs)
    .map(({ sortMs: _sortMs, ...ev }) => ev);
}

function TimelineRow({
  ev,
  expandSignal,
}: {
  ev: TimelineEvent;
  expandSignal?: { open: boolean; n: number } | null;
}) {
  const [open, setOpen] = useState(false);
  // Apply an expand-all / collapse-all pulse from the parent. Keyed on `n` so
  // the same direction can be re-applied after the user toggles a row.
  useEffect(() => {
    if (expandSignal) setOpen(expandSignal.open);
  }, [expandSignal?.n]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div style={{ display: "flex", gap: 14 }}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <div
          style={{
            width: 11,
            height: 11,
            borderRadius: "50%",
            background: ev.dot,
            border: "2px solid #fff",
            boxShadow: `0 0 0 2px ${ev.dot}`,
            marginTop: 6,
          }}
        />
        <div
          style={{ width: 2, flex: 1, background: "#EDEFF2", margin: "4px 0" }}
        />
      </div>
      <div style={{ flex: 1, minWidth: 0, paddingBottom: 14 }}>
        <button
          onClick={() => setOpen((v) => !v)}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: 10,
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
            textAlign: "left",
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14.5, fontWeight: 600 }}>{ev.title}</div>
            <div
              style={{
                fontSize: 12.5,
                color: "#94A3B8",
                marginTop: 2,
                fontWeight: 500,
              }}
            >
              {ev.time}
            </div>
          </div>
          <Chevron open={open} size={15} />
        </button>
        {open && (
          <div style={{ ...innerBox, marginTop: 12, padding: "14px 16px" }}>
            {ev.rows.map(([label, value]) => (
              <div
                key={label}
                style={{ display: "flex", gap: 12, padding: "5px 0" }}
              >
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: ".04em",
                    color: "#94A3B8",
                    textTransform: "uppercase",
                    width: 120,
                    flexShrink: 0,
                  }}
                >
                  {label}
                </div>
                <div
                  style={{
                    fontSize: 13.5,
                    fontWeight: 500,
                    color: "#334155",
                    flex: 1,
                  }}
                >
                  {value}
                </div>
              </div>
            ))}
            {ev.docs.length > 0 && (
              <div
                style={{
                  marginTop: 10,
                  paddingTop: 12,
                  borderTop: "1px solid #EDEFF2",
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                {ev.docs.map((doc, i) => (
                  <TimelineDocRow key={i} doc={doc} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function TimelineDocRow({ doc }: { doc: TimelineDoc }) {
  const { t } = useLanguage();
  const iconBtn: React.CSSProperties = {
    width: 30,
    height: 30,
    borderRadius: 8,
    border: "1px solid #E7E9EC",
    background: "#fff",
    color: "#64748B",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    cursor: "pointer",
  };
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 11,
        background: "#fff",
        border: "1px solid #EDEFF2",
        borderRadius: 11,
        padding: "9px 11px",
      }}
    >
      <SectionIcon bg="#EAF1FE" color="#3B82F6" size={32} radius={9}>
        <FileIcon size={16} />
      </SectionIcon>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {doc.name}
        </div>
        <div
          style={{
            fontSize: 11.5,
            color: "#94A3B8",
            fontWeight: 500,
            marginTop: 1,
          }}
        >
          {doc.meta}
        </div>
      </div>
      {doc.url && (doc.noteAttachment || doc.tenantDocument) ? (
        // Private (or going-private) bucket: one button, and the link is
        // minted when it is clicked.
        <button
          type="button"
          onClick={() =>
            void (doc.noteAttachment
              ? openNoteAttachment(doc.url as string)
              : openSignedTenantDocument(doc.url as string, {
                  fileName: doc.name,
                }))
          }
          title={t("admin.tenantTabs.openAttachment", "Open attachment")}
          aria-label={`${t("admin.tenantTabs.openPrefix", "Open")} ${doc.name}`}
          style={iconBtn}
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        </button>
      ) : doc.url ? (
        // A contract PDF: a stored public URL into the contracts bucket,
        // signed on click like everything else here (COL-436).
        <>
          <StoredFileLink
            value={doc.url}
            bucket="contracts"
            errorText={t(
              "admin.docs.openFailed",
              "Could not open the document."
            )}
            title={t("admin.tenantTabs.contractViewerPreview", "Preview")}
            style={iconBtn}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          </StoredFileLink>
          <StoredFileLink
            value={doc.url}
            bucket="contracts"
            download={doc.name}
            errorText={t(
              "admin.docs.openFailed",
              "Could not open the document."
            )}
            title={t("admin.tenantTabs.contractViewerDownload", "Download")}
            style={iconBtn}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
            </svg>
          </StoredFileLink>
        </>
      ) : (
        <span style={{ fontSize: 11.5, color: "#CBD5E1", fontWeight: 500 }}>
          {t("admin.tenantTabs.noFile", "No file")}
        </span>
      )}
    </div>
  );
}

/* ================================================================== */
/* PAYMENTS                                                            */
/* ================================================================== */

export function PaymentsTab({
  booking,
  payments,
  depositPaid,
  depositPaidAt,
  sepa,
  stageId,
  busy,
  proofByPayment,
  receiptsByPayment = {},
  bookingId,
  userId,
  paymentsActivity,
  noteCountByPayment,
  onMarkDepositPaid,
  onViewProof,
  onUploadProof,
  onUploadDepositProof,
  onDeleteProof,
  onEditPayment,
  onCreateScheduledRent,
  onSetFirstMonthOverride,
  onSetRentDueDay,
  onSendReminder,
  onChargeSepa,
  onDecideDdCancellation,
  loading,
  canManage = false,
}: {
  booking: Record<string, any>;
  payments: any[];
  depositPaid: boolean;
  depositPaidAt: string | null;
  /** Real mandate + room-config state; null hides the SEPA card (tests). */
  sepa: TenantSepaStatus | null;
  stageId: TenantJourneyStageId;
  busy: boolean;
  proofByPayment: Record<string, any>;
  /** COL-417: receipts per payment id. Passed in, not fetched here — this
   *  component is rendered in tests without a QueryClientProvider, and its
   *  sibling data (proofs, notes) arrives the same way. */
  receiptsByPayment?: Record<string, PaymentReceipt[]>;
  bookingId: number | null;
  userId: string | null;
  paymentsActivity: any[];
  /** How many admin notes each payment carries — shown on its Notes button. */
  noteCountByPayment: Record<string, number>;
  onMarkDepositPaid: () => void;
  onViewProof: (proof: any) => void;
  onUploadProof: (paymentId: string) => void;
  onUploadDepositProof: () => void;
  onDeleteProof: (proof: any) => void;
  onEditPayment: (
    paymentId: string,
    patch: {
      amount_cents?: number;
      due_date?: string;
      status?: string;
      /** COL-621: the full rent on a kickback month; null clears it. */
      gross_amount_cents?: number | null;
    }
  ) => Promise<void>;
  /** Insert a real `payments` row for a projected upcoming month. */
  onCreateScheduledRent: (input: {
    dueDate: string;
    amountCents: number;
    markPaid?: boolean;
    /** COL-621: the full rent when the month is a kickback. */
    grossAmountCents?: number | null;
  }) => Promise<string | null>;
  onSetFirstMonthOverride: (euros: number | null) => Promise<void>;
  /**
   * Set (or, with `null`, clear back to the default 1st) the admin's rent
   * due-day override (COL-410). Applies to this booking's future, still-open
   * rent rows only — the current month is never touched, and reminders
   * realign automatically.
   */
  onSetRentDueDay: (day: number | null) => Promise<void>;
  onSendReminder: () => void;
  /** Open the "Collect via direct debit" confirm for one rent row (5a). */
  onChargeSepa?: (payment: any) => void;
  /** Decide a tenant's request to end their mandate (feedback 6). */
  onDecideDdCancellation?: (
    requestId: string,
    decision: "approve" | "reject"
  ) => void;
  loading: boolean;
  /**
   * Admin or super-admin. Attesting the deposit also confirms the booking, so
   * this action is a booking-status write and carries the same gate.
   */
  canManage?: boolean;
}) {
  const { t, language } = useLanguage();
  const [rentLimit, setRentLimit] = useState(5);
  const [showAllSchedule, setShowAllSchedule] = useState(false);
  // Payment whose notes panel should open on mount — set when a projected month
  // is materialised via its "Notes" action, so the freshly-created real row
  // comes up with the notes already expanded instead of silently collapsed.
  const [autoNotesFor, setAutoNotesFor] = useState<string | null>(null);
  // Sections start collapsed so the profile opens cleanly (feedback #14).
  const [rentOpen, setRentOpen] = useState(false);
  // Signed-mandate PDF embed on the SEPA card (feedback 3a) — collapsed too.
  // The signed URL of the open mandate PDF, null while collapsed. Minted on
  // open: pdf_url is a public tenant-documents URL and that bucket holds the
  // IBAN, so it is going private (COL-436).
  const [mandatePdfSrc, setMandatePdfSrc] = useState<string | null>(null);
  const mandatePdfOpen = mandatePdfSrc !== null;
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [dueDayOpen, setDueDayOpen] = useState(false);
  const [depositOpen, setDepositOpen] = useState(false);
  const [depositNotesOpen, setDepositNotesOpen] = useState(false);
  // Canceled rows are deduped duplicates (e.g. a scheduled rent superseded by
  // the matching Stripe payment) — listing them reads as a ghost "Pending" row.
  const rentRows = payments
    .filter((p: any) => p.type === "rent" && p.status !== "canceled")
    .sort((a: any, b: any) =>
      (a.due_date || "").localeCompare(b.due_date || "")
    );
  const depositRows = payments.filter(
    (p: any) => p.type === "deposit" && p.status !== "canceled"
  );

  // ── What the deposit card states ──────────────────────────────────────
  // Expected is the CONTRACTUAL deposit stored on the booking (#767) — never a
  // sum of the billed rows, which can be partial, duplicated or missing.
  const depositExpectedEUR = Number(booking.deposit_amount) || 0;
  const depositPaidEUR =
    depositRows
      .filter((p: any) => effectivelyPaid(p, proofByPayment))
      .reduce((sum: number, p: any) => sum + (Number(p.amount_cents) || 0), 0) /
    100;
  const depositOutstandingEUR = Math.max(
    0,
    depositExpectedEUR - depositPaidEUR
  );
  // Does anything actually back the money — a transfer proof, or an admin who put
  // their name to it? A bare `status = 'paid'` is somebody's word for it, and the
  // journey deliberately ignores it, so the card must not claim more than it has.
  const depositProofOnFile = depositRows.some(
    (p: any) => !!proofByPayment[p.id]
  );
  const depositAttested = depositRows.some(
    (p: any) => !!p.marked_paid_by || !!p.marked_paid_at
  );
  const depositSummary = ((): {
    /**
     * `none`     — no deposit is agreed on this booking, so none is owed.
     * `mismatch` — money against a deposit the booking does not have.
     */
    state: "paid" | "partial" | "due" | "none" | "mismatch";
    label: string;
    chip: React.CSSProperties;
    line: string;
  } => {
    if (depositPaid) {
      const backing = depositProofOnFile
        ? t(
            "admin.tenantTabs.depositSummary.transferProofOnFile",
            "transfer proof on file"
          )
        : depositAttested
          ? t(
              "admin.tenantTabs.depositSummary.recordedByAdmin",
              "recorded by an admin"
            )
          : t(
              "admin.tenantTabs.depositSummary.noProofOnFile",
              "no proof on file"
            );
      const paidLabel = t("admin.tenantTabs.depositSummary.paid", "Paid");
      return {
        state: "paid",
        label: paidLabel,
        chip: CHIP.green,
        line: [
          depositPaidAt
            ? `${paidLabel} ${formatDate(depositPaidAt) ?? ""}`
            : paidLabel,
          backing,
        ].join(" · "),
      };
    }
    // Nothing is CONTRACTUALLY owed (booking.deposit_amount is unset or zero).
    // "Pending · nothing received yet" against €0.00 invents a debt: it puts a
    // deposit on the admin's chase list, and offers to record a payment for a
    // deposit that does not exist. Both states below are read-only statements of
    // fact instead.
    if (depositExpectedEUR <= 0) {
      if (depositPaidEUR > 0) {
        return {
          state: "mismatch",
          label: t(
            "admin.tenantTabs.depositSummary.checkDeposit",
            "Check deposit"
          ),
          chip: CHIP.amber2,
          line: `${money(depositPaidEUR)} ${t("admin.tenantTabs.depositSummary.receivedButNoDepositSet", "received, but no deposit is set on this booking")}`,
        };
      }
      return {
        state: "none",
        label: t("admin.tenantTabs.depositSummary.noneAgreed", "None agreed"),
        chip: CHIP.slate2,
        line: t(
          "admin.tenantTabs.depositSummary.noDepositSetNothingOwed",
          "No deposit is set on this booking · nothing is owed"
        ),
      };
    }
    if (depositPaidEUR > 0) {
      return {
        state: "partial",
        label: t("admin.tenantTabs.depositSummary.partPaid", "Part-paid"),
        chip: CHIP.amber2,
        // The shortfall is already called out under the amount; repeating it here
        // just crowded the line.
        line: `${money(depositPaidEUR)} ${t("admin.tenantTabs.depositSummary.ofReceived", "of")} ${money(depositExpectedEUR)} ${t("admin.tenantTabs.depositSummary.received", "received")}${depositProofOnFile ? ` · ${t("admin.tenantTabs.depositSummary.transferProofOnFile", "transfer proof on file")}` : ""}`,
      };
    }
    return {
      state: "due",
      label: t("admin.tenantTabs.depositSummary.pending", "Pending"),
      chip: CHIP.amber,
      line: depositProofOnFile
        ? t(
            "admin.tenantTabs.depositSummary.proofOnFileNotSettled",
            "A transfer proof is on file but the deposit is not settled yet"
          )
        : t(
            "admin.tenantTabs.depositSummary.nothingReceivedYetNoProof",
            "Nothing received yet · no transfer proof on file"
          ),
    };
  })();
  // Money in / money owed are corroborated-only, matching the row badges and
  // every rollup outside this tab. A row whose `status` says paid with nothing
  // behind it is money we cannot show as received — its badge reads "Unverified"
  // and it stays in Outstanding until a proof or an attestation lands.
  //
  // COL-417: both tiles count MONEY, not months. A month with EUR 200 of EUR 650
  // received adds 200 here and 450 to Outstanding, so the two tiles still sum to
  // what was billed — before this they added 0 and 650, which read as if nothing
  // had arrived at all. `resolveRentSettlement` is the one resolver behind the
  // row badges and the reminder email, so no surface can disagree with another;
  // it reports received = expected for a settled row that predates receipts, and
  // for any caller that does not select `received_cents`, which keeps every
  // historical figure exactly where it was.
  const settlementOf = (p: any) =>
    resolveRentSettlement(p, (id) => proofByPayment[id] ?? null);
  // `appliedCents`, not `receivedCents`: an overpayment is deliberately visible
  // on two rows — the overpaid month keeps the full amount in `received_cents`
  // and `carry_forward_overpayment()` also books it as a `credit_carryover`
  // receipt on the next open month. Summing the raw figure would count those
  // euros twice (EUR 800 against a EUR 650 month reads as EUR 950 paid to date).
  // Capping each row at what that row was billed counts them once, on the month
  // they settle, and keeps this tile and Outstanding summing to what was billed.
  const paidTotal = payments
    .filter((p: any) => String(p.status ?? "").toLowerCase() !== "canceled")
    .reduce((s: number, p: any) => s + settlementOf(p).appliedCents, 0);
  const today = businessToday();
  // Only CANCELED rows are voided (that's what the duplicate-dedup migration
  // writes to retire a superseded row). A `failed` row is a collection that
  // bounced — the money is still owed and someone has to re-collect it, so it
  // counts as outstanding and stays listed rather than vanishing from the tab.
  const unpaid = (p: any) =>
    !effectivelyPaid(p, proofByPayment) &&
    String(p.status ?? "").toLowerCase() !== "canceled";
  const outstanding = payments
    .filter((p: any) => unpaid(p) && p.due_date && p.due_date <= today)
    .reduce((s: number, p: any) => s + settlementOf(p).remainingCents, 0);
  const nextDue = payments
    .filter((p: any) => unpaid(p) && p.due_date)
    .sort((a: any, b: any) =>
      (a.due_date || "").localeCompare(b.due_date || "")
    )[0];
  // COL-472: Mahngebühren are one pool on the booking, never part of a month's
  // amount_cents. They are already owed, so Outstanding adds them once, and one
  // due month carries them — placed by the same rule as the reminders list
  // (see feeCarrierPaymentId), so the two screens agree. A pool left over after
  // the rent was settled shows in Outstanding only.
  const feePoolCents = accruedFeeCents(booking.accumulated_fees_eur);
  const outstandingWithFees = outstanding + feePoolCents;
  // Outstanding also counts non-rent rows (an unpaid deposit), so the split
  // under it names rent and the rest separately rather than calling it all rent.
  const outstandingRent = payments
    .filter(
      (p: any) =>
        p?.type === "rent" && unpaid(p) && p.due_date && p.due_date <= today
    )
    .reduce((s: number, p: any) => s + settlementOf(p).remainingCents, 0);
  const feeCarrierId = feeCarrierPaymentId({
    openRows: payments.filter((p: any) => p?.type === "rent" && unpaid(p)),
    rentDueDay: Number(booking.rent_due_date) || 1,
    todayIso: today,
  });

  const moveIn =
    booking.actual_move_in_date || booking.move_in_date || booking.start_date;
  const standardRentCents = Math.round(
    (Number(booking.monthly_rent) || 0) * 100
  );

  // The month the schedule window starts at. Normally the current one; for a
  // lease that has not started yet, its FIRST month — a November lease viewed in
  // August windows November–January rather than resolving to nothing at all.
  const currentYm = today.slice(0, 7);
  const moveInYm = moveIn ? String(moveIn).slice(0, 7) : null;
  const windowStartYm = moveInYm && moveInYm > currentYm ? moveInYm : currentYm;
  // One anchor for BOTH the split below and the projection further down: given
  // separate anchors, a future lease's real November rows sorted into `laterOpen`
  // while November was simultaneously being projected as missing. Noon UTC so
  // the anchor lands on the same calendar day in Germany as in UTC — `today` is
  // already the German business day, and rentScheduleWindowEnd reads local parts.
  const scheduleAnchor = new Date(
    windowStartYm === currentYm
      ? `${today}T12:00:00Z`
      : `${windowStartYm}-01T12:00:00Z`
  );

  // Rent split for the two cards (feedback #12, revised): history = PAID rent
  // only; schedule = everything still owed, through the two-month window. The
  // current month therefore stays in the schedule until it is marked paid, then
  // moves to history — a pending month is not history just because its due date
  // passed. See splitRentRows; both cards render the same RentPaymentRow table.
  const {
    history: paidRent,
    schedule: openRent,
    laterOpen: laterOpenRent,
  } = splitRentRows(
    rentRows,
    (p: any) => effectivelyPaid(p, proofByPayment),
    // Canceled only — a failed (bounced) collection is still owed, so it must
    // stay in the schedule where it can be acted on.
    (p: any) => String(p.status ?? "").toLowerCase() === "canceled",
    scheduleAnchor
  );
  const firstRentId = rentRows[0]?.id ?? null;

  // Rent payment rows are only created as each month comes due, so a freshly
  // active lease has no future rows yet — which left the schedule and Next due
  // blank. Project the upcoming months from the lease (monthly rent, the rent
  // day-of-month, tenancy end) as read-only "scheduled" entries to fill the gap.
  //
  // ONLY a confirmed departure bounds the projection — never `end_date`. Same
  // predicate as the materializer (decideMaterialization's `tenancy_ended`) and
  // dunning (`isOccupyingNow`), deliberately: `end_date` is the earliest
  // POSSIBLE move-out, contracts auto-prolong unless someone cancels, and on
  // prod every live booking carries one while none has a confirmed move-out.
  // Bounding on it here made the card promise "no open rent — lease ends …" for
  // months the materializer goes on to invoice (Neel, 2026-09-12; same anchor
  // the move-out emails and dunning were moved off in #1066/#1067).
  const leaseEndIso =
    booking.actual_move_out_date || booking.move_out_date || null;
  /**
   * The lease's rent for the month a due date falls in — what a projected row
   * shows, and what a kickback on a real row is measured from (COL-621,
   * kickbackBaseCents). Discount and all: a projected row must show what will
   * actually be billed, otherwise the schedule promises €650 for a month the
   * materializer will invoice at €600 (COL-360).
   */
  const leaseRentCentsForDue = (dueIso: string): number => {
    if (standardRentCents <= 0 || !moveIn) return 0;
    const year = Number(dueIso.slice(0, 4));
    const monthIdx = Number(dueIso.slice(5, 7)) - 1;
    if (!Number.isFinite(year) || !Number.isFinite(monthIdx)) return 0;
    const amountCents = getExpectedRentCentsForDueCalendarMonth({
      fullMonthlyRent: standardRentCents / 100,
      moveInIso: moveIn,
      firstMonthOverride: booking.first_month_rent_override ?? null,
      dueYear: year,
      dueMonthIndex0: monthIdx,
    });
    const discountOffCents = Math.round(
      rentDiscountOffEURForMonth(booking, dueIso) * 100
    );
    return Math.max(0, amountCents - discountOffCents);
  };

  /**
   * The lease's rent row for one calendar month (`YYYY-MM`), projected — used
   * for months that have no real `payments` row yet. Returns null when that
   * month is outside the lease.
   *
   * Month-keyed on purpose. The previous version walked forward from move-in
   * and skipped anything not strictly in the future, so a month whose due date
   * had just passed WITH NO REAL ROW fell into a hole: on 3 August the schedule
   * jumped from an unpaid July straight to September.
   */
  const projectRentForMonth = (ym: string): any | null => {
    if (standardRentCents <= 0 || !moveIn) return null;
    const start = new Date(moveIn);
    if (Number.isNaN(start.getTime())) return null;
    // Nothing is owed before the lease starts.
    if (ym < moveIn.slice(0, 7)) return null;

    // Anchor the due day on the admin's override (COL-410) when set — that's
    // the explicit intent for every future month. Otherwise fall back to a
    // full-rent row (not a prorated first month) so it reflects the
    // steady-state schedule, then the latest real row, then the move-in day.
    const fullRentRows = rentRows.filter(
      (r: any) => r.due_date && r.amount_cents === standardRentCents
    );
    const anchorRow =
      fullRentRows[fullRentRows.length - 1] ??
      [...rentRows].reverse().find((r: any) => r.due_date) ??
      null;
    // The override never applies to the current month — it takes effect the
    // month after it's set, same as the bulk rewrite in set_rent_due_day.
    const anchorDay =
      (ym > today.slice(0, 7) ? booking.rent_due_day_override : null) ??
      (anchorRow ? new Date(anchorRow.due_date) : start).getUTCDate();

    const year = Number(ym.slice(0, 4));
    const monthIdx = Number(ym.slice(5, 7)) - 1;
    if (!Number.isFinite(year) || !Number.isFinite(monthIdx)) return null;
    // Day 31 anchored on a 30-day month lands on that month's last day rather
    // than rolling into the next one.
    const clampedDay = clampDueDayInMonth(anchorDay, ym);
    const d = new Date(Date.UTC(year, monthIdx, clampedDay));
    const iso = d.toISOString().slice(0, 10);

    const leaseEnd = leaseEndIso ? new Date(leaseEndIso) : null;
    // A final instalment due exactly on the lease-end date is still owed.
    if (leaseEnd && !Number.isNaN(leaseEnd.getTime()) && d > leaseEnd)
      return null;

    return {
      id: `projected-${ym}`,
      type: "rent",
      amount_cents: leaseRentCentsForDue(iso),
      due_date: iso,
      status: "scheduled",
      __projected: true,
    };
  };
  const addMonths = (ym: string, n: number): string => {
    const y = Number(ym.slice(0, 4));
    const m = Number(ym.slice(5, 7)) - 1 + n;
    const d = new Date(Date.UTC(y, m, 1));
    return d.toISOString().slice(0, 7);
  };
  // Everything still owed, plus projected months so this month and the next two
  // (the same span splitRentRows uses) are always visible — rent rows are only
  // created as each month comes due, so a fresh lease has no future rows yet.
  // Sorted together so a projected row filling a gap earlier than an existing
  // real row still surfaces first.
  //
  // Keyed by MONTH rather than walked forward from move-in emitting only
  // strictly future dates: a month whose due date has just passed with no real
  // row fell into a hole that way — on 3 August the schedule jumped from an
  // unpaid July straight to September. A lease that has not started yet
  // projects its own first months instead of three empty ones.
  const existingRentMonths = new Set(
    rentRows.map((r: any) => String(r.due_date || "").slice(0, 7))
  );
  const upcomingDisplay = [
    ...openRent,
    // Never duplicate a real row: a month that already has one is listed from
    // `openRent`, or is paid and belongs to the history card.
    ...[0, 1, 2]
      .map((n) => addMonths(windowStartYm, n))
      .filter((ym) => !existingRentMonths.has(ym))
      .map((ym) => projectRentForMonth(ym))
      .filter(Boolean),
  ].sort((a: any, b: any) =>
    (a.due_date || "").localeCompare(b.due_date || "")
  );
  // Long back-lists (imported months whose "paid" status nothing corroborates)
  // would otherwise push the actionable current month far down the card, so the
  // OLDEST open months collapse behind a toggle — never the newest ones, and
  // never silently: the button says how many are folded away.
  const SCHEDULE_VISIBLE = 6;
  const olderOpenCount = Math.max(0, upcomingDisplay.length - SCHEDULE_VISIBLE);
  const scheduleShown =
    showAllSchedule || olderOpenCount === 0
      ? upcomingDisplay
      : upcomingDisplay.slice(olderOpenCount);

  // Next due (feedback #11): the next unpaid rent, else the next upcoming or
  // projected month. `prevRentRow` is the month before it, for the comparison.
  const nextDueRow = nextDue ?? upcomingDisplay[0] ?? null;
  const prevRentRow = nextDueRow
    ? (rentRows
        .filter((p: any) => (p.due_date || "") < (nextDueRow.due_date || ""))
        .slice(-1)[0] ?? null)
    : (rentRows[rentRows.length - 1] ?? null);
  // Which referral put a EUR 100 credit on which rent row (COL-434) — one
  // RPC for the whole table, so a discounted month can say why.
  const referralNotesByPayment = useReferralCreditNotes(
    payments
      .filter((p: any) => p?.type === "rent" && p?.id != null)
      .map((p: any) => String(p.id))
  );

  const rowProps = {
    proofByPayment,
    receiptsByPayment,
    // Bounds the Mahngebühr portion of a recorded payment. Same column the
    // reminder emails itemise, so the dialog cannot offer to allocate fees the
    // tenant was never charged.
    accruedFeesEur: Number(booking.accumulated_fees_eur ?? 0),
    noteCountByPayment,
    referralNotesByPayment,
    busy,
    onViewProof,
    onUploadProof,
    onDeleteProof,
    onEditPayment,
    bookingId,
    userId,
    onSendReminder,
    reminderStage: booking.reminder_stage ?? null,
    reminderSentAt: booking.reminder_sent_at ?? null,
    rentDueDay: booking.rent_due_date ?? null,
    // "Collect via direct debit" only offers itself when it could actually
    // succeed: active mandate + the room's opt-in gate (5a). The edge
    // function re-checks both — this just keeps the button honest.
    sepaChargeable: !!(
      sepa?.ready &&
      sepa.hasActiveMandate &&
      sepa.roomSepaEnabled &&
      onChargeSepa
    ),
    onChargeSepa,
  };

  if (loading && payments.length === 0) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3,1fr)",
            gap: 14,
          }}
        >
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              style={{
                background: "#fff",
                border: "1px solid #ECEEF1",
                borderRadius: 18,
                padding: "18px 20px",
              }}
            >
              <Skeleton h={11} w="55%" />
              <Skeleton h={22} w="70%" style={{ marginTop: 10 }} />
            </div>
          ))}
        </div>
        {[0, 1].map((i) => (
          <div
            key={i}
            style={{
              background: "#fff",
              border: "1px solid #ECEEF1",
              borderRadius: 20,
              padding: "20px 24px",
            }}
          >
            <Skeleton h={16} w="40%" />
            <Skeleton h={12} w="60%" style={{ marginTop: 10 }} />
            <Skeleton h={44} w="100%" style={{ marginTop: 16 }} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {/* summary strip */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3,1fr)",
          gap: 14,
        }}
      >
        <StatCard
          label={t("admin.tenantTabs.paidToDate", "Paid to date")}
          value={centsToEUR(paidTotal)}
        />
        <StatCard
          label={t("admin.tenantTabs.outstanding", "Outstanding")}
          value={centsToEUR(outstandingWithFees)}
          color={outstandingWithFees > 0 ? "#B45309" : "#047857"}
          sub={
            feePoolCents > 0 ? (
              <RentFeeSplit
                rentCents={outstandingRent}
                otherCents={outstanding - outstandingRent}
                feeCents={feePoolCents}
              />
            ) : null
          }
        />
        <NextDueCard
          row={nextDueRow}
          prevAmountCents={prevRentRow?.amount_cents ?? null}
          settlement={nextDueRow ? settlementOf(nextDueRow) : null}
          feeCents={
            feeCarrierId != null && String(nextDueRow?.id) === feeCarrierId
              ? feePoolCents
              : 0
          }
        />
      </div>

      {/* Rent due day — admin override, applies from next month on (COL-410) */}
      <RentDueDayCard
        dueDayOverride={booking.rent_due_day_override ?? null}
        busy={busy}
        canManage={canManage}
        open={dueDayOpen}
        onToggle={() => setDueDayOpen((v) => !v)}
        onSetRentDueDay={onSetRentDueDay}
      />

      {/* Rent schedule — everything still owed (feedback #12) */}
      <CollapsibleCard
        iconBg="#E7F7EF"
        iconColor="#10B981"
        icon={<EuroIcon />}
        title={t("admin.tenantTabs.rentScheduleTitle", "Rent schedule")}
        subtitle={t(
          "admin.tenantTabs.rentScheduleSubtitle",
          "Open rent — this month and the next 2 · amounts, due dates, proofs and notes are editable"
        )}
        open={scheduleOpen}
        onToggle={() => setScheduleOpen((v) => !v)}
      >
        <div style={{ padding: "6px 12px 12px" }}>
          <TableHead
            cols={["Month", "Amount", "Status", "Payment method", "Actions"]}
          />
          {upcomingDisplay.length === 0 ? (
            <div
              style={{
                padding: "16px 12px",
                fontSize: 13.5,
                color: "#94A3B8",
                fontWeight: 500,
              }}
            >
              {/* "Everything due is paid" is only true when NOTHING is open —
                  the footnote below can otherwise report open months right
                  under this line (same rule as the owner invoice card).

                  The tenancy-end line reads off `leaseEndIso`, so it can only
                  appear for a CONFIRMED move-out — the same bound the
                  projection uses. Blaming `end_date` here told admins the
                  schedule had stopped when the materializer was still
                  invoicing those months. */}
              {laterOpenRent.length > 0
                ? "Nothing open in this window."
                : leaseEndIso
                  ? `No open rent — tenancy ends ${formatDate(leaseEndIso) ?? ""}.`
                  : "No open rent — everything due is paid."}
            </div>
          ) : (
            <>
              {/* Nothing owed is ever dropped — the oldest open months are only
                  folded, and this button says how many. */}
              {olderOpenCount > 0 && !showAllSchedule && (
                <button
                  onClick={() => setShowAllSchedule(true)}
                  style={{
                    width: "100%",
                    padding: "10px",
                    background: "none",
                    border: "none",
                    borderBottom: "1px solid #F1F5F9",
                    cursor: "pointer",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#0F172A",
                  }}
                >
                  Show {olderOpenCount} older open month
                  {olderOpenCount > 1 ? "s" : ""}
                </button>
              )}
              {scheduleShown.map((p: any) =>
                p.__projected ? (
                  <ProjectedRentRow
                    key={p.id}
                    payment={p}
                    busy={busy}
                    onCreate={onCreateScheduledRent}
                    onUploadProof={onUploadProof}
                    onOpenNotes={setAutoNotesFor}
                    onSendReminder={onSendReminder}
                  />
                ) : (
                  <RentPaymentRow
                    key={p.id}
                    payment={p}
                    isFirstMonth={p.id === firstRentId}
                    standardRentCents={standardRentCents}
                    leaseRentCents={
                      p.due_date
                        ? leaseRentCentsForDue(String(p.due_date).slice(0, 10))
                        : 0
                    }
                    firstMonthOverride={
                      booking.first_month_rent_override ?? null
                    }
                    autoOpenNotes={String(p.id) === autoNotesFor}
                    onSetFirstMonthOverride={onSetFirstMonthOverride}
                    carriesFees={
                      feeCarrierId != null && String(p.id) === feeCarrierId
                    }
                    {...rowProps}
                  />
                )
              )}
            </>
          )}
          {/* Open rent dated past the two-month window — listed nowhere above,
              so it is reported here rather than silently disappearing (the
              owner invoice card carries the same note). */}
          {laterOpenRent.length > 0 && (
            <div
              style={{
                padding: "10px 12px 0",
                fontSize: 12.5,
                color: "#94A3B8",
                fontWeight: 500,
              }}
            >
              {laterOpenRent.length} further open month
              {laterOpenRent.length > 1 ? "s" : ""} due after{" "}
              {monthLabel(
                `${rentScheduleWindowEnd(scheduleAnchor)}-01`,
                language
              )}{" "}
              — not shown here.
            </div>
          )}
        </div>
      </CollapsibleCard>

      {/* Rent history — PAID rent only (feedback #12, revised). A month lands
          here the moment it is marked paid, not when its due date passes. Same
          layout + actions as the schedule above. */}
      <CollapsibleCard
        iconBg="#E7F7EF"
        iconColor="#10B981"
        icon={<EuroIcon />}
        title={t("admin.tenantTabs.rentHistoryTitle", "Rent history")}
        subtitle={rentScheduleSummary(
          paidRent,
          booking.monthly_rent,
          moveIn,
          proofByPayment,
          language
        )}
        open={rentOpen}
        onToggle={() => setRentOpen((v) => !v)}
      >
        <div style={{ padding: "6px 12px 12px" }}>
          <TableHead
            cols={["Month", "Amount", "Status", "Payment method", "Actions"]}
          />
          {paidRent.length === 0 ? (
            <div
              style={{
                padding: "16px 12px",
                fontSize: 13.5,
                color: "#94A3B8",
                fontWeight: 500,
              }}
            >
              No rent paid yet.
            </div>
          ) : (
            (() => {
              const shown = paidRent.slice(0, rentLimit);
              // Year subheaders only once a tenancy spans more than one year.
              const years = new Set(
                shown
                  .map((p: any) => String(p.due_date || "").slice(0, 4))
                  .filter(Boolean)
              );
              const multiYear = years.size > 1;
              // Per-year paid-count + total, shown next to each subheader.
              const yearStats: Record<
                string,
                { paid: number; total: number; cents: number }
              > = {};
              for (const p of shown) {
                const y = String(p.due_date || "").slice(0, 4);
                if (!y) continue;
                const s = (yearStats[y] ??= { paid: 0, total: 0, cents: 0 });
                s.total += 1;
                if (effectivelyPaid(p, proofByPayment)) {
                  s.paid += 1;
                  s.cents += p.amount_cents || 0;
                }
              }
              const out: React.ReactNode[] = [];
              let lastYear = "";
              for (const p of shown) {
                const yr = String(p.due_date || "").slice(0, 4);
                if (multiYear && yr && yr !== lastYear) {
                  lastYear = yr;
                  const st = yearStats[yr];
                  out.push(
                    <div
                      key={`yr-${yr}`}
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        justifyContent: "space-between",
                        gap: 8,
                        padding: "12px 12px 4px",
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: ".08em",
                        textTransform: "uppercase",
                        color: "#94A3B8",
                      }}
                    >
                      <span>{yr}</span>
                      {st && (
                        <span
                          style={{ letterSpacing: 0, textTransform: "none" }}
                        >
                          {st.paid}/{st.total} paid · {centsToEUR(st.cents)}
                        </span>
                      )}
                    </div>
                  );
                }
                out.push(
                  <RentPaymentRow
                    key={p.id}
                    payment={p}
                    isFirstMonth={p.id === firstRentId}
                    standardRentCents={standardRentCents}
                    leaseRentCents={
                      p.due_date
                        ? leaseRentCentsForDue(String(p.due_date).slice(0, 10))
                        : 0
                    }
                    firstMonthOverride={
                      booking.first_month_rent_override ?? null
                    }
                    onSetFirstMonthOverride={onSetFirstMonthOverride}
                    {...rowProps}
                  />
                );
              }
              return out;
            })()
          )}
          {paidRent.length > rentLimit && (
            <button
              onClick={() => setRentLimit(paidRent.length)}
              style={{
                width: "100%",
                padding: "12px",
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: 13.5,
                fontWeight: 700,
                color: "#0F172A",
              }}
            >
              See more ({paidRent.length - rentLimit} more)
            </button>
          )}
        </div>
      </CollapsibleCard>

      {/* Security deposit — single section (feedback #13): the summary-card
          header (amount + what actually backs it) with the collapsible
          proof/notes table folded in.

          The header states FACTS rather than a binary: how much of the
          contractual deposit has arrived, when, and what corroborates it. A
          part-paid deposit used to render as a flat "Pending" with the received
          money invisible — an admin chasing €150 could not see that €75 was
          already in. `depositPaid` (the prop) still decides paid-vs-not so the
          journey stage cannot disagree with this card; the resolver below only
          adds the detail. */}
      <CollapsibleCard
        iconBg="#F1ECFE"
        iconColor="#8B5CF6"
        icon={<WalletIcon />}
        title={t(
          "admin.tenantTabs.timeline.securityDeposit",
          "Security deposit"
        )}
        subtitle={depositSummary.line}
        right={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              flexShrink: 0,
            }}
          >
            <div style={{ textAlign: "right" }}>
              <div
                style={{
                  fontSize: 19,
                  fontWeight: 800,
                  letterSpacing: "-.02em",
                  lineHeight: 1.1,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {/* No agreed deposit is not a €0.00 deposit — an amount here
                    reads as a real figure that happens to be zero. */}
                {depositSummary.state === "none"
                  ? "—"
                  : money(depositExpectedEUR)}
              </div>
              {depositSummary.state === "partial" && (
                <div
                  style={{
                    fontSize: 11.5,
                    fontWeight: 600,
                    color: "#B45309",
                    marginTop: 2,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {money(depositOutstandingEUR)} still due
                </div>
              )}
            </div>
            <span style={depositSummary.chip}>{depositSummary.label}</span>
          </div>
        }
        action={
          // Nothing to settle: already paid, or never owed in the first place.
          !canManage ||
          depositPaid ||
          depositSummary.state === "none" ? undefined : (
            <button
              onClick={onMarkDepositPaid}
              disabled={busy}
              title="Records the transfer with your name against it — see the note inside the card."
              style={depositActionBtn(busy)}
            >
              <CheckIcon /> Mark deposit paid
            </button>
          )
        }
        open={depositOpen}
        onToggle={() => setDepositOpen((v) => !v)}
      >
        <div style={{ padding: "6px 12px 12px" }}>
          <TableHead
            cols={[
              "Type",
              "Amount",
              "Status",
              "Payment method",
              "Payment proof",
            ]}
          />
          {depositRows.length === 0 ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1.4fr 1fr 1fr 1.1fr 1.6fr",
                gap: 12,
                padding: "13px 12px",
                alignItems: "center",
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 700 }}>
                Security deposit
              </div>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {depositSummary.state === "none"
                  ? "—"
                  : money(depositExpectedEUR)}
              </div>
              <div>
                {/* Was hardcoded "Pending" — it now follows the card's header, so
                    the two lines cannot disagree about the same deposit. */}
                <span style={depositSummary.chip}>{depositSummary.label}</span>
              </div>
              <div>
                <span style={{ color: "#CBD5E1" }}>—</span>
              </div>
              {/* No deposit row yet — same shape as a real row's proof cell so
                  the card does not change layout the moment one appears. */}
              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <button
                  onClick={() => !busy && onUploadDepositProof()}
                  disabled={busy}
                  style={{
                    ...actionBtn("#0E7490"),
                    border: "1px dashed #C4D0DC",
                  }}
                >
                  <IcUpload />
                  {t("admin.tenantTabs.uploadProof", "Upload proof")}
                </button>
                <RowActionsMenu
                  label={t("admin.tenantTabs.rowActions", "More actions")}
                  actions={[
                    {
                      key: "notes",
                      label: t("admin.tenantTabs.notes", "Notes"),
                      icon: <IcNotes />,
                      onSelect: () => setDepositNotesOpen((v) => !v),
                    },
                  ]}
                />
              </div>
            </div>
          ) : (
            depositRows.map((p: any) => (
              <DepositPaymentRow key={p.id} payment={p} {...rowProps} />
            ))
          )}
          {depositRows.length === 0 && depositNotesOpen && (
            <div style={{ margin: "0 12px 12px" }}>
              <AdminNotesSection
                bookingId={bookingId}
                userId={userId}
                title={t("admin.tenantTabs.depositNotesTitle", "Deposit notes")}
                compact
              />
            </div>
          )}
          {/* The primary action lives in the card header (reachable without
              expanding). This explains what pressing it actually does — it used
              to be a second, differently-labelled copy of the same button. Not
              shown when there is no action to explain. */}
          {!depositPaid && depositSummary.state !== "none" && (
            <div
              style={{
                margin: "4px 12px 12px",
                padding: "11px 13px",
                borderRadius: 12,
                background: "#F8FAFC",
                border: "1px solid #EDEFF2",
                display: "flex",
                alignItems: "flex-start",
                gap: 9,
              }}
            >
              <span style={{ color: "#0F766E", flexShrink: 0, marginTop: 1 }}>
                <CheckIcon />
              </span>
              <div
                style={{
                  fontSize: 12.5,
                  color: "#64748B",
                  fontWeight: 500,
                  lineHeight: 1.55,
                }}
              >
                <strong style={{ color: "#334155", fontWeight: 700 }}>
                  Mark deposit paid
                </strong>{" "}
                records{" "}
                {depositSummary.state === "partial"
                  ? `the outstanding ${money(depositOutstandingEUR)} as`
                  : "this deposit as"}{" "}
                settled with your name and the time against it — which is what
                makes an off-platform transfer count as evidence rather than a
                claim.
                {stageId === "deposit_pending"
                  ? " It also advances the tenant to Key pickup pending."
                  : ""}{" "}
                Attach the bank-transfer proof here whenever you have one.
              </div>
            </div>
          )}
        </div>
      </CollapsibleCard>

      {/* Reminders / dunning history */}
      <RemindersSection activity={paymentsActivity} booking={booking} />

      {/* Payment method — driven by the tenant's REAL mandate state
          (payment_methods + sepa_mandates), never by deposit status. Hidden
          when SEPA isn't offered for the room and no mandate ever existed:
          there is nothing to report, and an empty card reads as "missing". */}
      {sepa &&
        sepa.ready &&
        (sepa.hasActiveMandate ||
          sepa.mandateStatus !== null ||
          sepa.roomSepaEnabled) && (
          <div
            style={{
              background: "#fff",
              border: "1px solid #ECEEF1",
              borderRadius: 20,
              padding: "22px 24px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 13 }}>
                <SectionIcon bg="#EAF1FE" color="#3B82F6" size={40}>
                  <CardIcon />
                </SectionIcon>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700 }}>
                    SEPA Direct Debit
                  </div>
                  <div
                    style={{
                      fontSize: 12.5,
                      color: "#94A3B8",
                      fontWeight: 500,
                      marginTop: 2,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {sepa.hasActiveMandate
                      ? sepa.ibanLast4
                        ? `IBAN •••• ${sepa.ibanLast4} · ${t("admin.tenantTabs.sepa.debitedAutomatically", "rent is debited automatically")}`
                        : t(
                            "admin.tenantTabs.sepa.onFileDebitedAutomatically",
                            "Mandate on file · rent is debited automatically"
                          )
                      : sepa.mandateStatus === "revoked" ||
                          sepa.mandateStatus === "expired"
                        ? `${t("admin.tenantTabs.sepa.previousMandatePrefix", "Previous mandate")} ${sepa.mandateStatus} · ${t("admin.tenantTabs.sepa.mustSignNew", "tenant must sign a new one")}`
                        : sepa.mandateStatus === "active"
                          ? t(
                              "admin.tenantTabs.sepa.signedNotChargeable",
                              "Mandate signed but not chargeable · tenant must redo SEPA setup"
                            )
                          : t(
                              "admin.tenantTabs.sepa.notSignedYet",
                              "Tenant hasn't signed a mandate yet"
                            )}
                  </div>
                </div>
              </div>
              <span style={sepa.hasActiveMandate ? CHIP.green : CHIP.amber}>
                {sepa.hasActiveMandate
                  ? t("admin.tenantTabs.sepa.mandateActive", "Mandate active")
                  : sepa.mandateStatus === "revoked"
                    ? t(
                        "admin.tenantTabs.sepa.mandateRevoked",
                        "Mandate revoked"
                      )
                    : sepa.mandateStatus === "expired"
                      ? t(
                          "admin.tenantTabs.sepa.mandateExpired",
                          "Mandate expired"
                        )
                      : sepa.mandateStatus === "active"
                        ? t(
                            "admin.tenantTabs.sepa.notChargeable",
                            "Not chargeable"
                          )
                        : t("admin.tenantTabs.sepa.noMandate", "No mandate")}
              </span>
            </div>
            {/* Signed mandate PDF — collapsible embed of the exact document the
                tenant accepted (feedback 3a). Only offered when a PDF was
                persisted at creation; older mandates have none. */}
            {sepa.mandatePdfUrl && (
              <div style={{ marginTop: 14 }}>
                <button
                  onClick={async () => {
                    if (mandatePdfOpen) return setMandatePdfSrc(null);
                    const src = await resolveTenantDocumentUrl(
                      sepa.mandatePdfUrl
                    );
                    if (src) setMandatePdfSrc(src);
                    else
                      toast.error(
                        t(
                          "admin.docs.openFailed",
                          "Could not open the document."
                        )
                      );
                  }}
                  aria-expanded={mandatePdfOpen}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "6px 12px",
                    borderRadius: 8,
                    border: "1px solid #E2E8F0",
                    background: "#F8FAFC",
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: "#0F172A",
                    cursor: "pointer",
                  }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      transition: "transform 0.15s",
                      transform: mandatePdfOpen ? "rotate(90deg)" : "none",
                    }}
                  >
                    ▸
                  </span>
                  {t("admin.tenantTabs.sepa.signedMandate", "Signed mandate")}
                  {sepa.mandateReference ? ` · ${sepa.mandateReference}` : ""}
                </button>
                {mandatePdfOpen && (
                  <div
                    style={{
                      marginTop: 10,
                      border: "1px solid #ECEEF1",
                      borderRadius: 12,
                      overflow: "hidden",
                    }}
                  >
                    <iframe
                      src={mandatePdfSrc}
                      title={`${t("admin.tenantTabs.sepa.mandateTitlePrefix", "SEPA mandate")} ${sepa.mandateReference ?? ""}`}
                      style={{ width: "100%", height: 480, border: "none" }}
                    />
                  </div>
                )}
              </div>
            )}
            {/* Tenant asked to end the mandate (feedback 6) — the admin
                decision lives HERE, on the SEPA card, exactly where Neel
                asked for it. Approve revokes the mandate (Stripe detach) and
                applies the requested method; reject leaves everything as-is. */}
            {sepa.pendingCancellation && onDecideDdCancellation && (
              <div
                style={{
                  marginTop: 14,
                  border: "1px solid #F7E4BD",
                  background: "#FFFBEB",
                  borderRadius: 12,
                  padding: "12px 14px",
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{ fontSize: 13, fontWeight: 700, color: "#92400E" }}
                  >
                    {t(
                      "admin.tenantTabs.sepa.requestedToEndTitle",
                      "Tenant requested to end direct debit"
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: "#B45309", marginTop: 2 }}>
                    {t(
                      "admin.tenantTabs.sepa.wantsToSwitchTo",
                      "Wants to switch to"
                    )}{" "}
                    {sepa.pendingCancellation.requested_method === "card"
                      ? t("admin.tenantTabs.sepa.cardPayment", "card payment")
                      : t(
                          "admin.tenantTabs.sepa.bankTransfer",
                          "bank transfer"
                        )}{" "}
                    · {t("admin.tenantTabs.sepa.requestedOn", "requested")}{" "}
                    {formatDateNumeric(
                      sepa.pendingCancellation.created_at,
                      language
                    )}
                    .{" "}
                    {t(
                      "admin.tenantTabs.sepa.approvingRevokesMandate",
                      "Approving revokes the mandate — no further debits."
                    )}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    disabled={busy}
                    onClick={() =>
                      !busy &&
                      onDecideDdCancellation(
                        sepa.pendingCancellation!.id,
                        "reject"
                      )
                    }
                    style={actionBtn("#64748B")}
                  >
                    {t("admin.tenantTabs.sepa.reject", "Reject")}
                  </button>
                  <button
                    disabled={busy}
                    onClick={() =>
                      !busy &&
                      onDecideDdCancellation(
                        sepa.pendingCancellation!.id,
                        "approve"
                      )
                    }
                    style={{
                      ...actionBtn("#B45309"),
                      border: "1px solid #F7E4BD",
                      background: "#FFF7E0",
                    }}
                  >
                    {t(
                      "admin.tenantTabs.sepa.approveEndMandate",
                      "Approve & end mandate"
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
    </div>
  );
}

function TableHead({ cols }: { cols: string[] }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1.4fr 1fr 1fr 1.1fr 1.6fr",
        gap: 12,
        padding: "10px 12px",
        borderBottom: "1px solid #F0F1F3",
      }}
    >
      {cols.map((c) => (
        <div key={c} style={{ ...uppLabel, fontSize: 10.5 }}>
          {c}
        </div>
      ))}
    </div>
  );
}

function paymentMethod(p: any, proof: any): string {
  if (p.stripe_payment_intent_id || p.stripe_charge_id) return "Stripe";
  if (proof || p.status === "paid") return "Manual";
  return "—";
}

/**
 * Payment-method cell: the method chip, plus — for Stripe payments — the Stripe
 * payment intent id (falling back to the charge id) below it, truncated with
 * the full id on hover so it can be copied / cross-referenced in Stripe.
 */
function PaymentMethodCell({ p, proof }: { p: any; proof: any }) {
  const method = paymentMethod(p, proof);
  if (method === "—") return <span style={{ color: "#CBD5E1" }}>—</span>;
  const stripeId: string | null =
    p.stripe_payment_intent_id || p.stripe_charge_id || null;
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 3,
        minWidth: 0,
      }}
    >
      <span style={CHIP.slate2}>{method}</span>
      {stripeId && (
        <span
          title={stripeId}
          style={{
            fontSize: 11,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            color: "#94A3B8",
            maxWidth: 170,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {stripeId}
        </span>
      )}
    </div>
  );
}
/**
 * Is this row's money actually confirmed received? The canonical predicate —
 * the same one the deposit gate, the tenant list and the journey resolver use —
 * so the Payments tab can't claim "Paid" on a row every rollup counts unpaid.
 *
 * The map is read optionally on purpose: this project compiles with
 * `strictNullChecks: false`, so a caller that forgets to thread the prop down
 * fails at runtime rather than at build time. Treating a missing map as "no
 * proofs" degrades to the stricter answer instead of throwing.
 */
const effectivelyPaid = (
  p: any,
  proofByPayment: Record<string, any> | null | undefined
): boolean => isPaymentEffectivelyPaid(p, (id) => proofByPayment?.[id] ?? null);

/**
 * Row status badge — colours only. The semantics come from
 * `resolvePaymentDisplayStatus`, so this can't drift from the predicate the
 * deposit gate and the rollups use.
 */
function statusMeta(
  p: any,
  proofByPayment: Record<string, any> | null | undefined,
  t: (key: string, fallback: string) => string
): { chip: React.CSSProperties; label: string; title?: string } {
  switch (
    resolvePaymentDisplayStatus(p, (id) => proofByPayment?.[id] ?? null)
  ) {
    case "paid":
      return {
        chip: CHIP.green,
        label: t("admin.tenantTabs.status.paid", "Paid"),
      };
    case "waived":
      return {
        chip: { ...CHIP.cyan, whiteSpace: "nowrap" },
        label: t("admin.tenantTabs.status.waived", "Waived"),
        title: t(
          "admin.tenantTabs.status.waivedTooltip",
          "Given as a referral reward — the tenant owes nothing for this month and it must not be collected. The owner is still reported the full rent (payments.gross_amount_cents)."
        ),
      };
    case "partially_paid":
      return {
        chip: { ...CHIP.blue, whiteSpace: "nowrap" },
        label: t("admin.tenantTabs.status.partiallyPaid", "Partially paid"),
        title: t(
          "admin.tenantTabs.status.partiallyPaidTooltip",
          "Part of this month has been received; the rest is still owed. It stays in the schedule, keeps its reminders, and keeps accruing Mahngeb\u00fchren on the open balance. The owner sees nothing until it is fully paid."
        ),
      };
    case "unverified":
      return {
        // One word, `nowrap`: the Status column is narrow and a chip that wraps
        // breaks the pill. The tooltip carries the explanation.
        chip: {
          ...chip("#FEF6E7", "#B45309", "#F7E4BD"),
          whiteSpace: "nowrap",
        },
        label: t("admin.tenantTabs.status.unverified", "Unverified"),
        title: t(
          "admin.tenantTabs.status.unverifiedTooltip",
          "Marked paid, but nothing backs it up — no transfer proof and no record of who marked it. Upload a proof, or use “Mark paid” to record the confirmation, and it counts everywhere."
        ),
      };
    case "sepa_in_progress":
      return {
        chip: { ...CHIP.blue, whiteSpace: "nowrap" },
        label: t("admin.tenantTabs.status.sepaInProgress", "SEPA in progress"),
        title: t(
          "admin.tenantTabs.status.sepaInProgressTooltip",
          "The direct debit has been submitted to the bank and settles in 2–5 business days. Nothing to collect — the webhook flips this row to paid (or failed) when the money moves."
        ),
      };
    case "overdue":
      return {
        chip: chip("#FEECEC", "#B91C1C", "#F7CFCF"),
        label: String(p.status).replace(/_/g, " "),
      };
    case "failed":
      return {
        chip: {
          ...chip("#FEECEC", "#B91C1C", "#F7CFCF"),
          whiteSpace: "nowrap",
        },
        label: t("admin.tenantTabs.status.failed", "Failed"),
        title: t(
          "admin.tenantTabs.status.failedTooltip",
          "The collection bounced (e.g. a returned SEPA debit). The money is still owed — re-collect it or record how it was settled."
        ),
      };
    default:
      return {
        chip: CHIP.amber,
        label: t("admin.tenantTabs.status.pending", "Pending"),
      };
  }
}
/** Parse a date value as LOCAL time. A bare `YYYY-MM-DD` is otherwise parsed as
 *  UTC midnight, which renders as the previous month for admins west of UTC. */
function parseLocalDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso).trim());
  const d = m
    ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    : new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}
function monthLabel(iso: string | null | undefined, language?: string): string {
  const d = parseLocalDate(iso);
  if (!d) return "—";
  return d.toLocaleDateString(language === "de" ? "de-DE" : "en-GB", {
    month: "long",
    year: "numeric",
  });
}
function monthShort(iso: string | null | undefined, language?: string): string {
  const d = parseLocalDate(iso);
  if (!d) return "—";
  return d.toLocaleDateString(language === "de" ? "de-DE" : "en-GB", {
    month: "short",
    year: "numeric",
  });
}
function rentScheduleSummary(
  rows: any[],
  monthlyRent: number | null | undefined,
  moveIn: string | null | undefined,
  proofByPayment: Record<string, any>,
  language?: string
): string {
  const perMonth = money(monthlyRent);
  if (rows.length === 0)
    return `${perMonth} / month · move-in ${formatDate(moveIn) ?? "—"}`;
  // Corroborated only — the same count the row badges show.
  const paid = rows.filter((r) => effectivelyPaid(r, proofByPayment)).length;
  // Earliest/latest by date, independent of the rows' display order (history
  // now lists newest-first).
  const dueDates = rows.map((r) => String(r.due_date ?? "")).sort();
  const range =
    rows.length > 1
      ? `${monthShort(dueDates[0], language)} – ${monthShort(dueDates[dueDates.length - 1], language)}`
      : monthShort(rows[0].due_date, language);
  return `${perMonth} / month · ${range} · ${paid} paid`;
}

/**
 * The deposit card's primary action. Uses the same teal as every other primary
 * in the profile (#0F766E) rather than the one-off #12B5A8 this card used to
 * carry, so "the button that commits something" looks the same everywhere.
 */
function depositActionBtn(busy: boolean): React.CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    flexShrink: 0,
    background: busy ? "#94A3B8" : "#0F766E",
    color: "#fff",
    border: "none",
    borderRadius: 11,
    padding: "9px 14px",
    fontSize: 12.5,
    fontWeight: 700,
    letterSpacing: "-.01em",
    cursor: busy ? "default" : "pointer",
    boxShadow: busy ? "none" : "0 1px 2px rgba(15,118,110,.28)",
  };
}

/**
 * Count pill on a row's "Notes" button — so an admin can see that a payment
 * carries notes without opening the panel first.
 */
function NoteCountPill({ n }: { n: number }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 17,
        height: 17,
        padding: "0 5px",
        borderRadius: 999,
        background: "#0F766E",
        color: "#fff",
        fontSize: 10.5,
        fontWeight: 800,
        lineHeight: 1,
      }}
    >
      {n}
    </span>
  );
}

/**
 * Admin-facing "Rent due day" control (COL-410). Rent defaults to the 1st;
 * an admin can override it to any day 1-31. The change only ever touches
 * future, still-open rent rows — never the current month — so it takes
 * effect the month after it's made, and dunning realigns automatically
 * (bookings.rent_due_date is re-derived from the ledger once the due dates
 * change, and every reminder surface reads that column).
 */
function RentDueDayCard({
  dueDayOverride,
  busy,
  canManage,
  open,
  onToggle,
  onSetRentDueDay,
}: {
  dueDayOverride: number | null;
  busy: boolean;
  canManage: boolean;
  open: boolean;
  onToggle: () => void;
  onSetRentDueDay: (day: number | null) => Promise<void>;
}) {
  const { t } = useLanguage();
  const effectiveDay = dueDayOverride ?? 1;
  // set_rent_due_day() stores an explicit 1 (not NULL) for "reset to
  // default" — see the migration header — so the 1st reads as "Default"
  // here regardless of whether that's because the feature was never used or
  // because it was reset.
  const isDefault = dueDayOverride == null || dueDayOverride === 1;
  const [day, setDay] = useState(String(effectiveDay));
  const [working, setWorking] = useState(false);
  // Re-sync if the booking's override changes underneath us (a background
  // refetch, or another admin's concurrent edit) — otherwise the input keeps
  // showing whatever was true at mount forever.
  useEffect(() => {
    setDay(String(effectiveDay));
  }, [effectiveDay]);

  const save = async () => {
    const parsed = Number(day);
    if (!Number.isInteger(parsed) || parsed < 1 || parsed > 31) {
      toast.error(
        t(
          "admin.tenantTabs.dueDayValidation",
          "Enter a due day between 1 and 31."
        )
      );
      return;
    }
    setWorking(true);
    try {
      await onSetRentDueDay(parsed);
    } finally {
      setWorking(false);
    }
  };

  const resetToDefault = async () => {
    setWorking(true);
    try {
      await onSetRentDueDay(null);
    } finally {
      setWorking(false);
    }
  };

  return (
    <CollapsibleCard
      iconBg="#EFF6FF"
      iconColor="#2563EB"
      icon={<CalendarIcon />}
      title={t("admin.tenantTabs.rentDueDayTitle", "Rent due day")}
      subtitle={
        isDefault
          ? t(
              "admin.tenantTabs.rentDueDayDefaultSubtitle",
              "Set to the 1st of each month"
            )
          : `${t("admin.tenantTabs.rentDueDayOverriddenPrefix", "Overridden to the")} ${ordinalLabel(effectiveDay)} ${t("admin.tenantTabs.rentDueDayOverriddenSuffix", "of each month")}`
      }
      right={
        isDefault ? (
          <span style={CHIP.slate}>
            {t("admin.tenantTabs.timeline.default", "Default")}
          </span>
        ) : (
          <span style={chip("#F1ECFE", "#6D28D9", "#E1D5FB")}>
            {t("admin.tenantTabs.override", "Override")}
          </span>
        )
      }
      open={open}
      onToggle={onToggle}
    >
      <div style={{ padding: "6px 12px 14px" }}>
        <div
          style={{
            ...innerBox,
            padding: "14px 16px",
            display: "flex",
            alignItems: "flex-end",
            gap: 12,
            flexWrap: "wrap",
            justifyContent: "space-between",
          }}
        >
          <div style={{ fontSize: 13, color: "#64748B", maxWidth: 480 }}>
            By default, rent is due on the <strong>1st</strong> of every month.
            Admins can override this to any day of the month (e.g. 5th) — the
            change applies to future rent periods (starting next month) and
            never the current month.
          </div>
          {canManage && (
            <div
              style={{
                display: "flex",
                alignItems: "flex-end",
                gap: 10,
              }}
            >
              <label style={{ display: "block" }}>
                <div style={{ ...uppLabel, marginBottom: 5 }}>
                  Due day of month
                </div>
                <input
                  type="number"
                  min={1}
                  max={31}
                  step={1}
                  value={day}
                  onChange={(e) => setDay(e.target.value)}
                  style={{
                    border: "1px solid #E3E6EA",
                    borderRadius: 10,
                    padding: "9px 12px",
                    fontSize: 14,
                    fontWeight: 600,
                    width: 90,
                  }}
                />
              </label>
              <button
                onClick={() => void save()}
                disabled={busy || working}
                style={{
                  background: "#12B5A8",
                  color: "#fff",
                  border: "none",
                  borderRadius: 10,
                  padding: "10px 16px",
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: busy || working ? "default" : "pointer",
                }}
              >
                Save
              </button>
              {!isDefault && (
                <button
                  onClick={() => void resetToDefault()}
                  disabled={busy || working}
                  style={actionBtn("#B45309")}
                >
                  Reset to default
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </CollapsibleCard>
  );
}

/**
 * COL-621: the "Kickback" checkbox on a rent edit. Ticked, the tenant is
 * charged the lower amount while the owner is still paid the full rent and the
 * fee is still charged on it — coliviq funds the difference
 * (src/lib/rent-kickback.ts). Only meaningful below the full rent, so it is
 * disabled at or above it, and a hint under the form says what coliviq covers.
 */
function KickbackCheckbox({
  checked,
  onChange,
  amount,
  fullRentCents,
  lockedByReferral = false,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** The amount field's raw value. */
  amount: string;
  fullRentCents: number;
  /** A referral credit sits on the row: it IS a kickback, and unticking here
   *  would silently take the credit off the owner's payout, so it stays on. */
  lockedByReferral?: boolean;
}) {
  const { t } = useLanguage();
  const parsed = parseFloat(amount);
  const amountCents = Number.isFinite(parsed) ? Math.round(parsed * 100) : NaN;
  const coversCents =
    Number.isFinite(amountCents) && amountCents > 0
      ? fullRentCents - amountCents
      : 0;
  const applies = coversCents > 0;
  const enabled = applies && !lockedByReferral;
  return (
    <>
      <label
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          cursor: enabled ? "pointer" : "default",
          paddingBottom: 9,
          opacity: applies ? 1 : 0.5,
        }}
        title={
          lockedByReferral
            ? t(
                "admin.tenantTabs.kickbackReferralHint",
                "A referral credit is always a kickback: the owner keeps the full rent."
              )
            : applies
              ? undefined
              : t(
                  "admin.tenantTabs.kickbackDisabledHint",
                  "Lower the amount below the full rent to mark the difference as a kickback."
                )
        }
      >
        <input
          type="checkbox"
          checked={checked && applies}
          disabled={!enabled}
          onChange={(e) => onChange(e.target.checked)}
          style={{ width: 16, height: 16, accentColor: "#7C3AED" }}
        />
        <span style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>
          {t("admin.tenantTabs.kickbackLabel", "Kickback")}
        </span>
      </label>
      {checked && applies && (
        <div
          style={{
            order: 99,
            flexBasis: "100%",
            fontSize: 11.5,
            color: "#6D28D9",
            fontWeight: 600,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {t(
            "admin.tenantTabs.kickbackHint",
            "coliviq covers {covers}. The owner is still paid the full {full}."
          )
            .replace("{covers}", centsToEUR(coversCents))
            .replace("{full}", centsToEUR(fullRentCents))}
        </div>
      )}
    </>
  );
}

/**
 * Rent row for a PROJECTED upcoming month — the month is derived from the lease
 * (rent, due day, lease end) because no `payments` row exists for it yet.
 *
 * The row is still fully actionable: editing the amount/due date, attaching a
 * transfer proof or adding a note all MATERIALISE the projection first, i.e.
 * insert the real scheduled `payments` row via `onCreate`, then act on it. Once
 * created the projection disappears and a normal `RentPaymentRow` takes over.
 */
function ProjectedRentRow({
  payment: p,
  busy,
  onCreate,
  onUploadProof,
  onOpenNotes,
  onSendReminder,
  showReminder = true,
}: {
  payment: any;
  busy: boolean;
  onCreate: (input: {
    dueDate: string;
    amountCents: number;
    markPaid?: boolean;
    grossAmountCents?: number | null;
  }) => Promise<string | null>;
  onUploadProof: (paymentId: string) => void;
  onOpenNotes: (paymentId: string) => void;
  onSendReminder: () => void;
  showReminder?: boolean;
}) {
  const { t, language } = useLanguage();
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState(
    ((p.amount_cents || 0) / 100).toString()
  );
  const [due, setDue] = useState(
    p.due_date ? String(p.due_date).slice(0, 10) : ""
  );
  const [markPaid, setMarkPaid] = useState(false);
  // COL-621: the projection IS the lease rent, so a kickback is measured from it.
  const [kickback, setKickback] = useState(false);
  const [working, setWorking] = useState(false);
  const disabled = busy || working;
  // A projected month with no real `payments` row yet is still genuinely due
  // once its due date passes — it must read the same as an unpaid real row
  // (RentPaymentRow's `isOverdue`), not sit as a neutral "Scheduled" forever
  // just because nothing has materialised it. Otherwise admins scanning this
  // card see no signal that the month is actually late.
  const today = businessToday();
  const isOverdue = !!p.due_date && p.due_date <= today;

  /** Create the row from the projection's own (unedited) values. */
  const materialise = async (): Promise<string | null> => {
    setWorking(true);
    try {
      return await onCreate({
        dueDate: String(p.due_date).slice(0, 10),
        amountCents: p.amount_cents || 0,
      });
    } finally {
      setWorking(false);
    }
  };

  const save = async () => {
    const parsed = parseFloat(amount);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      toast.error(
        t(
          "admin.tenantTabs.rentAmountValidation",
          "Enter a rent amount greater than €0."
        )
      );
      return;
    }
    if (!due) {
      toast.error(
        t(
          "admin.tenantTabs.dueDateValidationMonth",
          "Pick a due date for this month."
        )
      );
      return;
    }
    setWorking(true);
    try {
      const cents = Math.round(parsed * 100);
      const id = await onCreate({
        dueDate: due,
        amountCents: cents,
        markPaid,
        grossAmountCents: kickbackGrossCents(
          kickback,
          cents,
          p.amount_cents || 0
        ),
      });
      if (id) setEditing(false);
    } finally {
      setWorking(false);
    }
  };

  return (
    <div style={{ borderBottom: "1px solid #F5F6F7" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1.4fr 1fr 1fr 1.1fr 1.6fr",
          gap: 12,
          padding: "13px 12px",
          alignItems: "center",
        }}
      >
        <div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>
            {monthLabel(p.due_date, language)}
          </div>
          <div
            style={{
              fontSize: 11,
              color: "#94A3B8",
              fontWeight: 500,
              marginTop: 2,
            }}
            title="Projected from the lease — saved as a real payment as soon as you edit it, attach a proof or add a note."
          >
            Projected from lease
          </div>
          {p.due_date && (
            <div style={{ fontSize: 11.5, color: "#94A3B8", marginTop: 2 }}>
              Due {formatDate(p.due_date)}
            </div>
          )}
        </div>
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {centsToEUR(p.amount_cents)}
        </div>
        <div>
          {isOverdue ? (
            <span style={CHIP.amber}>
              {t("admin.tenantTabs.pendingChip", "Pending")}
            </span>
          ) : (
            <span style={CHIP.slate}>
              {t("admin.tenantTabs.pendingChip", "Pending")}
            </span>
          )}
        </div>
        <div>
          <span style={{ color: "#CBD5E1" }}>—</span>
        </div>
        <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
          <button
            onClick={() => setEditing((v) => !v)}
            disabled={disabled}
            style={actionBtn("#334155")}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
            </svg>
            Edit
          </button>
          <button
            onClick={() =>
              void (async () => {
                const id = await materialise();
                if (id) onUploadProof(id);
              })()
            }
            disabled={disabled}
            style={actionBtn("#64748B")}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />
            </svg>
            Upload
          </button>
          <button
            onClick={() =>
              void (async () => {
                const id = await materialise();
                if (id) onOpenNotes(id);
              })()
            }
            disabled={disabled}
            style={actionBtn("#64748B")}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <path d="M14 2v6h6" />
            </svg>
            Notes
          </button>
          {isOverdue && showReminder && (
            <button
              onClick={() => !disabled && onSendReminder()}
              disabled={disabled}
              style={{
                ...actionBtn("#B45309"),
                border: "1px solid #F7E4BD",
                background: "#FFFBEB",
              }}
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
                <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
              </svg>
              Send reminder
            </button>
          )}
        </div>
      </div>
      {editing && (
        <div
          style={{
            ...innerBox,
            margin: "0 12px 12px",
            padding: "14px 16px",
            display: "flex",
            alignItems: "flex-end",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <label style={{ display: "block" }}>
            <div style={{ ...uppLabel, marginBottom: 5 }}>Amount (€)</div>
            <input
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              style={{
                border: "1px solid #E3E6EA",
                borderRadius: 10,
                padding: "9px 12px",
                fontSize: 14,
                fontWeight: 600,
                width: 140,
              }}
            />
          </label>
          <label style={{ display: "block" }}>
            <div style={{ ...uppLabel, marginBottom: 5 }}>
              {t("admin.tenantTabs.dueDateLabel", "Due date")}
            </div>
            <input
              type="date"
              value={due}
              onChange={(e) => setDue(e.target.value)}
              style={{
                border: "1px solid #E3E6EA",
                borderRadius: 10,
                padding: "9px 12px",
                fontSize: 14,
                fontWeight: 600,
              }}
            />
          </label>
          <label
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
              paddingBottom: 9,
            }}
          >
            <input
              type="checkbox"
              checked={markPaid}
              onChange={(e) => setMarkPaid(e.target.checked)}
              style={{ width: 16, height: 16, accentColor: "#12B5A8" }}
            />
            <span style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>
              Mark paid
            </span>
          </label>
          <KickbackCheckbox
            checked={kickback}
            onChange={setKickback}
            amount={amount}
            fullRentCents={p.amount_cents || 0}
          />
          <button
            onClick={() => void save()}
            disabled={disabled}
            style={{
              background: "#12B5A8",
              color: "#fff",
              border: "none",
              borderRadius: 10,
              padding: "10px 16px",
              fontWeight: 700,
              fontSize: 13,
              cursor: disabled ? "default" : "pointer",
              opacity: disabled ? 0.7 : 1,
            }}
          >
            Save
          </button>
          <button
            onClick={() => setEditing(false)}
            disabled={disabled}
            style={actionBtn("#64748B")}
          >
            Cancel
          </button>
          <div
            style={{
              flexBasis: "100%",
              fontSize: 11.5,
              color: "#94A3B8",
              fontWeight: 500,
            }}
          >
            Saving creates the payment for this month so it can carry a proof, a
            note and its own status.
          </div>
        </div>
      )}
    </div>
  );
}

function RentPaymentRow({
  payment: p,
  isFirstMonth,
  standardRentCents,
  leaseRentCents = 0,
  firstMonthOverride,
  proofByPayment,
  noteCountByPayment,
  referralNotesByPayment,
  busy,
  bookingId,
  userId,
  autoOpenNotes,
  onViewProof,
  onUploadProof,
  onEditPayment,
  onSetFirstMonthOverride,
  onSendReminder,
  showReminder = true,
  reminderStage,
  reminderSentAt,
  rentDueDay,
  sepaChargeable = false,
  onChargeSepa,
  receiptsByPayment,
  accruedFeesEur = 0,
  carriesFees = false,
}: {
  payment: any;
  isFirstMonth: boolean;
  standardRentCents: number;
  /** COL-621: the lease's rent for this row's month, discount and all — what a
   *  kickback is measured from when the row carries no gross figure yet. */
  leaseRentCents?: number;
  firstMonthOverride: number | null;
  proofByPayment: Record<string, any>;
  noteCountByPayment: Record<string, number>;
  /** Referral credits that landed on each rent row (COL-434), by payment id. */
  referralNotesByPayment?: Record<string, ReferralCreditNote[]>;
  busy: boolean;
  bookingId: number | null;
  userId: string | null;
  /** Mount with the notes panel open — set when this row was just created
   *  from a projected month via its "Notes" action. */
  autoOpenNotes?: boolean;
  onViewProof: (proof: any) => void;
  onUploadProof: (paymentId: string) => void;
  onDeleteProof: (proof: any) => void;
  onEditPayment: (id: string, patch: any) => Promise<void>;
  onSetFirstMonthOverride: (euros: number | null) => Promise<void>;
  onSendReminder: () => void;
  /**
   * Whether an overdue row offers "Send next reminder" (on by default). The
   * Rent schedule keeps it on: since the schedule/history split, that card
   * carries every open month, arrears included, while history is paid rent
   * only — so chasing belongs next to the row rather than on some other
   * surface. Real (materialized) rows only ever show it once actually
   * overdue — see `isOverdue` below.
   */
  showReminder?: boolean;
  /** bookings.reminder_stage — last dunning stage actually sent (COL-381). */
  reminderStage?: number | null;
  /** bookings.reminder_sent_at of that last send (COL-381). */
  reminderSentAt?: string | null;
  /** bookings.rent_due_date — day-of-month the rent is due (COL-381). */
  rentDueDay?: number | null;
  /** Tenant has an active mandate + the room's SEPA gate is on (5a). */
  sepaChargeable?: boolean;
  /** Ask for confirmation to debit THIS row via the mandate (5a). */
  onChargeSepa?: (payment: any) => void;
  /** COL-417: receipts behind each payment id, newest first. */
  receiptsByPayment?: Record<string, PaymentReceipt[]>;
  /** bookings.accumulated_fees_eur — bounds the Mahngebühr split in the dialog. */
  accruedFeesEur?: number;
  /** COL-472: this is the earliest open month, which shows the fee pool. */
  carriesFees?: boolean;
}) {
  const { t, language } = useLanguage();
  const [editing, setEditing] = useState(false);
  const [notesOpen, setNotesOpen] = useState(!!autoOpenNotes);
  const noteCount = noteCountByPayment[String(p.id)] ?? 0;
  const [amount, setAmount] = useState(
    ((p.amount_cents || 0) / 100).toString()
  );
  const [due, setDue] = useState(
    p.due_date ? String(p.due_date).slice(0, 10) : ""
  );
  // The checkbox reflects the raw column (what "Mark paid" toggles), not the
  // corroborated state — un-ticking a row whose proof is merely missing must
  // still be the action that clears `status`.
  const [markPaid, setMarkPaid] = useState(p.status === "paid");
  // COL-621: on for a row coliviq already funds part of (a kickback, or a
  // referral credit — the same thing to the owner).
  const [kickback, setKickback] = useState(kickbackCents(p) > 0);
  const kickbackBase = kickbackBaseCents(p, leaseRentCents);
  const proof = proofByPayment[String(p.id)] || null;
  const st = statusMeta(p, proofByPayment, t);
  const referralNotes = referralNotesByPayment?.[String(p.id)] ?? [];
  // COL-417: expected / received / remaining, from the one canonical resolver
  // so this row can never disagree with the reminder email or the dashboard.
  const settlement = resolveRentSettlement(
    p,
    (id) => proofByPayment[id] ?? null
  );
  const receipts = receiptsByPayment?.[String(p.id)] ?? [];
  const [recordOpen, setRecordOpen] = useState(false);
  const [receiptsOpen, setReceiptsOpen] = useState(false);
  const referralCredited = referralNotes.length > 0;
  const today = businessToday();
  // Canceled only — a bounced (failed) collection is still owed, so a past due
  // date on it is genuinely overdue.
  const isOverdue =
    !effectivelyPaid(p, proofByPayment) &&
    String(p.status ?? "").toLowerCase() !== "canceled" &&
    p.due_date &&
    p.due_date <= today;
  // "Nth reminder sent (date, time)" under the status chip — same wording as
  // the property Reminders tab (COL-381), sourced from the same booking
  // columns so it reads identically and updates in real time.
  const reminderSentLine =
    isOverdue && (reminderStage ?? 0) > 0
      ? reminderScheduleLines({
          reminders_enabled: true,
          paid: false,
          rent_due_date: rentDueDay ?? null,
          reminder_stage: Math.min(4, Math.max(0, reminderStage ?? 0)) as
            | 0
            | ReminderStage,
          reminder_sent_at: reminderSentAt ?? null,
          current_stage: computeCurrentStage(
            computeDaysOverdue(rentDueDay ?? 0)
          ),
        }).sentLine
      : null;
  // The "Override" chip marks a figure an admin set by hand. A referral credit
  // explains exactly its own amount and nothing more: compare the rent BEFORE
  // the credit with the standard rent, so a row that is both credited and
  // manually changed still says so.
  const referralCreditCents = referralNotes.reduce(
    (sum, n) => sum + (Number(n.amount_cents) || 0),
    0
  );
  const overridden =
    (isFirstMonth && firstMonthOverride != null) ||
    (standardRentCents > 0 &&
      (p.amount_cents || 0) + referralCreditCents !== standardRentCents);

  const save = async () => {
    // `min="0"` on the input doesn't constrain a manually-typed / cleared value,
    // so validate here — a blank field must not silently save the month as €0.00.
    const parsed = parseFloat(amount);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      toast.error(
        t(
          "admin.tenantTabs.rentAmountValidation",
          "Enter a rent amount greater than €0."
        )
      );
      return;
    }
    const cents = Math.round(parsed * 100);
    // Only persist a first-month override when the amount actually changed from
    // what's stored. Opening the row just to tick "Mark paid" must not pin an
    // override (which would show a permanent "Override" chip and lock any
    // schedule regeneration to this value).
    if (isFirstMonth && cents !== (p.amount_cents || 0)) {
      await onSetFirstMonthOverride(cents / 100);
    }
    const patch: any = { amount_cents: cents, due_date: due || p.due_date };
    // Written only when it changes, so a plain amount edit on an ordinary row
    // does not touch the column at all.
    const gross = kickbackGrossCents(kickback, cents, kickbackBase);
    if (gross !== (p.gross_amount_cents ?? null))
      patch.gross_amount_cents = gross;
    if (markPaid && p.status !== "paid") {
      patch.status = "paid";
      patch.paid_at = new Date().toISOString();
    } else if (!markPaid && p.status === "paid") {
      patch.status = "pending";
      patch.paid_at = null;
    }
    await onEditPayment(String(p.id), patch);
    setEditing(false);
  };

  // ── Row actions (COL-417) ───────────────────────────────────────────────
  // Recording money received is hidden on Stripe rows: the gateway owns those,
  // and a local receipt would desync the ledger from the real charge.
  const canRecordPayment =
    !settlement.isSettled && !p.stripe_payment_intent_id && !p.stripe_charge_id;
  const canChargeSepa = Boolean(
    sepaChargeable &&
    onChargeSepa &&
    !effectivelyPaid(p, proofByPayment) &&
    !p.stripe_payment_intent_id &&
    // COL-417: a part-paid month is exactly when an admin wants to collect the
    // REMAINING balance by direct debit, so it must not be excluded here.
    isOpenRentStatus(String(p.status ?? "").toLowerCase())
  );
  const canSendReminder = Boolean(isOverdue && showReminder);

  const recordPaymentAction: RowAction = {
    key: "record",
    label: t("admin.tenantTabs.recordPayment", "Record payment"),
    icon: <IcPlus />,
    onSelect: () => !busy && setRecordOpen(true),
    disabled: busy,
    style: {
      ...actionBtn("#0B7268"),
      borderColor: "#C6EEDB",
      background: "#F4FCF8",
    },
  };
  const reminderAction: RowAction = {
    key: "reminder",
    label: t("admin.tenantTabs.sendNextReminder", "Send next reminder"),
    icon: <IcBell />,
    onSelect: () => !busy && onSendReminder(),
    disabled: busy,
    style: {
      ...actionBtn("#B45309"),
      border: "1px solid #F7E4BD",
      background: "#FFFBEB",
    },
  };

  // What an admin would actually do next with THIS month, in order: chase it,
  // else record what arrived. A settled month needs no primary at all — its
  // remaining actions are corrections, and a settled row should not present a
  // call to action it does not have.
  const primaryAction: RowAction | null = canSendReminder
    ? reminderAction
    : canRecordPayment
      ? recordPaymentAction
      : null;

  const overflowActions: RowAction[] = [
    ...(canSendReminder && canRecordPayment ? [recordPaymentAction] : []),
    ...(receipts.length > 0
      ? [
          {
            key: "receipts",
            label: t("admin.tenantTabs.receiptsAction", "Received"),
            icon: <IcReceipt />,
            count: receipts.length,
            onSelect: () => setReceiptsOpen((v) => !v),
          } as RowAction,
        ]
      : []),
    {
      key: "edit",
      label: t("admin.tenantTabs.editAmount", "Edit amount & due date"),
      icon: <IcEdit />,
      onSelect: () => setEditing((v) => !v),
      separatorBefore: receipts.length > 0 || canSendReminder,
    },
    {
      key: "upload",
      label: proof
        ? t("admin.tenantTabs.replaceProof", "Replace proof")
        : t("admin.tenantTabs.uploadProof", "Upload proof"),
      icon: <IcUpload />,
      onSelect: () => !busy && onUploadProof(String(p.id)),
      disabled: busy,
    },
    ...(proof
      ? [
          {
            key: "proof",
            label: t("admin.tenantTabs.viewProof", "View proof"),
            icon: <IcProof />,
            onSelect: () => onViewProof(proof),
          } as RowAction,
        ]
      : []),
    {
      key: "notes",
      label: t("admin.tenantTabs.notes", "Notes"),
      icon: <IcNotes />,
      count: noteCount > 0 ? noteCount : undefined,
      onSelect: () => setNotesOpen((v) => !v),
    },
    ...(canChargeSepa
      ? [
          {
            key: "sepa",
            label: t(
              "admin.tenantTabs.collectViaDirectDebit",
              "Collect via direct debit"
            ),
            icon: <IcBank />,
            onSelect: () => !busy && onChargeSepa!(p),
            disabled: busy,
            separatorBefore: true,
          } as RowAction,
        ]
      : []),
  ];

  return (
    <div style={{ borderBottom: "1px solid #F5F6F7" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1.4fr 1fr 1fr 1.1fr 1.6fr",
          gap: 12,
          padding: "13px 12px",
          alignItems: "center",
        }}
      >
        <div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>
            {monthLabel(p.due_date, language)}
          </div>
          {p.due_date && (
            <div style={{ fontSize: 11.5, color: "#94A3B8", marginTop: 2 }}>
              Due {formatDate(p.due_date)}
            </div>
          )}
          {referralNotes.map((n) => (
            <div
              key={n.referral_id}
              style={{
                fontSize: 11.5,
                color: "#0F766E",
                marginTop: 4,
                lineHeight: 1.45,
              }}
            >
              {referralCreditNoteText(n, "en")}
            </div>
          ))}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            flexWrap: "wrap",
          }}
        >
          {/* COL-417: on a part-paid month the headline figure is what ARRIVED
              against what was billed — "EUR 200.00 of EUR 650.00" — because an
              admin scanning the schedule is asking how far this month got, not
              re-reading the invoice. Untouched months render exactly as before:
              one figure, no scaffolding. */}
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {settlement.isPartial
              ? centsToEUR(settlement.receivedCents)
              : centsToEUR(p.amount_cents)}
            {settlement.isPartial && (
              <span style={{ color: "#94A3B8", fontWeight: 500 }}>
                {" "}
                {t("admin.tenantTabs.ofBilled", "of")}{" "}
                {centsToEUR(settlement.expectedCents)}
              </span>
            )}
          </span>
          {overridden && (
            <span style={chip("#F1ECFE", "#6D28D9", "#E1D5FB")}>
              {t("admin.tenantTabs.override", "Override")}
            </span>
          )}
          {/* COL-417: the shortfall belongs next to the figure, not hidden
              behind the status chip — an admin scanning the schedule needs to
              see how much is actually outstanding without opening the row. */}
          {settlement.isPartial && (
            <div style={{ flexBasis: "100%", marginTop: 5 }}>
              <div
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={settlement.expectedCents}
                aria-valuenow={settlement.receivedCents}
                style={{
                  height: 5,
                  borderRadius: 999,
                  background: "#EEF2F7",
                  overflow: "hidden",
                  maxWidth: 168,
                }}
              >
                <div
                  style={{
                    // Floored at a hairline so a token payment against a large
                    // month still reads as "something arrived" rather than as
                    // an empty bar that looks like nothing was recorded.
                    width: `${Math.max(
                      3,
                      Math.min(
                        100,
                        Math.round(
                          (settlement.receivedCents /
                            Math.max(1, settlement.expectedCents)) *
                            100
                        )
                      )
                    )}%`,
                    height: "100%",
                    borderRadius: 999,
                    background: "#2563EB",
                  }}
                />
              </div>
              <div
                style={{
                  fontSize: 11.5,
                  color: "#1D4ED8",
                  fontWeight: 600,
                  marginTop: 5,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {centsToEUR(settlement.remainingCents)}{" "}
                {t("admin.tenantTabs.remaining", "remaining")}
              </div>
            </div>
          )}
          {/* COL-472: the booking's Mahngebühren, on the one month that carries
              them, so the row reads the same total as the reminders list. */}
          {carriesFees && accruedFeeCents(accruedFeesEur) > 0 && (
            <div
              style={{
                flexBasis: "100%",
                fontSize: 11.5,
                color: "#B45309",
                fontWeight: 600,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              + {centsToEUR(accruedFeeCents(accruedFeesEur))}{" "}
              {t("admin.tenantTabs.dunningFees", "late fees")}
            </div>
          )}
          {referralCredited && (
            <span
              style={{
                ...chip("#E6FAF7", "#0F766E", "#B8EBE3"),
                whiteSpace: "nowrap",
              }}
              title="Reduced by a referral reward. The owner is still reported the full rent (payments.gross_amount_cents)."
            >
              Referral credit
            </span>
          )}
          {!referralCredited && kickbackCents(p) > 0 && (
            <span
              style={{
                ...chip("#F5F3FF", "#6D28D9", "#DDD6FE"),
                whiteSpace: "nowrap",
              }}
              title={t(
                "admin.tenantTabs.kickbackChipTitle",
                "coliviq covers this reduction. The owner is paid the full rent."
              )}
            >
              {t("admin.tenantTabs.kickbackChip", "Kickback {amount}").replace(
                "{amount}",
                `−${centsToEUR(kickbackCents(p))}`
              )}
            </span>
          )}
        </div>
        <div>
          <span style={st.chip} title={st.title}>
            {st.label}
          </span>
          {reminderSentLine && (
            <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 3 }}>
              {reminderSentLine}
            </div>
          )}
        </div>
        <div>
          <PaymentMethodCell p={p} proof={proof} />
        </div>
        {/* COL-417 / row actions: ONE contextual primary + an overflow menu.
            The row used to carry up to eight equal-weight outline buttons that
            wrapped onto three lines, which made every month look equally
            urgent and pushed the amounts around as the set changed. The
            primary is whatever an admin would actually do next to this month;
            everything else keeps working, one click deeper. */}
        <div
          style={{
            display: "flex",
            gap: 7,
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          {primaryAction && (
            <button
              onClick={primaryAction.onSelect}
              disabled={busy}
              style={primaryAction.style}
            >
              {primaryAction.icon}
              {primaryAction.label}
            </button>
          )}
          <RowActionsMenu
            actions={overflowActions}
            label={t("admin.tenantTabs.rowActions", "More actions")}
          />
        </div>
      </div>
      {editing && (
        <div
          style={{
            ...innerBox,
            margin: "0 12px 12px",
            padding: "14px 16px",
            display: "flex",
            alignItems: "flex-end",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <label style={{ display: "block" }}>
            <div style={{ ...uppLabel, marginBottom: 5 }}>
              Amount (€){isFirstMonth ? " · first-month override" : ""}
            </div>
            <input
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              style={{
                border: "1px solid #E3E6EA",
                borderRadius: 10,
                padding: "9px 12px",
                fontSize: 14,
                fontWeight: 600,
                width: 140,
              }}
            />
          </label>
          <label style={{ display: "block" }}>
            <div style={{ ...uppLabel, marginBottom: 5 }}>
              {t("admin.tenantTabs.dueDateLabel", "Due date")}
            </div>
            <input
              type="date"
              value={due}
              onChange={(e) => setDue(e.target.value)}
              style={{
                border: "1px solid #E3E6EA",
                borderRadius: 10,
                padding: "9px 12px",
                fontSize: 14,
                fontWeight: 600,
              }}
            />
          </label>
          <label
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
              paddingBottom: 9,
            }}
          >
            <input
              type="checkbox"
              checked={markPaid}
              onChange={(e) => setMarkPaid(e.target.checked)}
              style={{ width: 16, height: 16, accentColor: "#12B5A8" }}
            />
            <span style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>
              Mark paid
            </span>
          </label>
          <KickbackCheckbox
            checked={kickback}
            onChange={setKickback}
            amount={amount}
            fullRentCents={kickbackBase}
            lockedByReferral={referralCredited}
          />
          <button
            onClick={() => void save()}
            disabled={busy}
            style={{
              background: "#12B5A8",
              color: "#fff",
              border: "none",
              borderRadius: 10,
              padding: "10px 16px",
              fontWeight: 700,
              fontSize: 13,
              cursor: busy ? "default" : "pointer",
            }}
          >
            Save
          </button>
          {isFirstMonth && firstMonthOverride != null && (
            <button
              onClick={() => void onSetFirstMonthOverride(null)}
              disabled={busy}
              style={actionBtn("#B45309")}
            >
              Clear override
            </button>
          )}
          <button
            onClick={() => setEditing(false)}
            style={actionBtn("#64748B")}
          >
            Cancel
          </button>
        </div>
      )}
      {/* COL-417: what the balance is made of. Collapsed by default, and its
          own component so the correction hooks inside it mount only when an
          admin actually opens it — a hook at row level runs for every month in
          the schedule whether or not anyone looks. */}
      {receiptsOpen && receipts.length > 0 && (
        <PaymentReceiptsStrip
          receipts={receipts}
          // The transfer document lives on the month, not on one receipt —
          // offered from inside the strip so a hand-keyed receipt has
          // somewhere to point rather than being a dead end.
          proof={proof}
          onViewProof={onViewProof}
          onUploadProof={() => !busy && onUploadProof(String(p.id))}
          busy={busy}
        />
      )}
      {notesOpen && (
        <div style={{ margin: "0 12px 12px" }}>
          <AdminNotesSection
            paymentId={String(p.id)}
            bookingId={bookingId}
            userId={userId}
            title={t("admin.tenantTabs.paymentNotesTitle", "Payment notes")}
            compact
          />
        </div>
      )}
      {/* Mounted only while open. Every rent row renders one of these, and the
          dialog subscribes to react-query — an always-mounted copy per row
          means a query-client subscription per month for a dialog nobody has
          opened. */}
      {recordOpen && (
        <RecordPaymentDialog
          open
          onOpenChange={setRecordOpen}
          payment={{
            id: String(p.id),
            amount_cents: p.amount_cents,
            received_cents: p.received_cents,
          }}
          monthLabel={monthLabel(p.due_date, language)}
          accruedFeesEur={accruedFeesEur}
        />
      )}
    </div>
  );
}

function DepositPaymentRow({
  payment: p,
  proofByPayment,
  noteCountByPayment,
  busy,
  bookingId,
  userId,
  onViewProof,
  onUploadProof,
  onDeleteProof,
}: {
  payment: any;
  proofByPayment: Record<string, any>;
  noteCountByPayment: Record<string, number>;
  busy: boolean;
  bookingId: number | null;
  userId: string | null;
  onViewProof: (proof: any) => void;
  onUploadProof: (paymentId: string) => void;
  onDeleteProof: (proof: any) => void;
  onEditPayment: (id: string, patch: any) => Promise<void>;
  // Received via {...rowProps} spread but unused for the deposit row.
  onSendReminder?: () => void;
}) {
  const { t } = useLanguage();
  const [notesOpen, setNotesOpen] = useState(false);
  const proof = proofByPayment[String(p.id)] || null;
  const noteCount = noteCountByPayment[String(p.id)] ?? 0;
  const st = statusMeta(p, proofByPayment, t);
  return (
    <div style={{ borderBottom: "1px solid #F5F6F7" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1.4fr 1fr 1fr 1.1fr 1.6fr",
          gap: 12,
          padding: "13px 12px",
          alignItems: "center",
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 700 }}>
          {t("admin.tenantTabs.timeline.securityDeposit", "Security deposit")}
        </div>
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {centsToEUR(p.amount_cents)}
        </div>
        <div>
          <span style={st.chip} title={st.title}>
            {st.label}
          </span>
        </div>
        <div>
          <PaymentMethodCell p={p} proof={proof} />
        </div>
        {/* The proof cell used to stack four controls — filename + View, a red
            Delete, then Replace proof + Notes, then the upload timestamp —
            which made a single-row table about 250px tall and gave an
            irreversible Delete the same weight as everything else. One proof
            means one control: the file itself, with the rest behind the dot. */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            alignItems: "flex-start",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            {proof ? (
              <button
                onClick={() => onViewProof(proof)}
                title={proof.file_name || "Proof"}
                aria-label={`${t("admin.tenantTabs.viewProof", "View proof")}: ${proof.file_name || "Proof"}`}
                style={{
                  ...actionBtn("#0B7268"),
                  borderColor: "#C6EEDB",
                  background: "#F4FCF8",
                  maxWidth: 190,
                }}
              >
                <FileIcon size={13} />
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {proof.file_name || t("admin.tenantTabs.proof", "Proof")}
                </span>
              </button>
            ) : (
              <button
                onClick={() => !busy && onUploadProof(String(p.id))}
                disabled={busy}
                style={{
                  ...actionBtn("#0E7490"),
                  border: "1px dashed #C4D0DC",
                }}
              >
                <IcUpload />
                {t("admin.tenantTabs.uploadProof", "Upload proof")}
              </button>
            )}
            <RowActionsMenu
              label={t("admin.tenantTabs.rowActions", "More actions")}
              actions={[
                ...(proof
                  ? [
                      {
                        key: "view",
                        label: t("admin.tenantTabs.viewProof", "View proof"),
                        icon: <IcEye />,
                        onSelect: () => onViewProof(proof),
                      } as RowAction,
                    ]
                  : []),
                {
                  key: "upload",
                  label: proof
                    ? t("admin.tenantTabs.replaceProof", "Replace proof")
                    : t("admin.tenantTabs.uploadProof", "Upload proof"),
                  icon: <IcUpload />,
                  onSelect: () => !busy && onUploadProof(String(p.id)),
                  disabled: busy,
                },
                {
                  key: "notes",
                  label: t("admin.tenantTabs.notes", "Notes"),
                  icon: <IcNotes />,
                  count: noteCount > 0 ? noteCount : undefined,
                  onSelect: () => setNotesOpen((v) => !v),
                },
                ...(proof
                  ? [
                      {
                        key: "delete",
                        label: t(
                          "admin.tenantTabs.deleteProof",
                          "Delete proof…"
                        ),
                        icon: <IcTrash />,
                        onSelect: () => !busy && onDeleteProof(proof),
                        disabled: busy,
                        separatorBefore: true,
                        destructive: true,
                      } as RowAction,
                    ]
                  : []),
              ]}
            />
          </div>
          {proof?.created_at && (
            <span style={{ fontSize: 11.5, color: "#94A3B8", fontWeight: 500 }}>
              {t("admin.tenantTabs.uploadedAt", "Uploaded")}{" "}
              {formatDateTime(proof.created_at)}
            </span>
          )}
        </div>
      </div>
      {notesOpen && (
        <div style={{ margin: "0 12px 12px" }}>
          <AdminNotesSection
            paymentId={String(p.id)}
            bookingId={bookingId}
            userId={userId}
            title={t("admin.tenantTabs.depositNotesTitle", "Deposit notes")}
            compact
          />
        </div>
      )}
    </div>
  );
}

function RemindersSection({
  activity,
  booking,
}: {
  activity: any[];
  booking: Record<string, any>;
}) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const reminders = (activity || []).filter((a: any) =>
    /reminder_sent|admin_email_sent|inkasso_referred/.test(
      String(a.event_type || "")
    )
  );
  const overdue =
    /overdue|inkasso/.test(String(booking.payment_status || "")) ||
    (booking.reminder_stage ?? 0) > 0;
  return (
    <CollapsibleCard
      iconBg="#FFF2E7"
      iconColor="#EA580C"
      icon={
        <svg
          width="19"
          height="19"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </svg>
      }
      title={t("admin.tenantTabs.paymentRemindersTitle", "Payment reminders")}
      subtitle={`${reminders.length} ${t("admin.tenantTabs.sentCountUnit", "sent")}${overdue ? ` \u00b7 ${t("admin.tenantTabs.overdueSuffix", "overdue")}` : ""}`}
      open={open}
      onToggle={() => setOpen((v) => !v)}
    >
      {reminders.length === 0 ? (
        <div
          style={{
            padding: "16px 24px",
            fontSize: 13.5,
            color: "#94A3B8",
            fontWeight: 500,
          }}
        >
          {t(
            "admin.tenantTabs.noPaymentRemindersSent",
            "No payment reminders sent."
          )}
        </div>
      ) : (
        reminders.map((r: any) => (
          <div
            key={r.id}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              padding: "13px 24px",
              borderBottom: "1px solid #F5F6F7",
            }}
          >
            <div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>
                {r.email_subject || reminderLabel(r, t)}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "#94A3B8",
                  fontWeight: 500,
                  marginTop: 1,
                }}
              >
                {r.stage != null
                  ? `${t("admin.tenantTabs.stagePrefix", "Stage")} ${r.stage} \u00b7 `
                  : ""}
                {formatDateTime(r.created_at) ?? ""}
                {r.email_to ? ` · ${r.email_to}` : ""}
              </div>
            </div>
            {r.amount_eur != null && (
              <span style={{ fontSize: 13.5, fontWeight: 700 }}>
                {money(r.amount_eur)}
              </span>
            )}
          </div>
        ))
      )}
    </CollapsibleCard>
  );
}
function reminderLabel(
  r: any,
  t: (key: string, fallback?: string) => string
): string {
  const m: Record<string, string> = {
    reminder_sent: t(
      "admin.tenantTabs.reminderSentLabel",
      "Payment reminder sent"
    ),
    admin_email_sent: t(
      "admin.tenantTabs.adminEmailSentLabel",
      "Admin email sent"
    ),
    inkasso_referred: t(
      "admin.tenantTabs.inkassoReferredLabel",
      "Referred to Inkasso"
    ),
  };
  return (
    m[String(r.event_type)] ||
    t("admin.tenantTabs.reminderFallback", "Reminder")
  );
}

/* ================================================================== */
/* MOVE-IN                                                             */
/* ================================================================== */

export function MoveInTab({
  booking,
  journey,
  room,
  email,
  tenantName,
  isSuperAdmin = false,
  depositPaidAt,
  busy,
  moveInProtocolSubmitted,
  moveInProtocol,
  onOpenProtocolPdf,
  onRecordProtocol,
  onReplaceProtocol,
  onMarkKeyPickedUp,
  onMarkMoveInComplete,
  onReopenMoveInProtocol,
  onCloseMoveInProtocol,
  onUploadKeyDoc,
  onSaveKeyNote,
  adminNotes,
  canGenerateContract,
  canSendContract,
  onGenerateContract,
  onSendContract,
  onSaveContractInternalNote,
  contractInternalNote,
  onVerifyContractDraft,
  onMarkContractSigned,
  onUploadSignedContract,
  propertyDefaultTemplateId,
  undo,
  canManage = false,
}: {
  booking: Record<string, any>;
  journey: TenantJourney;
  room: Record<string, any> | null;
  email: string;
  /** profile display name — bookings.name is often empty */
  tenantName?: string;
  /** gates writing the admin-only internal contract note */
  isSuperAdmin?: boolean;
  depositPaidAt: string | null;
  busy: boolean;
  moveInProtocolSubmitted: boolean;
  moveInProtocol: any;
  /** Opens the finalised protocol PDF — same handler the Move-out tab uses. */
  onOpenProtocolPdf?: (kind: "move_in" | "move_out") => void;
  /** Opens the dialog that files a protocol on the tenant's behalf. */
  onRecordProtocol?: (kind: "move_in" | "move_out") => void;
  /** Opens the same dialog to swap the document on an operator-recorded protocol. */
  onReplaceProtocol?: (kind: "move_in" | "move_out") => void;
  onMarkKeyPickedUp: (pickedUpAt?: string) => void;
  onMarkMoveInComplete: () => void;
  /** COL-427: grants the tenant an indefinite reopen after auto-accept. */
  onReopenMoveInProtocol?: () => void;
  /** COL-427: re-locks a reopened move-in protocol without submitting it. */
  onCloseMoveInProtocol?: () => void;
  /** Admin or super-admin: may drive booking-status transitions. */
  canManage?: boolean;
  onUploadKeyDoc: (file: File) => void;
  onSaveKeyNote: (note: string) => Promise<void>;
  adminNotes?: any[];
  /** false when contract generation isn't wired on this surface */
  canGenerateContract: boolean;
  /** false when RELEASING the draft isn't wired on this surface (separate callback) */
  canSendContract: boolean;
  /** builds the contract PDF; "draft" never notifies the tenant */
  onGenerateContract: (
    templateId: string | null,
    mode: "draft" | "send"
  ) => void;
  /** writes the admin-only internal contract note (super admins) */
  onSaveContractInternalNote?: (note: string) => Promise<void>;
  /** the admin-only contract note itself (admin_notes row, not on the booking) */
  contractInternalNote?: { body: string | null; created_at: string } | null;
  /** releases the verified draft to the tenant (email + status flip) */
  onSendContract: () => void;
  /** records the draft verification that unlocks sending */
  onVerifyContractDraft: (note: string) => Promise<void>;
  /** properties.default_contract_template_id, when known on this surface */
  propertyDefaultTemplateId?: string | null;
  onMarkContractSigned: () => void;
  /** opens the signed-contract PDF picker (manual upload) */
  onUploadSignedContract: () => void;
  /** set once move-in is confirmed; renders Undo next to the protocol card */
  undo?: { label: string; onUndo: () => void; onDismiss: () => void } | null;
}) {
  const { t } = useLanguage();
  /**
   * COL-427: the ONE source of truth for the move-in protocol's window state,
   * computed once and reused for every ProtocolCard prop below (expired,
   * reopened, manuallyClosed, unlocked). Before this, `unlocked` came only
   * from the journey stage — independent of this same date math — so admin
   * and tenant could theoretically disagree about whether the window is
   * currently open. Deriving every one of these props from a single call
   * makes that structurally impossible.
   */
  const moveInWindowStatus = computeMoveInWindowStatus({
    moveInDate: booking.move_in_date,
    moveInReopenedAt: booking.move_in_protocol_reopened_at,
  });

  const emails: EmailRow[] = [
    {
      id: "mi1",
      title: "Application received",
      desc: "Welcome & next steps",
      sent: true,
      time: formatDateTime(booking.created_at),
      subject: "We received your application",
      trigger: "Application submitted",
    },
    {
      id: "mi2",
      title: "Contract ready to sign",
      desc: "DocuSign link sent to tenant",
      sent: isContractSentForSignature(booking),
      // No dedicated contract-ready send timestamp exists; fall back to the
      // signed time as a best-available marker so the row isn't dateless.
      time: formatDateTime(booking.contract_signed_at),
      subject: "Your lease contract is ready to sign",
      trigger: "Contract generated",
    },
    {
      id: "mi3",
      title: "Deposit payment request",
      desc: `${money(booking.deposit_amount)} · SEPA / bank transfer`,
      sent: isBookingContractSigned(booking),
      time: formatDateTime(booking.contract_signed_at),
      subject: "Security deposit — payment details",
      trigger: "Contract signed",
    },
    {
      id: "mi4",
      title: "Move-in & key pickup details",
      desc: "KeyNest address, codes & directions",
      sent: journey.reached("key_pickup_pending"),
      time: formatDateTime(depositPaidAt),
      subject: "Your move-in & key pickup details",
      trigger: "Deposit paid",
    },
    {
      id: "mi5",
      title: "Move-in protocol reminder",
      desc: "Complete the handover walkthrough",
      sent:
        !!booking.move_in_activation_email_sent_at ||
        journey.reached("handover_pending"),
      time: formatDateTime(
        booking.move_in_activation_email_sent_at || booking.key_picked_up_at
      ),
      subject: "Reminder: complete your move-in protocol",
      trigger: "Key picked up",
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {/* COL-302: real send-log of the automated move-in emails */}
      <LifecycleEmailsSection
        bookingId={booking.id ?? null}
        flow="move_in"
        anchorDate={booking.move_in_date ?? null}
        active={[
          "confirmed",
          "active",
          "approved",
          "notice_given",
          "move_out_pending",
        ].includes(String(booking.status))}
      />

      <EmailSequence
        title={t(
          "admin.tenantTabs.onboardingEmailSequence",
          "Onboarding email sequence"
        )}
        emails={emails}
        recipient={email}
      />

      {/* Contract — the first move-in gate: generate / verify / send / sign. */}
      <ContractSection
        booking={booking}
        busy={busy}
        canGenerate={canGenerateContract}
        canSend={canSendContract}
        onGenerate={onGenerateContract}
        onSend={onSendContract}
        onSaveInternalNote={onSaveContractInternalNote}
        internalNoteBody={contractInternalNote?.body ?? null}
        internalNoteAt={contractInternalNote?.created_at ?? null}
        isSuperAdmin={isSuperAdmin}
        onVerifyDraft={onVerifyContractDraft}
        onMarkSigned={onMarkContractSigned}
        onUploadSigned={onUploadSignedContract}
        tenantName={tenantName}
        tenantEmail={email}
        room={room}
        propertyTitle={
          (room as { properties?: { title?: string } } | null)?.properties
            ?.title
        }
        propertyDefaultTemplateId={propertyDefaultTemplateId}
      />

      <KeysSection
        mode="pickup"
        room={room}
        booking={booking}
        done={!!booking.key_picked_up_at}
        // Normally only actionable at the Key-pickup-pending stage. Escape
        // hatch: a submitted move-in protocol with no recorded pickup means
        // the tenant is genuinely moved in but got stuck behind this gate
        // (e.g. the deposit signal never landed) — keep the action available
        // so an admin can record the pickup retroactively and unstick them.
        can={
          journey.stageId === "key_pickup_pending" ||
          (moveInProtocolSubmitted && !booking.key_picked_up_at)
        }
        doneAt={booking.key_picked_up_at}
        busy={busy}
        onMark={onMarkKeyPickedUp}
        onUploadDoc={onUploadKeyDoc}
        onSaveNote={onSaveKeyNote}
        adminNotes={adminNotes}
        canManage={canManage}
      />

      {/* move-in protocol */}
      <ProtocolCard
        title={t("admin.tenantTabs.moveInProtocolTitle", "Move-in protocol")}
        subtitle={t(
          "admin.tenantTabs.handoverWalkthroughSubtitle",
          "Handover walkthrough submitted by the tenant"
        )}
        submitted={moveInProtocolSubmitted}
        submittedAt={moveInProtocol?.submitted_at ?? null}
        protocolId={moveInProtocol?.id ?? null}
        protocolRef={moveInProtocol?.ref ?? null}
        protocolKind="move_in"
        hasPdf={!!moveInProtocol?.pdf_storage_path}
        onOpenPdf={onOpenProtocolPdf}
        /*
         * COL-427: journey-reached is necessary (a step not reached yet stays
         * "Locked" regardless of dates) but no longer sufficient on its own —
         * `state !== "locked_before"` means the window's own date math agrees
         * the step is actually open, so admin and tenant can't disagree about
         * whether the tenant can submit right now.
         */
        unlocked={
          journey.reached("handover_pending") &&
          moveInWindowStatus.state !== "locked_before"
        }
        expired={!moveInProtocolSubmitted && moveInWindowStatus.isExpired}
        /*
         * "Closed", not "Auto-accepted": the badge names the STATE of the
         * step (the tenant's window is shut), which is what an admin scanning
         * this tab needs. The contractual consequence — the room being deemed
         * accepted — is still stated in the card body, on the tenant's own
         * card, and in the mail they receive when the window lapses.
         */
        expiredLabel="Closed"
        manuallyClosed={
          !moveInProtocolSubmitted &&
          !!booking.move_in_protocol_manually_closed_at
        }
        /*
         * A real submission that still STANDS ends the step, reopen or not.
         * Not the plain `!moveInProtocolSubmitted`: the tick's deemed-accepted
         * placeholder also carries status 'submitted', and gating on that made
         * a reopen after an auto-close invisible — the card dropped straight
         * back to a filed state and never offered "Close protocol for tenant".
         *
         * And not `!isRealSubmission(moveInProtocol)` alone either: after a
         * reopen over an operator's recording, `moveInProtocol` still points at
         * that recording (nothing else is on file) and it IS a real submission,
         * but the reopen retired it — `moveInProtocolSubmitted` is what knows it
         * no longer stands. Both must hold for the step to count as finished.
         */
        reopened={
          !(moveInProtocolSubmitted && isRealSubmission(moveInProtocol)) &&
          moveInWindowStatus.state === "reopened"
        }
        canOverride={canManage && journey.stageId === "handover_pending"}
        overrideLabel="Mark move-in complete"
        canReopen={canManage}
        onReopen={onReopenMoveInProtocol}
        onClose={onCloseMoveInProtocol}
        busy={busy}
        onOverride={onMarkMoveInComplete}
        recordedByName={moveInProtocol?.recorded_by_name ?? null}
        recordedAt={moveInProtocol?.recorded_at ?? null}
        adminNote={moveInProtocol?.admin_note ?? null}
        autoSubmittedAt={moveInProtocol?.auto_submitted_at ?? null}
        /*
         * Keyed on the missing PDF, not on `!submitted`, and on `reached`
         * rather than the current stage. That single predicate covers both
         * broken shapes — never submitted, and the force-staged row that says
         * submitted with nothing attached — and it stays available for a
         * tenant who has already moved on, which is the whole point of
         * recording retroactively. `cancelled` is excluded because
         * CLOSED_STATUSES parks it at the last stage too, and an application
         * cancelled before anyone held a key has no handover to record.
         */
        /*
         * A tenant's own signed submission does not block a fresh recording —
         * an admin can only ever REPLACE an operator's own recording (see
         * `canReplace` below and the DB trigger it mirrors), never a tenant's.
         * So the gate here is "no document yet, OR the document on file is
         * not an operator's" — in the latter case "Record protocol…" files an
         * ADDITIONAL protocol alongside the tenant's, which stands untouched.
         */
        canRecord={
          canManage &&
          journey.reached("handover_pending") &&
          String(booking.status) !== "cancelled" &&
          (!moveInProtocol?.pdf_storage_path ||
            !isAdminRecorded(moveInProtocol))
        }
        onRecord={
          onRecordProtocol ? () => onRecordProtocol("move_in") : undefined
        }
        canReplace={canManage}
        onReplace={
          onReplaceProtocol ? () => onReplaceProtocol("move_in") : undefined
        }
      />

      {undo && (
        <UndoBanner
          label={undo.label}
          onUndo={undo.onUndo}
          onDismiss={undo.onDismiss}
          busy={busy}
        />
      )}
    </div>
  );
}

/* ================================================================== */
/* MOVE-OUT                                                            */
/* ================================================================== */

export function MoveOutTab({
  booking,
  journey,
  room,
  email,
  busy,
  moveOutProtocolSubmitted,
  moveOutProtocol,
  onInitiate,
  onConfirmDate,
  onMarkKeyReturned,
  onReopenMoveOutProtocol,
  onCloseMoveOutProtocol,
  onUploadKeyDoc,
  onSaveKeyNote,
  onReassign,
  adminNotes,
  canManage = false,
  onMarkMoveOutComplete,
  moveInProtocol,
  settlement,
  deductions,
  settlementSchemaReady = true,
  depositHeldCents = 0,
  depositExpectedCents = 0,
  onAssembleBundle,
  onOpenBundle,
  onDownloadBundle,
  onDepositReleased,
  onDepositLog,
  onSendBundle,
  onSettleDeposit,
  onMarkRefundPaid,
  onOpenStatement,
  onGenerateProtocolPdf,
  onOpenProtocolPdf,
  onDownloadProtocolPdf,
  onRecordProtocol,
  onReplaceProtocol,
  generatingProtocol = null,
  markingRefundPaid = false,
  assemblingBundle = false,
  moveOutDateRequest = null,
  onDecideMoveOutRequest,
  moveOutDateRequestedByTenantAt = null,
  undo,
}: {
  booking: Record<string, any>;
  journey: TenantJourney;
  room: Record<string, any> | null;
  email: string;
  busy: boolean;
  moveOutProtocolSubmitted: boolean;
  moveOutProtocol: any;
  onInitiate: () => void;
  onConfirmDate: (date: string) => void;
  onMarkKeyReturned: (returnedAt?: string) => void;
  /** COL-427: grants the tenant an indefinite reopen after auto-completion. */
  onReopenMoveOutProtocol?: () => void;
  /** COL-427: re-locks a reopened move-out protocol without submitting it. */
  onCloseMoveOutProtocol?: () => void;
  onUploadKeyDoc: (file: File) => void;
  onSaveKeyNote: (note: string) => Promise<void>;
  /** Super-admin only; opens the room-reassign dialog. Undefined = hidden. */
  onReassign?: () => void;
  adminNotes?: any[];
  /** Admin or super-admin: may drive booking-status transitions. */
  canManage?: boolean;
  /** Opens the "submit protocol on behalf of tenant" dialog for move-out. */
  onMarkMoveOutComplete?: () => void;
  /** Needed for the bundle: the move-in protocol is one of its three documents. */
  moveInProtocol?: any;
  settlement?: (SettlementRow & { id: string }) | null;
  deductions?: DepositDeductionRow[];
  /** False when this database lacks the deposit settlement migration. */
  settlementSchemaReady?: boolean;
  /** Deposit actually paid in — resolveDepositState().paidCents. */
  depositHeldCents?: number;
  /** Contractual deposit — getExpectedDepositEUR, for the shortfall note. */
  depositExpectedCents?: number;
  /** Admin/super-admin only; assembles or rebuilds the bundle. */
  onAssembleBundle?: () => void;
  onOpenBundle?: () => void;
  onDownloadBundle?: () => void;
  onDepositReleased?: () => void;
  /** Told what the deposit card's inline action actually did, for the caller to audit-log. */
  onDepositLog?: (event: DepositReleaseLogEvent) => void;
  onSendBundle?: () => void;
  onSettleDeposit?: () => void;
  onMarkRefundPaid?: () => void;
  onOpenStatement?: () => void;
  /** Regenerate a protocol's PDF when the tenant-side finalize never ran. */
  onGenerateProtocolPdf?: (kind: "move_in" | "move_out") => void;
  onOpenProtocolPdf?: (kind: "move_in" | "move_out") => void;
  onDownloadProtocolPdf?: (kind: "move_in" | "move_out") => void;
  /** Opens the dialog that files a protocol on the tenant's behalf. */
  onRecordProtocol?: (kind: "move_in" | "move_out") => void;
  /** Opens the same dialog to swap the document on an operator-recorded protocol. */
  onReplaceProtocol?: (kind: "move_in" | "move_out") => void;
  /** Which protocol is mid-generation, for the busy state on its row. */
  generatingProtocol?: "move_in" | "move_out" | null;
  markingRefundPaid?: boolean;
  assemblingBundle?: boolean;
  /** Checkout protocol (Neel Slack DM 2026-10-19): the tenant's own pending
   *  move-out date request, or null. */
  moveOutDateRequest?: {
    id: string;
    requested_date: string;
    created_at: string;
  } | null;
  onDecideMoveOutRequest?: (id: string, decision: "approve" | "reject") => void;
  /** The confirmed date's provenance, when it came from a tenant's request
   *  (checkout protocol) — shown as "Requested by tenant on {date}". */
  moveOutDateRequestedByTenantAt?: string | null;
  /** The green "Tenant request confirmed" banner — set only right after
   *  approving a tenant's move-out request (checkout protocol). */
  undo?: { label: string; onUndo: () => void; onDismiss: () => void } | null;
}) {
  const { t, language } = useLanguage();
  const isActive = journey.stageId === "active";
  const initiated = journey.reached("moveout_scheduled");
  const dateConfirmed = journey.reached("moveout_protocol_pending");
  // Prefill only an explicitly confirmed/planned move_out_date. end_date is
  // the contract's EARLIEST POSSIBLE move-out (contracts auto-prolong), and
  // prefilling it invited one-click "confirmations" of a date nobody chose —
  // which instantly emails the tenant ("Dein Auszug ist bestätigt").
  const [dateDraft, setDateDraft] = useState<string>(
    booking.move_out_date || ""
  );

  // COL-427: single source of truth for the move-out protocol's window
  // state — see the matching comment in MoveInTab.
  const moveOutWindowStatus = computeMoveOutWindowStatus({
    moveOutDate: booking.move_out_date,
    moveOutReopenedAt: booking.move_out_protocol_reopened_at,
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {/* Checkout protocol: "Tenant request confirmed" — green, separate
          from the generic amber UndoBanner (shared with Move-in), since
          this one specific confirm deserves its own success treatment. */}
      {undo && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "11px 16px",
            borderRadius: 14,
            background: "#E6F7EF",
            border: "1px solid #C6EEDB",
            fontSize: 13,
            fontWeight: 600,
            color: "#047857",
          }}
        >
          <CheckIcon />
          <span style={{ flex: 1 }}>{undo.label}</span>
          <button
            disabled={busy}
            onClick={undo.onUndo}
            style={{
              border: "1px solid #C6EEDB",
              background: "#fff",
              color: "#047857",
              borderRadius: 9,
              padding: "7px 14px",
              fontWeight: 700,
              fontSize: 12.5,
              cursor: busy ? "default" : "pointer",
            }}
          >
            {t("admin.tenantTabs.moveOut.undo", "Undo")}
          </button>
        </div>
      )}

      {/* Tenant requested a check-out date (checkout protocol, Neel Slack DM
          2026-10-19) — the admin decision lives HERE, above "initiate
          move-out", exactly where Neel asked for it. Approve reuses
          onConfirmDate (same write + emails as a manual confirm); reject
          leaves the booking untouched and opens an email composer. */}
      {moveOutDateRequest && onDecideMoveOutRequest && (
        <div
          style={{
            borderRadius: 20,
            border: "1px solid #FBD6BF",
            background: "#fff",
            boxShadow: "0 0 0 4px #FFF6F0",
            padding: "22px 24px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 13,
              marginBottom: 14,
            }}
          >
            <SectionIcon bg="#FFF2E7" color="#EA580C" size={40}>
              <BellIcon />
            </SectionIcon>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 700 }}>
                {t(
                  "admin.tenantTabs.moveOut.requestedTitle",
                  "Move-out requested by tenant"
                )}
              </div>
              <div
                style={{
                  fontSize: 12.5,
                  color: "#94A3B8",
                  fontWeight: 500,
                  marginTop: 2,
                }}
              >
                {t(
                  "admin.tenantTabs.moveOut.submittedFrom",
                  "Submitted from the tenant dashboard on"
                )}{" "}
                {formatDateTime(moveOutDateRequest.created_at, language)}
              </div>
            </div>
            <span style={CHIP.amber2}>
              {t("admin.tenantTabs.moveOut.needsDecision", "Needs decision")}
            </span>
          </div>
          <div
            style={{
              ...innerBox,
              padding: "16px 18px",
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 14,
            }}
          >
            <div>
              <div style={{ ...uppLabel, marginBottom: 6 }}>
                {t("admin.tenantTabs.moveOut.requestedDate", "Requested date")}
              </div>
              <div style={{ fontSize: 17, fontWeight: 700 }}>
                {formatDateNumeric(moveOutDateRequest.requested_date, language)}
              </div>
            </div>
            <div>
              <div style={{ ...uppLabel, marginBottom: 6 }}>
                {t(
                  "admin.tenantTabs.moveOut.earliestPerContract",
                  "Earliest per contract"
                )}
              </div>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#475569",
                  paddingTop: 2,
                }}
              >
                {formatDateNumeric(booking.end_date, language) ?? "—"}
              </div>
            </div>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginTop: 16,
              flexWrap: "wrap",
            }}
          >
            <button
              disabled={busy}
              onClick={() =>
                !busy &&
                onDecideMoveOutRequest(moveOutDateRequest.id, "approve")
              }
              style={{
                background: "#EA580C",
                color: "#fff",
                border: "none",
                borderRadius: 10,
                padding: "10px 16px",
                fontWeight: 700,
                fontSize: 13.5,
                cursor: busy ? "default" : "pointer",
              }}
            >
              {t(
                "admin.tenantTabs.moveOut.confirmRequestedDate",
                "Confirm move-out"
              )}
            </button>
            <button
              disabled={busy}
              onClick={() =>
                !busy && onDecideMoveOutRequest(moveOutDateRequest.id, "reject")
              }
              style={actionBtn("#0F172A")}
            >
              {t("admin.tenantTabs.moveOut.rejectAndReply", "Reject & reply")}
            </button>
            <span
              style={{
                marginLeft: "auto",
                fontSize: 12.5,
                color: "#94A3B8",
                fontWeight: 500,
              }}
            >
              {t(
                "admin.tenantTabs.moveOut.confirmingStarts",
                "Confirming starts the move-out sequence with this date."
              )}
            </span>
          </div>
        </div>
      )}

      {/* initiate move-out */}
      <div
        style={{
          borderRadius: 20,
          padding: "20px 24px",
          background: initiated ? "#FFF1E8" : "#F6F7F9",
          border: `1px solid ${initiated ? "#FBD6BF" : "#ECEEF1"}`,
          display: "flex",
          alignItems: "center",
          gap: 16,
        }}
      >
        <SectionIcon
          bg="#fff"
          color={initiated ? "#EA580C" : "#64748B"}
          size={42}
        >
          <ExitIcon />
        </SectionIcon>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>
            {t("admin.tenantTabs.initiateMoveOut", "Initiate move-out")}
          </div>
          <div
            style={{
              fontSize: 13,
              color: "#64748B",
              fontWeight: 500,
              marginTop: 2,
            }}
          >
            {initiated
              ? "Move-out flow is active. Complete the steps below."
              : isActive
                ? "Turn on to start the move-out flow for this tenant."
                : "Only available while the tenant is Active."}
          </div>
        </div>
        <button
          onClick={() => {
            if (!canManage) return;
            if (isActive) onInitiate();
            else if (!initiated)
              toast.message(
                t(
                  "admin.tenantTabs.tenantMustBeActive",
                  "Tenant must be Active to initiate move-out."
                )
              );
          }}
          disabled={busy || !canManage || (!isActive && !initiated)}
          style={{
            position: "relative",
            width: 52,
            height: 30,
            borderRadius: 999,
            border: "none",
            cursor: isActive || initiated ? "pointer" : "not-allowed",
            flexShrink: 0,
            transition: "background .2s",
            background: initiated ? "#EA580C" : "#D8DCE2",
          }}
        >
          <span
            style={{
              position: "absolute",
              top: 3,
              left: 3,
              width: 24,
              height: 24,
              borderRadius: "50%",
              background: "#fff",
              boxShadow: "0 1px 3px rgba(0,0,0,.3)",
              transition: "transform .2s",
              transform: `translateX(${initiated ? "22px" : "0"})`,
            }}
          />
        </button>
      </div>

      {/* steady-state indefinite note */}
      {isActive && (
        <div
          style={{
            background: "#F0FAF8",
            border: "1px solid #CDECE7",
            borderRadius: 16,
            padding: "16px 18px",
            display: "flex",
            alignItems: "center",
            gap: 13,
          }}
        >
          <SectionIcon bg="#fff" color="#0B7268" size={38} radius={11}>
            <RefreshIcon />
          </SectionIcon>
          <div
            style={{
              fontSize: 13,
              color: "#0B5A52",
              fontWeight: 500,
              lineHeight: 1.45,
            }}
          >
            {t("admin.tenantTabs.tenantStaysActivePrefix", "This tenant stays")}{" "}
            <strong style={{ fontWeight: 700 }}>
              {t("admin.tenantTabs.timeline.active", "Active")}
            </strong>{" "}
            {t(
              "admin.tenantTabs.tenantStaysActiveSuffix",
              "until move-out is initiated — on your command or at the tenant's request."
            )}
          </div>
        </div>
      )}

      {/* date confirm */}
      {initiated && (
        <div
          style={{
            background: "#fff",
            border: "1px solid #ECEEF1",
            borderRadius: 20,
            padding: "22px 24px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 13,
              marginBottom: 14,
            }}
          >
            <SectionIcon bg="#FFF2E7" color="#EA580C" size={40}>
              <CalendarIcon />
            </SectionIcon>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 700 }}>
                {t("admin.tenantTabs.moveOutDateHeader", "Move-out date")}
              </div>
              <div
                style={{
                  fontSize: 12.5,
                  color: "#94A3B8",
                  fontWeight: 500,
                  marginTop: 2,
                }}
              >
                {dateConfirmed
                  ? "Confirmed. Move-out sequence is running."
                  : "Edit and confirm the final move-out date."}
              </div>
            </div>
            <span style={dateConfirmed ? CHIP.green : CHIP.amber2}>
              {dateConfirmed ? "Confirmed" : "Needs confirmation"}
            </span>
          </div>
          <div
            style={{
              ...innerBox,
              padding: "16px 18px",
              display: "flex",
              alignItems: "flex-end",
              gap: 14,
              flexWrap: "wrap",
            }}
          >
            <div>
              <div style={{ ...uppLabel, marginBottom: 6 }}>
                Confirmed move-out date
              </div>
              <input
                type="date"
                value={dateDraft ? String(dateDraft).slice(0, 10) : ""}
                onChange={(e) => setDateDraft(e.target.value)}
                disabled={dateConfirmed}
                style={{
                  border: "1px solid #E3E6EA",
                  borderRadius: 10,
                  padding: "9px 12px",
                  fontSize: 14,
                  fontWeight: 600,
                  background: "#fff",
                }}
              />
            </div>
            {dateConfirmed && moveOutDateRequestedByTenantAt && (
              <div
                style={{
                  fontSize: 12.5,
                  color: "#94A3B8",
                  fontWeight: 500,
                  paddingBottom: 10,
                }}
              >
                {t(
                  "admin.tenantProfile.requestedByTenantOn",
                  "Requested by tenant on"
                )}{" "}
                {formatDateNumeric(moveOutDateRequestedByTenantAt, language)}
              </div>
            )}
            {!dateConfirmed && !!booking.end_date && (
              <div
                style={{
                  flexBasis: "100%",
                  order: 2,
                  fontSize: 12.5,
                  color: "#94A3B8",
                  fontWeight: 500,
                }}
              >
                Earliest possible move-out per contract:{" "}
                {formatDate(booking.end_date) ?? "—"} · confirming sends the
                tenant the move-out confirmation email immediately.
              </div>
            )}
            {!dateConfirmed && (
              <button
                onClick={() =>
                  canManage && dateDraft && onConfirmDate(dateDraft)
                }
                disabled={busy || !canManage || !dateDraft}
                style={{
                  background: "#EA580C",
                  color: "#fff",
                  border: "none",
                  borderRadius: 10,
                  padding: "10px 16px",
                  fontWeight: 700,
                  fontSize: 13.5,
                  cursor: busy || !dateDraft ? "default" : "pointer",
                  opacity: busy || !dateDraft ? 0.7 : 1,
                }}
              >
                Confirm date
              </button>
            )}
          </div>
        </div>
      )}

      {/* COL-304: real send-log of the automated move-out emails */}
      {initiated && (
        <LifecycleEmailsSection
          bookingId={booking.id ?? null}
          flow="move_out"
          anchorDate={booking.move_out_date ?? null}
          active={dateConfirmed}
        />
      )}

      {/* move-out email sequence */}
      {initiated && (
        <EmailSequence
          title={t(
            "admin.tenantTabs.moveOutEmailSequence",
            "Move-out email sequence"
          )}
          recipient={email}
          emails={[
            {
              id: "mo1",
              title: "Move-out initiated",
              desc: "Notice acknowledged to tenant",
              sent: journey.reached("moveout_scheduled"),
              time: null,
              subject: "We've received your move-out notice",
              trigger: "Move-out initiated",
            },
            {
              id: "mo2",
              title: "Move-out date confirmed",
              desc: "Confirmation with final date",
              sent: journey.reached("moveout_protocol_pending"),
              time: formatDate(booking.move_out_date),
              subject: "Your move-out date is confirmed",
              trigger: "Move-out date confirmed",
            },
            {
              id: "mo3",
              title: "Move-out protocol reminder",
              desc: "Book the final walkthrough",
              sent: !!booking.move_out_reminder_48h_sent_at,
              time: formatDateTime(booking.move_out_reminder_48h_sent_at),
              subject: "Reminder: book your final walkthrough",
              trigger: "Move-out date confirmed",
            },
            {
              id: "mo4",
              title: "Key return instructions",
              desc: "Where & how to return keys",
              sent: journey.reached("key_return_pending"),
              time: null,
              subject: "How to return your keys",
              trigger: "Move-out protocol submitted",
            },
            {
              id: "mo5",
              title: "Deposit return summary",
              desc: "Deposit settlement statement",
              // Reaching `moved_out` was never evidence this went out — keys
              // being back says nothing about a statement having been sent. The
              // settlement's own timestamp is the only thing that does.
              sent: !!settlement?.statement_sent_at,
              time: formatDateTime(settlement?.statement_sent_at ?? null),
              subject: "Your deposit settlement statement",
              trigger: "Deposit released",
            },
          ]}
        />
      )}

      {/* key return */}
      {initiated && (
        <KeysSection
          mode="return"
          room={room}
          booking={booking}
          done={!!booking.key_returned_at}
          can={
            journey.reached("moveout_protocol_pending") &&
            // Not `!reached("moved_out")`: deposit_refund_pending now sits
            // between, so that would keep the control live after the keys are
            // already back. This is the stage the old predicate meant.
            !journey.reached("deposit_refund_pending")
          }
          doneAt={booking.key_returned_at}
          busy={busy}
          onMark={onMarkKeyReturned}
          onUploadDoc={onUploadKeyDoc}
          onSaveNote={onSaveKeyNote}
          adminNotes={adminNotes}
          canManage={canManage}
        />
      )}

      {/* move-out protocol */}
      {dateConfirmed && (
        <ProtocolCard
          title={t(
            "admin.tenantTabs.moveOutProtocolTitle",
            "Move-out protocol"
          )}
          subtitle={t(
            "admin.tenantTabs.finalWalkthroughSubtitle",
            "Final walkthrough submitted by the tenant"
          )}
          submitted={moveOutProtocolSubmitted}
          submittedAt={moveOutProtocol?.submitted_at ?? null}
          protocolId={moveOutProtocol?.id ?? null}
          protocolRef={moveOutProtocol?.ref ?? null}
          protocolKind="move_out"
          hasPdf={!!moveOutProtocol?.pdf_storage_path}
          onOpenPdf={onOpenProtocolPdf}
          // COL-427: same reasoning as MoveInTab — journey-reached alone is
          // no longer sufficient, the window's own date math must agree too.
          unlocked={
            journey.reached("moveout_protocol_pending") &&
            moveOutWindowStatus.state !== "locked_before"
          }
          /*
           * Still no bare "mark submitted" here: an unevidenced claim with no
           * document is what leaves the deposit bundle permanently blocked in
           * the first place. Recording IS the affordance — it produces the PDF
           * the bundle needs.
           */
          expired={!moveOutProtocolSubmitted && moveOutWindowStatus.isExpired}
          expiredLabel="Window closed"
          manuallyClosed={
            !moveOutProtocolSubmitted &&
            !!booking.move_out_protocol_manually_closed_at
          }
          /* Same as the move-in card above — the tick auto-completes this
           * window too, leaving a placeholder that must not swallow a reopen,
           * and a reopen retires an operator's recording the same way. */
          reopened={
            !(moveOutProtocolSubmitted && isRealSubmission(moveOutProtocol)) &&
            moveOutWindowStatus.state === "reopened"
          }
          canOverride={
            canManage && journey.stageId === "moveout_protocol_pending"
          }
          overrideLabel="Mark move-out complete"
          canReopen={canManage}
          onReopen={onReopenMoveOutProtocol}
          onClose={onCloseMoveOutProtocol}
          busy={busy}
          recordedByName={moveOutProtocol?.recorded_by_name ?? null}
          recordedAt={moveOutProtocol?.recorded_at ?? null}
          adminNote={moveOutProtocol?.admin_note ?? null}
          autoSubmittedAt={moveOutProtocol?.auto_submitted_at ?? null}
          // See the move-in card above for why this allows an additional
          // recording alongside a tenant's own submission.
          canRecord={
            canManage &&
            journey.reached("moveout_protocol_pending") &&
            String(booking.status) !== "cancelled" &&
            (!moveOutProtocol?.pdf_storage_path ||
              !isAdminRecorded(moveOutProtocol))
          }
          onRecord={
            onRecordProtocol ? () => onRecordProtocol("move_out") : undefined
          }
          canReplace={canManage}
          onReplace={
            onReplaceProtocol ? () => onReplaceProtocol("move_out") : undefined
          }
          onOverride={onMarkMoveOutComplete}
        />
      )}

      {/* Reassign / move room — avoids a dead-end when a tenant is changing
          rooms rather than leaving. Routes to the shared room-reassign flow. */}
      {onReassign && (
        <div
          style={{
            background: "#fff",
            border: "1px solid #ECEEF1",
            borderRadius: 20,
            padding: "18px 22px",
            display: "flex",
            alignItems: "center",
            gap: 14,
          }}
        >
          <SectionIcon bg="#F1ECFE" color="#8B5CF6" size={40}>
            <RefreshIcon />
          </SectionIcon>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700 }}>
              Reassign / move room
            </div>
            <div
              style={{
                fontSize: 12.5,
                color: "#94A3B8",
                fontWeight: 500,
                marginTop: 2,
              }}
            >
              Move this tenant to a different room instead of moving them out.
            </div>
          </div>
          <button
            onClick={onReassign}
            style={{
              border: "1px solid #E7E9EC",
              background: "#fff",
              color: "#6D28D9",
              borderRadius: 11,
              padding: "10px 16px",
              fontSize: 13.5,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Reassign room…
          </button>
        </div>
      )}

      {/* deposit return — the bundle to the lessor's representative, their
          release decision, and the refund. Renders its own states, including
          the blocked one when a bundle document is missing, and a dormant one
          before the move-out date is confirmed. Deliberately unconditional:
          the card explains its own absence of actions, which an omitted card
          cannot do. */}
      <DepositReturnCard
        moveOutDateConfirmed={dateConfirmed}
        blockedRequirement={journey.blockedBy?.requirement ?? null}
        moveOutDate={booking.actual_move_out_date ?? booking.move_out_date}
        settlement={settlement}
        deductions={deductions}
        schemaReady={settlementSchemaReady}
        contractUrl={booking.signed_contract_url ?? booking.contract_pdf_url}
        moveInProtocolPdfPath={moveInProtocol?.pdf_storage_path ?? null}
        moveOutProtocolPdfPath={moveOutProtocol?.pdf_storage_path ?? null}
        depositHeldCents={depositHeldCents}
        depositExpectedCents={depositExpectedCents}
        onAssemble={canManage ? onAssembleBundle : undefined}
        onOpenBundle={onOpenBundle}
        onDownloadBundle={onDownloadBundle}
        onReleased={onDepositReleased}
        onLog={onDepositLog}
        onSendToOwner={canManage ? onSendBundle : undefined}
        onSettle={canManage ? onSettleDeposit : undefined}
        onMarkRefundPaid={canManage ? onMarkRefundPaid : undefined}
        onOpenStatement={onOpenStatement}
        onGenerateProtocolPdf={canManage ? onGenerateProtocolPdf : undefined}
        onOpenProtocolPdf={onOpenProtocolPdf}
        onDownloadProtocolPdf={onDownloadProtocolPdf}
        generatingProtocol={generatingProtocol}
        markingPaid={markingRefundPaid}
        assembling={assemblingBundle}
      />
    </div>
  );
}

/* ================================================================== */
/* DOCUMENTS                                                           */
/* ================================================================== */

const isImageName = (name: string) =>
  /\.(png|jpe?g|webp|gif|avif)$/i.test(name || "");

const DOC_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "other", label: "Other" },
  { value: "id_passport", label: "ID / Passport" },
  { value: "residence_permit", label: "Residence permit" },
  { value: "proof_of_income", label: "Proof of income" },
  { value: "employment_letter", label: "Employment letter" },
  { value: "contract", label: "Contract" },
  { value: "reference", label: "Reference" },
  { value: "move_in_protocol", label: "Move-in protocol" },
  { value: "move_out_protocol", label: "Move-out protocol" },
];

/** Sentinel value for the "+ New label" option — never a real document_type. */
const NEW_LABEL_VALUE = "__new_label__";

const MAX_UPLOAD_BYTES = 20 * 1024 * 1024; // 20 MB
/** Validate a document upload; returns an error message or null if OK. */
function documentUploadError(
  file: File,
  t: (key: string, fallback?: string) => string
): string | null {
  const okType =
    /\.(pdf|jpe?g|png)$/i.test(file.name) ||
    ["application/pdf", "image/jpeg", "image/png"].includes(file.type);
  if (!okType)
    return t(
      "admin.tenantTabs.uploadTypeError",
      "Only PDF, JPG or PNG files can be uploaded."
    );
  if (file.size > MAX_UPLOAD_BYTES)
    return t(
      "admin.tenantTabs.uploadSizeError",
      "File is too large (max 20 MB)."
    );
  return null;
}

export function DocumentsTab({
  documents,
  filedDocuments,
  booking,
  issueComments,
  adminNotes,
  auditLogs,
  busy,
  canUpload,
  onUploadDocument,
  onRenameDocument,
  onRenameNoteAttachment,
  onSaveDocumentNote,
  onDeleteDocument,
  onEditDocumentLabel,
  customLabels,
  onCreateLabel,
  loading,
}: {
  documents: any[];
  /**
   * apartment_documents rows with scope='tenant' — what the OWNER (or staff)
   * filed against this tenancy, as opposed to what the tenant uploaded about
   * themselves. They were invisible on this screen until now: this tab only
   * ever read tenant_documents, the contract and admin-note attachments, so an
   * owner's upload landed correctly in the database and was never shown.
   */
  filedDocuments: FiledTenantDocument[];
  booking: Record<string, any>;
  issueComments: any[];
  /** Admin notes (incl. key-handover uploads) — file attachments surface here. */
  adminNotes: any[];
  /** Audit rows — supply the "who + when" behind each saved document note. */
  auditLogs: any[];
  busy: boolean;
  canUpload: boolean;
  onUploadDocument: (file: File, docType: string) => void;
  /**
   * Rename / delete apply to tenant_documents uploads and admin_notes
   * attachments (see the DOC_NOTE_PREFIX-routed ids). The notes field only
   * exists on tenant_documents rows — gated separately via `notable`.
   */
  onRenameDocument?: (id: string, name: string) => void;
  /**
   * Rename an internal note's attachment. Separate from `onRenameDocument`
   * because it writes `admin_notes.file_name`, not a tenant_documents row, and
   * the database only lets the note's author (or a super admin) do it.
   */
  onRenameNoteAttachment?: (noteId: string, name: string) => void;
  onSaveDocumentNote?: (id: string, note: string) => void;
  onDeleteDocument?: (
    doc: { id: string; file_url?: string | null; file_name?: string | null },
    reason: string
  ) => void;
  /**
   * Change a tenant_documents row's label (document_type) after upload —
   * same set as the upload dropdown. Admin-notes attachments have no
   * document_type column, so this only applies to `editable` items.
   */
  onEditDocumentLabel?: (id: string, docType: string) => void;
  /** Admin-created labels ("+ New label"), merged into every type dropdown. */
  customLabels?: Array<{ slug: string; label: string }>;
  onCreateLabel?: (label: string) => Promise<unknown> | void;
  loading: boolean;
}) {
  const { t, language } = useLanguage();
  // Rename / note / delete / label modal targets (tenant_documents items only).
  const [renameTarget, setRenameTarget] = useState<any | null>(null);
  const [noteTarget, setNoteTarget] = useState<any | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [labelTarget, setLabelTarget] = useState<any | null>(null);
  const [creatingLabel, setCreatingLabel] = useState(false);
  const [query, setQuery] = useState("");
  const [group, setGroup] = useState<string>("All");
  const uploadRef = useRef<HTMLInputElement>(null);
  const [uploadType, setUploadType] = useState<string>("other");
  const [dragOver, setDragOver] = useState(false);
  // Fixed options plus whatever custom labels admins have created — same list
  // for the upload dropdown and the per-document "Edit label" picker.
  const docTypeOptions = [
    ...DOC_TYPE_OPTIONS.map((o) => ({
      ...o,
      label: t(`admin.docType.${o.value}`, o.label),
    })),
    ...(customLabels ?? []).map((l) => ({ value: l.slug, label: l.label })),
  ];
  // Validate, then upload; switch the filter to the uploaded type's group so
  // the admin sees the new file land.
  const startUpload = (file: File) => {
    if (busy) return;
    const err = documentUploadError(file, t);
    if (err) {
      toast.error(err);
      return;
    }
    setGroup(docGroup(uploadType));
    onUploadDocument(file, uploadType);
  };
  const customLabelsMap = useMemo(
    () =>
      Object.fromEntries((customLabels ?? []).map((l) => [l.slug, l.label])),
    [customLabels]
  );
  const all = useMemo(() => {
    type DocItem = {
      id: string;
      name: string;
      type: string;
      group: string;
      status: string;
      meta: string;
      url: string | null;
      editable?: boolean;
      /** Rename-only card: the generated contract, backed by the booking. */
      renamable?: boolean;
      // Gates the note-editor button separately from rename/delete: real
      // tenant_documents rows support the `notes` field, admin_notes-backed
      // attachments already carry their own body text and don't.
      notable?: boolean;
      docId?: string;
      note?: string | null;
      noteAt?: string | null;
      noteBy?: string | null;
      fileUrl?: string | null;
      fileName?: string;
      /** `url` is a private admin-notes path — sign it before opening. */
      privateNotePath?: boolean;
      /**
       * `url` is a tenant_documents Storage path (bare, or bucket-qualified
       * like "handover-protocols/pdfs/<id>.pdf") rather than a browser URL —
       * sign it via resolveTenantDocumentUrl before opening.
       */
      signedDocPath?: boolean;
      /** Set on an internal-note attachment: the admin_notes row it hangs off. */
      noteId?: string;
      /** Raw document_type, so "Edit label" can preselect the current value. */
      docType?: string;
    };
    const base: DocItem[] = documents.map((d: any) => ({
      id: String(d.id),
      name: d.file_name || d.title || "Document",
      type: labelForDocType(d.document_type, customLabelsMap, t),
      group: docGroup(d.document_type),
      status: d.status || "uploaded",
      meta: d.created_at ? `uploaded ${formatDate(d.created_at) ?? ""}` : "",
      url: d.file_url,
      signedDocPath: true,
      // Real tenant_documents rows: rename / note / delete / label all apply.
      editable: true,
      notable: true,
      docId: String(d.id),
      docType: d.document_type,
      note: d.notes ?? null,
      noteAt: d.notes_updated_at ?? null,
      noteBy: d.notes_author?.name || d.notes_author?.email || null,
      fileUrl: d.file_url ?? null,
      fileName: d.file_name || d.title || "Document",
    }));
    const contractUrl = booking.signed_contract_url || booking.contract_pdf_url;
    if (contractUrl) {
      // The card is built from the booking's contract URL, not a row, so its
      // name lives on the booking (COL-588). Rename is the only edit it takes.
      const contractName =
        String(booking.contract_display_name ?? "").trim() ||
        "Lease contract.pdf";
      base.push({
        id: CONTRACT_DOC_ID,
        docId: CONTRACT_DOC_ID,
        fileName: contractName,
        renamable: true,
        name: contractName,
        type: "Contract",
        group: "Contract",
        status: isBookingContractSigned(booking) ? "signed" : "sent",
        meta: booking.contract_signed_at
          ? `signed ${formatDate(booking.contract_signed_at) ?? ""}`
          : "sent",
        url: contractUrl,
      });
    }
    const seen = new Set<string>();
    // File attachments on admin notes. This view is admin-only, so an internal
    // note's file belongs here — but it is filed under what wrote it, not all
    // lumped into Handover:
    //
    //   'general'  → an attachment somebody added in the notes drawer. Its own
    //                group, and its url is a PRIVATE bucket path, not a link.
    //   otherwise  → the key-pickup / key-return / contract upload audit rows,
    //                which store a public tenant-documents URL.
    //
    // Nothing is copied into tenant_documents to achieve this: that table is
    // readable by the tenant, and an internal note's attachment is not.
    for (const n of adminNotes || []) {
      if (!n.file_url || seen.has(n.file_url)) continue;
      seen.add(n.file_url);
      const internal = n.kind === "general";
      base.push({
        id: `${DOC_NOTE_PREFIX}${n.id}`,
        name: n.file_name || "Attachment",
        type: internal ? "Internal note" : "Handover / note",
        group: internal ? "Internal notes" : "Handover",
        status: "uploaded",
        meta: n.created_at ? `uploaded ${formatDate(n.created_at) ?? ""}` : "",
        url: n.file_url,
        // admin_notes attachments are rename/delete-able too — routed via
        // resolveDocTarget() in renameDocument/deleteDocument. Not `notable`:
        // the note's own body text is edited in the Notes section, not via
        // this card's note editor.
        editable: true,
        notable: false,
        docId: `${DOC_NOTE_PREFIX}${n.id}`,
        fileUrl: n.file_url,
        // Every note file is signed on click: an internal note's private
        // admin-notes path, and the public tenant-documents URL the handover
        // rows store (that bucket is going private, COL-436).
        // noteAttachmentHref handles both.
        privateNotePath: true,
        noteId: internal ? String(n.id) : undefined,
        fileName: n.file_name || "Attachment",
      });
    }
    // Filed against the tenancy. Read-only here: rename/delete on this tab are
    // routed through resolveDocTarget to tenant_documents or admin_notes, and
    // an apartment_documents row is neither — offering a button that cannot
    // work is worse than not offering it.
    for (const f of filedDocuments || []) {
      base.push({
        id: `filed-${f.id}`,
        name: f.title || f.file_name || "Document",
        // The badge answers the question this tab could not: WHICH side put
        // this here. A property with several owners makes "from the owner"
        // useless on its own, so the name goes in the sub-line below.
        type: f.source === "owner" ? "Owner upload" : "coliviq upload",
        group: "Filed on tenancy",
        status: "filed",
        meta: [
          f.source === "owner" ? f.uploader_name : null,
          // Full timestamp, matching the property/apartment/room panels: who
          // and exactly when are the two things staff check on an owner upload.
          f.created_at ? formatDateTime(f.created_at) : null,
        ]
          .filter(Boolean)
          .join(" · "),
        // A bare path in the contracts bucket; FileCard signs it on click.
        url: f.file_url,
        editable: false,
        notable: false,
        fileName: f.file_name || f.title || "Document",
      });
    }
    for (const c of issueComments || []) {
      const { atts } = decodeChat(c.comment);
      for (const a of atts) {
        if (!a.url || seen.has(a.url)) continue;
        seen.add(a.url);
        base.push({
          id: `chat-${a.url}`,
          name: a.name || "Attachment",
          type: "Support chat",
          group: "Support chat",
          status: "shared",
          meta: c.created_at ? `shared ${formatDate(c.created_at) ?? ""}` : "",
          url: a.url,
        });
      }
    }
    return base;
  }, [
    documents,
    filedDocuments,
    booking,
    issueComments,
    adminNotes,
    customLabelsMap,
    // The translated document-type badges are baked into these rows, so the
    // memo must follow the language. `t` is a fresh function every render, which
    // is why the language it speaks is the dependency, not `t` itself.
    language,
  ]);

  // Who last saved each document's note, and when. Primary source is the
  // stored `notes_updated_at` / `notes_updated_by` pair; the audit log is a
  // fallback that fills in the author for rows the backfill could only date
  // (it knew WHEN the row was last touched, never by whom).
  const noteMetaByDoc = useMemo(() => {
    const m: Record<string, string> = {};
    for (const a of auditLogs || []) {
      if (a?.resource_type !== "tenant_document") continue;
      const action = String(a?.action ?? "");
      if (!action.startsWith("tenant_document_note_")) continue;
      if (action === "tenant_document_note_cleared") continue;
      const id = String(a.resource_id ?? "");
      if (!id || m[id]) continue;
      const who = a?.actor?.name || a?.actor?.email || "Admin";
      const when = a?.created_at ? formatDateTime(a.created_at) : null;
      m[id] = when ? `${who} · ${when}` : who;
    }
    return m;
  }, [auditLogs]);

  /** Stored attribution wins; audit fallback fills the gaps; date-only if all
   *  we ever recorded was the timestamp. */
  const noteMetaFor = (d: {
    docId?: string;
    noteAt?: string | null;
    noteBy?: string | null;
  }): string | null => {
    if (d.noteBy) {
      const when = d.noteAt ? formatDateTime(d.noteAt) : null;
      return when ? `${d.noteBy} · ${when}` : d.noteBy;
    }
    const fromAudit = d.docId ? noteMetaByDoc[d.docId] : null;
    if (fromAudit) return fromAudit;
    if (d.noteAt) return `edited ${formatDateTime(d.noteAt) ?? ""}`.trim();
    return null;
  };

  const groups = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const d of all) counts[d.group] = (counts[d.group] || 0) + 1;
    return ["All", ...Object.keys(counts)].map((g) => ({
      key: g,
      count: g === "All" ? all.length : counts[g],
    }));
  }, [all]);

  const items = useMemo(() => {
    const q = query.trim().toLowerCase();
    return all.filter(
      (d) =>
        (group === "All" || d.group === group) &&
        (!q || `${d.name} ${d.type} ${d.status}`.toLowerCase().includes(q))
    );
  }, [all, group, query]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div
        style={{
          background: "#fff",
          border: "1px solid #ECEEF1",
          borderRadius: 20,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "18px 24px",
            borderBottom: "1px solid #F0F1F3",
            background: "#FBFCFD",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 11,
              flexShrink: 0,
            }}
          >
            <SectionIcon bg="#EAF1FE" color="#3B82F6" size={30} radius={9}>
              <FileIcon size={16} />
            </SectionIcon>
            <span style={{ fontSize: 15, fontWeight: 700 }}>
              {t("admin.tenantTabs.documentsHeader", "Documents")}
            </span>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 22,
                height: 22,
                borderRadius: 999,
                background: "#EAF7F5",
                color: "#0B7268",
                fontSize: 12,
                fontWeight: 700,
                padding: "0 7px",
              }}
            >
              {items.length}
            </span>
          </div>
          <div
            style={{
              flex: 1,
              position: "relative",
              maxWidth: 320,
              marginLeft: "auto",
            }}
          >
            <span
              style={{
                position: "absolute",
                left: 12,
                top: "50%",
                transform: "translateY(-50%)",
                color: "#94A3B8",
                pointerEvents: "none",
                display: "flex",
              }}
            >
              <SearchIcon />
            </span>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search documents…"
              aria-label={t(
                "admin.tenantTabs.searchDocumentsAria",
                "Search documents"
              )}
              style={{
                width: "100%",
                border: "1px solid #E7E9EC",
                borderRadius: 10,
                padding: "9px 12px 9px 34px",
                fontSize: 13.5,
                background: "#fff",
                color: "#0F172A",
              }}
            />
          </div>
        </div>

        {/* group filter chips */}
        {all.length > 0 && (
          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              padding: "12px 16px 0",
            }}
          >
            {groups.map((g) => {
              const active = group === g.key;
              return (
                <button
                  key={g.key}
                  onClick={() => setGroup(g.key)}
                  style={{
                    border: `1px solid ${active ? "#12B5A8" : "#E7E9EC"}`,
                    background: active ? "#E4F7F4" : "#fff",
                    color: active ? "#0B7268" : "#64748B",
                    borderRadius: 999,
                    padding: "5px 12px",
                    fontSize: 12.5,
                    fontWeight: 600,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  {g.key}
                  <span style={{ fontSize: 11, opacity: 0.7 }}>{g.count}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Upload area (feedback #17) — files land in this tenant's Documents. */}
        {canUpload && (
          <div style={{ padding: "12px 16px 0" }}>
            <input
              ref={uploadRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
              style={{ display: "none" }}
              onChange={(e) => {
                const f = e.currentTarget.files?.[0];
                if (f) startUpload(f);
                e.currentTarget.value = "";
              }}
            />
            <div
              onClick={() => !busy && uploadRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                const f = e.dataTransfer.files?.[0];
                if (f) startUpload(f);
              }}
              style={{
                border: `1.5px dashed ${dragOver ? "#12B5A8" : "#D8DEE6"}`,
                background: dragOver ? "#E4F7F4" : "#FBFCFD",
                borderRadius: 14,
                padding: "14px 16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                cursor: busy ? "default" : "pointer",
                flexWrap: "wrap",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                <SectionIcon bg="#EAF1FE" color="#3B82F6" size={34} radius={10}>
                  {busy ? (
                    <svg
                      className="animate-spin"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                    >
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                  ) : (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />
                    </svg>
                  )}
                </SectionIcon>
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: 700 }}>
                    {busy ? "Uploading…" : "Upload a document"}
                  </div>
                  <div
                    style={{ fontSize: 12, color: "#94A3B8", fontWeight: 500 }}
                  >
                    Click or drop a file — saved to this tenant profile.
                  </div>
                </div>
              </div>
              <div
                style={{ display: "flex", alignItems: "center", gap: 8 }}
                onClick={(e) => e.stopPropagation()}
              >
                <select
                  value={uploadType}
                  onChange={(e) => {
                    if (e.target.value === NEW_LABEL_VALUE) {
                      setCreatingLabel(true);
                      return;
                    }
                    setUploadType(e.target.value);
                  }}
                  aria-label={t(
                    "admin.tenantTabs.documentTypeAria",
                    "Document type"
                  )}
                  style={{
                    height: 34,
                    borderRadius: 9,
                    border: "1px solid #E7E9EC",
                    background: "#fff",
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: "#334155",
                    padding: "0 8px",
                  }}
                >
                  {docTypeOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                  {onCreateLabel && (
                    <option value={NEW_LABEL_VALUE}>+ New label</option>
                  )}
                </select>
                <button
                  onClick={() => !busy && uploadRef.current?.click()}
                  disabled={busy}
                  style={{
                    background: "#12B5A8",
                    color: "#fff",
                    border: "none",
                    borderRadius: 9,
                    padding: "8px 14px",
                    fontSize: 12.5,
                    fontWeight: 700,
                    cursor: busy ? "default" : "pointer",
                    opacity: busy ? 0.7 : 1,
                  }}
                >
                  Choose file
                </button>
              </div>
            </div>
          </div>
        )}

        {loading && all.length === 0 ? (
          <div
            style={{
              padding: 16,
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
            }}
          >
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  gap: 12,
                  padding: "14px 16px",
                  border: "1px solid #EDEFF2",
                  borderRadius: 14,
                }}
              >
                <Skeleton h={38} w={38} style={{ borderRadius: 10 }} />
                <div style={{ flex: 1 }}>
                  <Skeleton h={13} w="70%" />
                  <Skeleton h={11} w="40%" style={{ marginTop: 8 }} />
                </div>
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div
            style={{
              padding: "36px 24px",
              textAlign: "center",
              color: "#94A3B8",
              fontSize: 13.5,
              fontWeight: 500,
            }}
          >
            {query
              ? `No documents match "${query}".`
              : "No documents uploaded yet."}
          </div>
        ) : (
          <div
            style={{
              padding: 16,
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
            }}
          >
            {items.map((d) => (
              <FileCard
                key={d.id}
                name={d.name}
                sub={d.meta}
                url={d.url}
                privateNotePath={d.privateNotePath}
                signedDocPath={d.signedDocPath}
                // No stored value is an <img src> any more — every bucket
                // behind this tab is private or going private, so an image
                // card gets a signed thumbnail instead (COL-436).
                signThumb={isImageName(d.name)}
                note={d.note}
                noteMeta={noteMetaFor(d)}
                onRename={
                  ((d.editable || d.renamable) && onRenameDocument) ||
                  (d.noteId && onRenameNoteAttachment)
                    ? () => setRenameTarget(d)
                    : undefined
                }
                onNote={
                  d.notable && onSaveDocumentNote
                    ? () => setNoteTarget(d)
                    : undefined
                }
                onDelete={
                  d.editable && onDeleteDocument
                    ? () => setDeleteTarget(d)
                    : undefined
                }
                onEditLabel={
                  d.editable && onEditDocumentLabel
                    ? () => setLabelTarget(d)
                    : undefined
                }
                badges={
                  <>
                    <span
                      style={{
                        fontSize: 10.5,
                        fontWeight: 600,
                        color: "#64748B",
                        background: "#F1F3F6",
                        border: "1px solid #E7E9EC",
                        borderRadius: 999,
                        padding: "1px 7px",
                      }}
                    >
                      {d.type}
                    </span>
                    <span style={docStatusChip(d.status)}>{d.status}</span>
                  </>
                }
              />
            ))}
          </div>
        )}
      </div>

      {renameTarget && (
        <DocTextModal
          title={t("admin.tenantTabs.renameDocumentTitle", "Rename document")}
          label={t("admin.tenantTabs.fileNameLabel", "File name")}
          initial={renameTarget.fileName || ""}
          submitLabel={t("admin.tenantTabs.saveButton", "Save")}
          multiline={false}
          required
          busy={busy}
          onClose={() => setRenameTarget(null)}
          onSubmit={(v) => {
            // Two different rows can be behind this card — a tenant_documents
            // upload, or an internal note's attachment.
            if (renameTarget.noteId) {
              onRenameNoteAttachment?.(renameTarget.noteId, v);
            } else {
              onRenameDocument?.(renameTarget.docId, v);
            }
            setRenameTarget(null);
          }}
        />
      )}
      {noteTarget && (
        <DocTextModal
          title={
            noteTarget.note
              ? t(
                  "admin.tenantTabs.editDocumentNoteTitle",
                  "Edit document note"
                )
              : t("admin.tenantTabs.addDocumentNoteTitle", "Add document note")
          }
          label={t("admin.tenantTabs.noteLabel", "Note")}
          hint={`${t("admin.tenantTabs.noteHintPrefix", "Saved on")} “${noteTarget.fileName || noteTarget.name}”. ${t("admin.tenantTabs.noteHintSuffix", "It shows on the document card here and is recorded in the Activity timeline on the Overview tab. Leave empty to remove the note.")}`}
          placeholder={t(
            "admin.tenantTabs.noteHintPlaceholder",
            "e.g. Signed copy — original collected at handover."
          )}
          initial={noteTarget.note || ""}
          submitLabel={t("admin.tenantTabs.saveNoteButton", "Save note")}
          multiline
          required={false}
          busy={busy}
          onClose={() => setNoteTarget(null)}
          onSubmit={(v) => {
            onSaveDocumentNote?.(noteTarget.docId, v);
            setNoteTarget(null);
          }}
        />
      )}
      {deleteTarget && (
        <DocTextModal
          title={t("admin.tenantTabs.deleteDocumentTitle", "Delete document")}
          label={t(
            "admin.tenantTabs.reasonForDeletionLabel",
            "Reason for deletion"
          )}
          hint={
            deleteTarget.docId?.startsWith(DOC_NOTE_PREFIX)
              ? t(
                  "admin.tenantTabs.deleteNoteHint",
                  "This also removes the underlying note and its key-handover history entry, not just the file."
                )
              : undefined
          }
          placeholder={t(
            "admin.tenantTabs.deleteReasonPlaceholder",
            "Why is this document being deleted? (recorded in the audit log)"
          )}
          initial=""
          submitLabel={t(
            "admin.tenantTabs.deleteDocumentButton",
            "Delete document"
          )}
          destructive
          multiline
          required
          busy={busy}
          onClose={() => setDeleteTarget(null)}
          onSubmit={(reason) => {
            onDeleteDocument?.(
              {
                id: deleteTarget.docId,
                file_url: deleteTarget.fileUrl,
                file_name: deleteTarget.fileName,
              },
              reason
            );
            setDeleteTarget(null);
          }}
        />
      )}
      {labelTarget && (
        <DocLabelModal
          initial={labelTarget.docType || "other"}
          options={docTypeOptions}
          busy={busy}
          onClose={() => setLabelTarget(null)}
          onSubmit={(docType) => {
            onEditDocumentLabel?.(labelTarget.docId, docType);
            setLabelTarget(null);
          }}
        />
      )}
      {creatingLabel && (
        <DocTextModal
          title={t("admin.tenantTabs.newLabelTitle", "New label")}
          label={t("admin.tenantTabs.newLabelFieldLabel", "Label")}
          hint={t(
            "admin.tenantTabs.newLabelHint",
            "Added to this dropdown for every tenant, not just this document."
          )}
          placeholder={t(
            "admin.tenantTabs.newLabelPlaceholder",
            "e.g. Key list"
          )}
          initial=""
          submitLabel={t("admin.tenantTabs.createLabelButton", "Create label")}
          multiline={false}
          required
          busy={busy}
          onClose={() => setCreatingLabel(false)}
          onSubmit={async (v) => {
            try {
              await onCreateLabel?.(v);
              setCreatingLabel(false);
            } catch (e) {
              toast.error(
                e instanceof Error
                  ? e.message
                  : t(
                      "admin.tenantTabs.createLabelFailed",
                      "Failed to create label."
                    )
              );
            }
          }}
        />
      )}
    </div>
  );
}

/** Change a document's label — same fixed + custom options as the upload dropdown. */
function DocLabelModal({
  initial,
  options,
  busy,
  onClose,
  onSubmit,
}: {
  initial: string;
  options: Array<{ value: string; label: string }>;
  busy: boolean;
  onClose: () => void;
  onSubmit: (value: string) => void;
}) {
  const { t } = useLanguage();
  const [value, setValue] = useState(initial);
  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o && !busy) onClose();
      }}
    >
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>
            {t("admin.tenantTabs.editLabelTitle", "Edit label")}
          </DialogTitle>
        </DialogHeader>
        <div>
          <div style={{ ...uppLabel, marginBottom: 6 }}>
            {t("admin.tenantTabs.newLabelFieldLabel", "Label")}
          </div>
          <select
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            style={{
              width: "100%",
              border: "1px solid #E7E9EC",
              borderRadius: 10,
              padding: "10px 12px",
              fontSize: 13.5,
              color: "#0F172A",
              fontFamily: "inherit",
              outline: "none",
              background: "#fff",
            }}
          >
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <DialogFooter>
          <button
            onClick={onClose}
            disabled={busy}
            style={{
              border: "1px solid #E3E6EA",
              background: "#fff",
              color: "#64748B",
              borderRadius: 10,
              padding: "8px 14px",
              fontSize: 13,
              fontWeight: 600,
              cursor: busy ? "default" : "pointer",
            }}
          >
            {t("admin.tenantTabs.cancelButton", "Cancel")}
          </button>
          <button
            onClick={() => !busy && onSubmit(value)}
            disabled={busy}
            style={{
              border: "none",
              background: "#12B5A8",
              color: "#fff",
              borderRadius: 10,
              padding: "8px 16px",
              fontSize: 13,
              fontWeight: 700,
              cursor: busy ? "default" : "pointer",
              opacity: busy ? 0.6 : 1,
            }}
          >
            {t("admin.tenantTabs.saveButton", "Save")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Small modal for document actions: rename (single-line, required), add note
 * (multiline, optional), and delete-with-reason (multiline, required,
 * destructive). Manages its own input so it mounts fresh from `initial`.
 */
function DocTextModal({
  title,
  label,
  hint,
  initial,
  placeholder,
  submitLabel,
  multiline,
  required,
  destructive,
  busy,
  onClose,
  onSubmit,
}: {
  title: string;
  label: string;
  /** Explains where the saved value ends up, shown under the field. */
  hint?: string;
  initial: string;
  placeholder?: string;
  submitLabel: string;
  multiline: boolean;
  required: boolean;
  destructive?: boolean;
  busy: boolean;
  onClose: () => void;
  onSubmit: (value: string) => void;
}) {
  const { t } = useLanguage();
  const [value, setValue] = useState(initial);
  const canSubmit = !busy && (!required || value.trim().length > 0);
  const fieldStyle: React.CSSProperties = {
    width: "100%",
    border: "1px solid #E7E9EC",
    borderRadius: 10,
    padding: "10px 12px",
    fontSize: 13.5,
    color: "#0F172A",
    fontFamily: "inherit",
    outline: "none",
    background: "#fff",
  };
  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o && !busy) onClose();
      }}
    >
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div>
          <div style={{ ...uppLabel, marginBottom: 6 }}>{label}</div>
          {multiline ? (
            <textarea
              autoFocus
              value={value}
              placeholder={placeholder}
              onChange={(e) => setValue(e.target.value)}
              style={{ ...fieldStyle, minHeight: 96, resize: "vertical" }}
            />
          ) : (
            <input
              autoFocus
              value={value}
              placeholder={placeholder}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && canSubmit) onSubmit(value.trim());
              }}
              style={fieldStyle}
            />
          )}
          {hint && (
            <div
              style={{
                marginTop: 8,
                fontSize: 12,
                lineHeight: 1.45,
                color: "#64748B",
                fontWeight: 500,
              }}
            >
              {hint}
            </div>
          )}
        </div>
        <DialogFooter>
          <button
            onClick={onClose}
            disabled={busy}
            style={{
              border: "1px solid #E3E6EA",
              background: "#fff",
              color: "#64748B",
              borderRadius: 10,
              padding: "8px 14px",
              fontSize: 13,
              fontWeight: 600,
              cursor: busy ? "default" : "pointer",
            }}
          >
            {t("admin.tenantTabs.cancelButton", "Cancel")}
          </button>
          <button
            onClick={() => canSubmit && onSubmit(value.trim())}
            disabled={!canSubmit}
            style={{
              border: "none",
              background: destructive ? "#DC2626" : "#12B5A8",
              color: "#fff",
              borderRadius: 10,
              padding: "8px 16px",
              fontSize: 13,
              fontWeight: 700,
              cursor: canSubmit ? "pointer" : "default",
              opacity: canSubmit ? 1 : 0.6,
            }}
          >
            {submitLabel}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ================================================================== */
/* Shared building blocks                                              */
/* ================================================================== */

interface EmailRow {
  id: string;
  title: string;
  desc: string;
  sent: boolean;
  time: string | null;
  subject: string;
  trigger: string;
}

function EmailSequence({
  title,
  emails,
  recipient,
}: {
  title: string;
  emails: EmailRow[];
  recipient: string;
}) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const sentCount = emails.filter((e) => e.sent).length;
  return (
    <CollapsibleCard
      iconBg="#EAF1FE"
      iconColor="#3B82F6"
      icon={<MailIcon size={19} />}
      title={title}
      subtitle={`${sentCount} ${t("admin.tenantTabs.ofUnit", "of")} ${emails.length} ${t("admin.tenantTabs.sentCountUnit", "sent")}`}
      open={open}
      onToggle={() => setOpen((v) => !v)}
    >
      {emails.map((e) => (
        <EmailRowItem key={e.id} email={e} recipient={recipient} />
      ))}
    </CollapsibleCard>
  );
}

function EmailRowItem({
  email: e,
  recipient,
}: {
  email: EmailRow;
  recipient: string;
}) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  return (
    <div style={{ borderBottom: "1px solid #F5F6F7" }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 14,
          padding: "14px 24px",
          background: "none",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: 9,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: e.sent ? "#E6F7EF" : "#F1F3F6",
            color: e.sent ? "#10B981" : "#94A3B8",
            flexShrink: 0,
          }}
        >
          {e.sent ? (
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
          ) : (
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="9" />
              <path d="M12 8v4" />
            </svg>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{e.title}</div>
          <div
            style={{
              fontSize: 12,
              color: "#94A3B8",
              fontWeight: 500,
              marginTop: 1,
            }}
          >
            {e.desc}
          </div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <span style={e.sent ? CHIP.green : CHIP.slate}>
            {e.sent
              ? t("admin.tenantTabs.emailSentValue", "Sent")
              : t("admin.tenantTabs.pendingChip", "Pending")}
          </span>
          <div
            style={{
              fontSize: 11.5,
              color: "#94A3B8",
              fontWeight: 500,
              marginTop: 4,
            }}
          >
            {e.sent
              ? (e.time ?? t("admin.tenantTabs.emailSentValue", "Sent"))
              : t("admin.tenantTabs.emailNotSentValue", "Not sent yet")}
          </div>
        </div>
        <Chevron open={open} size={15} />
      </button>
      {open && (
        <div style={{ padding: "0 24px 16px 68px" }}>
          <div style={{ ...innerBox, padding: "14px 16px" }}>
            {[
              [t("admin.tenantTabs.emailSubjectLabel", "Subject"), e.subject],
              [t("admin.tenantTabs.recipientLabel", "Recipient"), recipient],
              [t("admin.tenantTabs.emailTriggerLabel", "Trigger"), e.trigger],
              e.sent
                ? [
                    t("admin.tenantTabs.emailDeliveredLabel", "Delivered"),
                    e.time ?? t("admin.tenantTabs.emailSentValue", "Sent"),
                  ]
                : [
                    t("admin.tenantTabs.emailStatusLabel", "Status"),
                    `${t("admin.tenantTabs.queuedSendsOnPrefix", "Queued — sends on")} ${e.trigger.toLowerCase()}`,
                  ],
            ].map(([label, value]) => (
              <div
                key={label}
                style={{ display: "flex", gap: 12, padding: "5px 0" }}
              >
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: ".04em",
                    color: "#94A3B8",
                    textTransform: "uppercase",
                    width: 96,
                    flexShrink: 0,
                  }}
                >
                  {label}
                </div>
                <div
                  style={{
                    fontSize: 13.5,
                    fontWeight: 500,
                    color: "#334155",
                    flex: 1,
                  }}
                >
                  {value}
                </div>
              </div>
            ))}
            {e.sent && (
              <div
                style={{
                  marginTop: 10,
                  paddingTop: 12,
                  borderTop: "1px solid #EDEFF2",
                  display: "flex",
                  gap: 8,
                }}
              >
                <button
                  onClick={() => setPreviewOpen(true)}
                  style={emailActionBtn("#0E7490")}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                  {t("admin.tenantTabs.viewEmailButton", "View email")}
                </button>
                <button
                  onClick={() =>
                    toast.message(
                      t(
                        "admin.tenantTabs.manualResendNotWired",
                        "Manual resend isn't wired up yet — coming in a later pass."
                      )
                    )
                  }
                  style={emailActionBtn("#64748B")}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 12a9 9 0 1 1-3-6.7L21 8" />
                    <path d="M21 3v5h-5" />
                  </svg>
                  {t("admin.tenantTabs.resendButton", "Resend")}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {previewOpen && (
        <EmailPreviewModal
          email={e}
          recipient={recipient}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * Focused "View email" preview. The app doesn't archive the rendered HTML body
 * of sent emails, so this shows the delivery record (subject, recipient,
 * trigger, time) with a clear note. Full-body preview needs a backend source
 * (store the rendered HTML on send, or a render endpoint).
 */
function EmailPreviewModal({
  email: e,
  recipient,
  onClose,
}: {
  email: EmailRow;
  recipient: string;
  onClose: () => void;
}) {
  const { t } = useLanguage();
  const rows: Array<[string, string]> = [
    [t("admin.tenantTabs.emailSubjectLabel", "Subject"), e.subject || e.title],
    [t("admin.tenantTabs.emailToLabel", "To"), recipient || "—"],
    [t("admin.tenantTabs.emailTriggerLabel", "Trigger"), e.trigger || "—"],
    [
      t("admin.tenantTabs.emailStatusLabel", "Status"),
      e.sent
        ? t("admin.tenantTabs.emailSentValue", "Sent")
        : t("admin.tenantTabs.emailNotSentValue", "Not sent yet"),
    ],
    [
      t("admin.tenantTabs.emailDeliveredLabel", "Delivered"),
      e.time ? (formatDateTime(e.time) ?? "—") : "—",
    ],
  ];
  return (
    <div
      onClick={onClose}
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 40,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(9,15,28,.55)",
        backdropFilter: "blur(2px)",
        padding: 24,
      }}
    >
      <div
        onClick={(ev) => ev.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 520,
          background: "#fff",
          borderRadius: 20,
          boxShadow: "0 30px 80px -20px rgba(9,15,28,.5)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "18px 24px",
            borderBottom: "1px solid #F0F1F3",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <SectionIcon bg="#EAF1FE" color="#3B82F6" size={34} radius={10}>
            <MailIcon size={17} />
          </SectionIcon>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{e.title}</div>
        </div>
        <div style={{ padding: "18px 24px" }}>
          {rows.map(([label, value]) => (
            <div
              key={label}
              style={{
                display: "grid",
                gridTemplateColumns: "110px 1fr",
                gap: 12,
                padding: "7px 0",
              }}
            >
              <div style={uppLabel}>{label}</div>
              <div
                style={{ fontSize: 13.5, fontWeight: 500, color: "#334155" }}
              >
                {value}
              </div>
            </div>
          ))}
          <div
            style={{
              marginTop: 12,
              padding: "10px 12px",
              borderRadius: 12,
              background: "#F8FAFC",
              border: "1px solid #EDEFF2",
              fontSize: 12.5,
              color: "#94A3B8",
              fontWeight: 500,
            }}
          >
            {t(
              "admin.tenantTabs.emailBodyNotArchived",
              "The rendered email body isn't archived in the app yet — this shows the delivery record."
            )}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginTop: 16,
              flexWrap: "wrap",
            }}
          >
            {recipient && recipient !== "—" && (
              <>
                <button
                  onClick={() => {
                    navigator.clipboard?.writeText(recipient).catch(() => {});
                    toast.success(
                      t(
                        "admin.tenantTabs.recipientCopiedToast",
                        "Recipient copied."
                      )
                    );
                  }}
                  style={emailActionBtn("#0E7490")}
                >
                  {t("admin.tenantTabs.copyRecipientButton", "Copy recipient")}
                </button>
                <a
                  href={`mailto:${recipient}?subject=${encodeURIComponent(e.subject || e.title)}`}
                  style={{
                    ...emailActionBtn("#0E7490"),
                    textDecoration: "none",
                  }}
                >
                  {t("admin.tenantTabs.openInMailButton", "Open in mail…")}
                </a>
              </>
            )}
            <button
              onClick={onClose}
              style={{
                marginLeft: "auto",
                background: "#0F172A",
                color: "#fff",
                border: "none",
                borderRadius: 11,
                padding: "9px 18px",
                fontWeight: 700,
                fontSize: 13.5,
                cursor: "pointer",
              }}
            >
              {t("admin.tenantTabs.closeButton", "Close")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function emailActionBtn(color: string): React.CSSProperties {
  return {
    border: "1px solid #E7E9EC",
    background: "#fff",
    color,
    borderRadius: 9,
    padding: "7px 12px",
    fontSize: 12.5,
    fontWeight: 600,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
  };
}

// Shown when a KeyNest pickup has no custom instructions set on the room /
// apartment / property — the standard KeyNest handover guidance.
const DEFAULT_KEYNEST_INSTRUCTIONS =
  'Go to the cashier and ask for "KeyNest" key pick-up/drop-off. You will then need to give them the code you were provided with.';

// <input type="datetime-local"> wants "YYYY-MM-DDTHH:mm" in local time, not ISO.
function toDatetimeLocalValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function KeysSection({
  mode,
  room,
  booking,
  done,
  can,
  doneAt,
  busy,
  onMark,
  onUploadDoc,
  onSaveNote,
  adminNotes = [],
  canManage = false,
}: {
  mode: "pickup" | "return";
  room: Record<string, any> | null;
  booking: Record<string, any>;
  done: boolean;
  can: boolean;
  doneAt: string | null;
  busy: boolean;
  onMark: (dateIso?: string) => void;
  onUploadDoc: (file: File) => void;
  onSaveNote: (note: string) => Promise<void>;
  /** admin_notes rows (text notes + uploaded documents) shown as history. */
  adminNotes?: any[];
  /** Admin or super-admin: may backdate the pickup/return timestamp. The
   *  plain mark-as-done action itself stays gated by `can` alone (e.g. the
   *  key_pickup_pending escape hatch is intentionally available to more
   *  than just managers) — this only gates the editable-timestamp override. */
  canManage?: boolean;
}) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  // Defaults to now, but stays editable so a pickup/return recorded after the
  // fact can be backdated to when it actually happened instead of the click
  // time. `keyDateDraftTouched` tracks whether the admin actually edited the
  // field — the dialog can stay open long after mount, so an untouched
  // confirmation must still stamp the click time rather than this initial
  // value.
  const [keyDateDraft, setKeyDateDraft] = useState(() =>
    toDatetimeLocalValue(new Date())
  );
  const [keyDateDraftTouched, setKeyDateDraftTouched] = useState(false);
  // Lets a manager fix an already-submitted pickup/return timestamp (e.g. an
  // accidental entry) after the fact, separately from the pre-submit draft
  // above. Editing reuses the same clamp-to-not-future logic via `onMark`,
  // which already just overwrites the stored timestamp regardless of whether
  // one exists yet.
  const [editingDoneAt, setEditingDoneAt] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const statusChip = done ? CHIP.green : can ? CHIP.cyan : CHIP.slate;
  const statusText = done
    ? mode === "return"
      ? "Returned"
      : "Picked up"
    : can
      ? "Action needed"
      : "Pending";
  const shop = room?.keynest_partner_shop_name || "—";
  // KeyNest values can be inherited property→apartment→room (see
  // resolveRoomKeynest); surface where an inherited value came from.
  const keynestSources = (room?.__keynestSources ?? {}) as Record<
    string,
    "room" | "apartment" | "property" | null
  >;
  const inheritedHint = (field: string): string | null => {
    const src = keynestSources[field];
    return src === "apartment"
      ? "Inherited from apartment"
      : src === "property"
        ? "Inherited from property"
        : null;
  };
  // Manual pickup (non-KeyNest): the codes/shop/opening-hours don't apply, so
  // show just the pickup address instead of the KeyNest grid.
  const isManualPickup =
    String(room?.pickup_method || "").toLowerCase() === "manual";
  // Surfaced on the collapsed Keys header so admins see the state at a glance.
  const anyKeynestInherited = [
    "partner_shop_name",
    "partner_shop_address",
    "access_url",
    "pickup_method",
  ].some(
    (f) => keynestSources[f] === "apartment" || keynestSources[f] === "property"
  );
  const keysSummarySuffix = isManualPickup
    ? " · Manual pickup"
    : anyKeynestInherited
      ? " · Inherited defaults"
      : "";
  // `keynest_collection_code` is a BOOKINGS column (the tenant-specific pickup
  // code they actually received) — prefer it, matching the Overview timeline.
  // `room.keynest_collection_code` doesn't exist, so relying on it would show
  // the static room code or "—".
  const code =
    booking?.keynest_collection_code || room?.keynest_pickup_code || "—";

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #ECEEF1",
        borderRadius: 20,
        overflow: "hidden",
      }}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 13,
          padding: "22px 24px",
          background: "none",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <SectionIcon bg="#E5FAFD" color="#0891B2" size={40}>
          <KeyIcon />
        </SectionIcon>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>
            {t("admin.tenantTabs.keysHeader", "Keys")}
          </div>
          <div
            style={{
              fontSize: 12.5,
              color: "#94A3B8",
              fontWeight: 500,
              marginTop: 2,
            }}
          >
            KeyNest pickup details & key handover tracking
            {keysSummarySuffix}
          </div>
        </div>
        <span style={statusChip}>{statusText}</span>
        <Chevron open={open} />
      </button>
      {open && (
        <div style={{ padding: "0 24px 22px" }}>
          {/* keynest details */}
          <div
            style={{
              border: "1px solid #EDEFF2",
              borderRadius: 14,
              overflow: "hidden",
              marginBottom: 14,
            }}
          >
            <button
              onClick={() => setDetailsOpen((v) => !v)}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 11,
                padding: "13px 16px",
                background: "#F6F7F9",
                border: "none",
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              <SectionIcon bg="#fff" color="#0891B2" size={30} radius={9}>
                <PinIcon size={15} />
              </SectionIcon>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700 }}>
                  {t(
                    "admin.tenantTabs.keynestPickupDetails",
                    "KeyNest pickup details"
                  )}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "#94A3B8",
                    fontWeight: 500,
                    marginTop: 1,
                  }}
                >
                  {shop} · code {code}
                </div>
              </div>
              <Chevron open={detailsOpen} size={18} />
            </button>
            {detailsOpen && (
              <div
                style={{
                  background: "#F6F7F9",
                  borderTop: "1px solid #EDEFF2",
                  padding: "16px 18px",
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "14px 24px",
                }}
              >
                {isManualPickup ? (
                  <>
                    <div style={{ gridColumn: "1 / -1" }}>
                      <KeyField
                        label={t(
                          "admin.tenantTabs.pickupAddressLabel",
                          "Pickup address"
                        )}
                        value={room?.keynest_partner_shop_address || "—"}
                        hint={inheritedHint("partner_shop_address")}
                      />
                    </div>
                    <div style={{ gridColumn: "1 / -1" }}>
                      <span
                        style={{
                          fontSize: 12,
                          color: "#94A3B8",
                          fontWeight: 500,
                        }}
                      >
                        {t(
                          "admin.tenantTabs.manualPickupNoCodes",
                          "Manual pickup — no KeyNest codes for this room."
                        )}
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <KeyField
                      label={t(
                        "admin.tenantTabs.pickupCodeLabel",
                        "Pickup code"
                      )}
                      value={code}
                    />
                    <KeyField
                      label={t(
                        "admin.tenantTabs.accessCodeLabel",
                        "Access code"
                      )}
                      value={room?.keynest_access_code || "—"}
                    />
                    <KeyField
                      label={t(
                        "admin.tenantTabs.partnerShopLabel",
                        "Partner shop"
                      )}
                      value={shop}
                      hint={inheritedHint("partner_shop_name")}
                    />
                    <KeyField
                      label={t("admin.tenantTabs.keyLabelLabel", "Key label")}
                      value={room?.keynest_key_label || "—"}
                    />
                    <div style={{ gridColumn: "1 / -1" }}>
                      <KeyField
                        label={t(
                          "admin.tenantTabs.shopAddressLabel",
                          "Shop address"
                        )}
                        value={room?.keynest_partner_shop_address || "—"}
                        hint={inheritedHint("partner_shop_address")}
                      />
                    </div>
                    <KeyField
                      label={t(
                        "admin.tenantTabs.openingHoursWeekdaysLabel",
                        "Opening hours (weekdays)"
                      )}
                      value={room?.keynest_opening_hours_weekdays || "—"}
                    />
                    <KeyField
                      label={t(
                        "admin.tenantTabs.openingHoursSaturdayLabel",
                        "Opening hours (Saturday)"
                      )}
                      value={room?.keynest_opening_hours_saturday || "—"}
                    />
                  </>
                )}
                {/* Instructions — a custom value inherited room → apartment →
                    property wins; otherwise KeyNest pickups fall back to the
                    standard guidance so the row is never blank. */}
                {(() => {
                  const custom = room?.pickup_instructions
                    ? String(room.pickup_instructions).trim()
                    : "";
                  const text =
                    custom ||
                    (isManualPickup
                      ? ""
                      : t(
                          "admin.tenantTabs.defaultKeynestInstructions",
                          DEFAULT_KEYNEST_INSTRUCTIONS
                        ));
                  if (!text) return null;
                  return (
                    <div style={{ gridColumn: "1 / -1" }}>
                      <KeyField
                        label={t(
                          "admin.tenantTabs.instructionsLabel",
                          "Instructions"
                        )}
                        value={text}
                        hint={
                          custom
                            ? inheritedHint("pickup_instructions")
                            : t(
                                "admin.tenantTabs.defaultKeynestInstructionsHint",
                                "Default KeyNest instructions"
                              )
                        }
                      />
                    </div>
                  );
                })()}
              </div>
            )}
          </div>

          {/* mark toggle */}
          <div style={{ ...innerBox, padding: "16px 18px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                marginBottom: 12,
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 700 }}>
                {mode === "return"
                  ? t("admin.tenantTabs.keyReturned", "Key returned")
                  : t("admin.tenantTabs.keyPickedUp", "Key picked up")}
              </div>
              <span style={statusChip}>{statusText}</span>
            </div>
            {!done && canManage && (
              <div style={{ marginBottom: 10 }}>
                <label
                  htmlFor="key-date-input"
                  style={{ ...uppLabel, marginBottom: 6, display: "block" }}
                >
                  {mode === "return"
                    ? t(
                        "admin.tenantTabs.returnDateTimeLabel",
                        "Return date/time"
                      )
                    : t(
                        "admin.tenantTabs.pickupDateTimeLabel",
                        "Pickup date/time"
                      )}
                </label>
                <input
                  id="key-date-input"
                  type="datetime-local"
                  value={keyDateDraft}
                  onChange={(e) => {
                    setKeyDateDraft(e.target.value);
                    setKeyDateDraftTouched(true);
                  }}
                  disabled={!can}
                  style={{
                    border: "1px solid #E3E6EA",
                    borderRadius: 10,
                    padding: "9px 12px",
                    fontSize: 13,
                    fontWeight: 600,
                    background: "#fff",
                    width: "100%",
                  }}
                />
              </div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => {
                  if (!can) return;
                  if (!canManage || !keyDateDraftTouched)
                    return onMark(undefined);
                  const draft = new Date(keyDateDraft);
                  const now = Date.now();
                  onMark(
                    Number.isNaN(draft.getTime())
                      ? new Date().toISOString()
                      : new Date(Math.min(draft.getTime(), now)).toISOString()
                  );
                }}
                disabled={busy || (!can && !done)}
                style={{
                  flex: 1,
                  padding: 10,
                  borderRadius: 10,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: can ? "pointer" : "default",
                  border: `1px solid ${done ? "#12B5A8" : "#E3E6EA"}`,
                  background: done ? "#12B5A8" : "#F1F3F6",
                  color: done ? "#fff" : "#0F172A",
                }}
              >
                {t("admin.tenantTabs.yesButton", "Yes")}
                {done ? " \u2713" : ""}
              </button>
              <button
                disabled
                style={{
                  flex: 1,
                  padding: 10,
                  borderRadius: 10,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "default",
                  border: "1px solid #E3E6EA",
                  background: !done ? "#F1F3F6" : "#fff",
                  color: !done ? "#0F172A" : "#94A3B8",
                }}
              >
                {t("admin.tenantTabs.noButton", "No")}
              </button>
            </div>
            {done && doneAt && !editingDoneAt && (
              <div
                style={{
                  marginTop: 10,
                  fontSize: 12.5,
                  color: "#0E7490",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <span>
                  {mode === "return"
                    ? t("admin.tenantTabs.returnedLabel", "Returned")
                    : t("admin.tenantTabs.pickedUpLabel", "Picked up")}{" "}
                  {formatDateTime(doneAt)}
                </span>
                {canManage && (
                  <button
                    onClick={() => {
                      setKeyDateDraft(toDatetimeLocalValue(new Date(doneAt)));
                      setEditingDoneAt(true);
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      fontSize: 12.5,
                      fontWeight: 600,
                      color: "#0891B2",
                      textDecoration: "underline",
                      cursor: "pointer",
                    }}
                  >
                    {t("admin.tenantTabs.editButton", "Edit")}
                  </button>
                )}
              </div>
            )}
            {done && editingDoneAt && (
              <div style={{ marginTop: 10 }}>
                <label
                  htmlFor="key-date-edit-input"
                  style={{ ...uppLabel, marginBottom: 6, display: "block" }}
                >
                  {mode === "return"
                    ? t(
                        "admin.tenantTabs.returnDateTimeLabel",
                        "Return date/time"
                      )
                    : t(
                        "admin.tenantTabs.pickupDateTimeLabel",
                        "Pickup date/time"
                      )}
                </label>
                <input
                  id="key-date-edit-input"
                  type="datetime-local"
                  value={keyDateDraft}
                  onChange={(e) => setKeyDateDraft(e.target.value)}
                  style={{
                    border: "1px solid #E3E6EA",
                    borderRadius: 10,
                    padding: "9px 12px",
                    fontSize: 13,
                    fontWeight: 600,
                    background: "#fff",
                    width: "100%",
                  }}
                />
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <button
                    onClick={() => {
                      const draft = new Date(keyDateDraft);
                      const now = Date.now();
                      onMark(
                        Number.isNaN(draft.getTime())
                          ? new Date().toISOString()
                          : new Date(
                              Math.min(draft.getTime(), now)
                            ).toISOString()
                      );
                      setEditingDoneAt(false);
                    }}
                    disabled={busy}
                    style={{
                      flex: 1,
                      padding: 9,
                      borderRadius: 10,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "pointer",
                      border: "1px solid #12B5A8",
                      background: "#12B5A8",
                      color: "#fff",
                    }}
                  >
                    {t("admin.tenantTabs.saveButton", "Save")}
                  </button>
                  <button
                    onClick={() => setEditingDoneAt(false)}
                    style={{
                      flex: 1,
                      padding: 9,
                      borderRadius: 10,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "pointer",
                      border: "1px solid #E3E6EA",
                      background: "#F1F3F6",
                      color: "#0F172A",
                    }}
                  >
                    {t("admin.tenantTabs.cancelButton", "Cancel")}
                  </button>
                </div>
              </div>
            )}

            {/* handover note */}
            <div style={{ ...uppLabel, marginTop: 16, marginBottom: 6 }}>
              {t("admin.tenantTabs.timeline.note", "Note")}
            </div>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t(
                "admin.tenantTabs.handoverNotePlaceholder",
                "Add a note — saved to the tenant profile…"
              )}
              style={{
                width: "100%",
                minHeight: 58,
                border: "1px solid #E7E9EC",
                borderRadius: 10,
                padding: "10px 12px",
                fontSize: 13,
                background: "#fff",
                color: "#0F172A",
                resize: "vertical",
                fontFamily: "inherit",
                outline: "none",
              }}
            />
            <div
              style={{
                marginTop: 10,
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <button
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                style={{
                  border: "1px dashed #C4D0DC",
                  background: "#fff",
                  color: "#0E7490",
                  borderRadius: 10,
                  padding: "8px 13px",
                  fontSize: 12.5,
                  fontWeight: 600,
                  cursor: busy ? "default" : "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 7,
                }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />
                </svg>
                {t("admin.tenantTabs.uploadDocumentButton", "Upload document")}
              </button>
              <input
                ref={fileRef}
                type="file"
                style={{ display: "none" }}
                onChange={(e) => {
                  const f = e.currentTarget.files?.[0];
                  if (f) onUploadDoc(f);
                  if (fileRef.current) fileRef.current.value = "";
                }}
              />
              {note.trim() && (
                <button
                  onClick={async () => {
                    setSavingNote(true);
                    try {
                      await onSaveNote(note.trim());
                      setNote("");
                      toast.success(
                        t(
                          "admin.tenantTabs.noteSavedToast",
                          "Note saved to the tenant profile."
                        )
                      );
                    } catch (e) {
                      toast.error(
                        e instanceof Error
                          ? e.message
                          : t(
                              "admin.tenantTabs.saveNoteFailed",
                              "Failed to save note."
                            )
                      );
                    } finally {
                      setSavingNote(false);
                    }
                  }}
                  disabled={busy || savingNote}
                  style={{
                    border: "none",
                    background: "#12B5A8",
                    color: "#fff",
                    borderRadius: 10,
                    padding: "8px 14px",
                    fontSize: 12.5,
                    fontWeight: 700,
                    cursor: busy || savingNote ? "default" : "pointer",
                  }}
                >
                  {savingNote
                    ? t("admin.tenantTabs.savingEllipsis", "Saving…")
                    : t("admin.tenantTabs.saveNoteButton", "Save note")}
                </button>
              )}
              <span style={{ fontSize: 12, color: "#94A3B8", fontWeight: 500 }}>
                {t(
                  "admin.tenantTabs.persistsPlatformWide",
                  "Persists platform-wide"
                )}
              </span>
            </div>
          </div>

          <KeyHistory notes={adminNotes} />
        </div>
      )}
    </div>
  );
}

/**
 * Notes + uploaded-documents history for the Keys section. Reads the same
 * `admin_notes` feed that the Activity Timeline uses (newest-first). Rows with
 * a file are documents; text-only rows are notes. Each entry shows who added it
 * and when, so the handover trail is visible without leaving the section.
 */
function KeyHistory({ notes = [] }: { notes?: any[] }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(true);
  const authorOf = (n: any): string =>
    n?.author?.name ||
    n?.author?.email ||
    t("admin.tenantTabs.timeline.adminFallback", "Admin");
  const withText = (n: any) => !!(n?.body && String(n.body).trim());
  const docs = notes.filter((n) => !!n?.file_url);
  const textNotes = notes.filter((n) => !n?.file_url && withText(n));
  const total = docs.length + textNotes.length;
  if (total === 0) return null;

  const metaStyle: React.CSSProperties = {
    fontSize: 12,
    color: "#94A3B8",
    fontWeight: 500,
    marginTop: 2,
  };
  const viewBtnStyle: React.CSSProperties = {
    flexShrink: 0,
    border: "1px solid #C4D0DC",
    background: "#fff",
    color: "#0E7490",
    borderRadius: 8,
    padding: "5px 12px",
    fontSize: 12,
    fontWeight: 600,
    textDecoration: "none",
    lineHeight: 1.4,
  };
  return (
    <div style={{ ...innerBox, padding: "16px 18px", marginTop: 14 }}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 8,
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 700 }}>
          {t("admin.tenantTabs.historyHeader", "History")}
        </span>
        <span
          style={{
            fontSize: 11.5,
            fontWeight: 700,
            color: "#0E7490",
            background: "#E5FAFD",
            borderRadius: 999,
            padding: "1px 8px",
          }}
        >
          {total}
        </span>
        <span style={{ marginLeft: "auto", display: "inline-flex" }}>
          <Chevron open={open} size={18} />
        </span>
      </button>

      {open && (
        <div style={{ marginTop: 12 }}>
          {docs.length > 0 && (
            <>
              <div style={{ ...uppLabel, marginBottom: 8 }}>
                {t("admin.tenantTabs.documentsHeader", "Documents")}
              </div>
              <div
                style={{ display: "flex", flexDirection: "column", gap: 10 }}
              >
                {docs.map((d) => (
                  <div
                    key={d.id}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 12,
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13.5,
                          fontWeight: 600,
                          color: "#0F172A",
                          wordBreak: "break-word",
                        }}
                      >
                        {d.file_name ||
                          t("admin.tenantTabs.timeline.document", "Document")}
                      </div>
                      {withText(d) && (
                        <div
                          style={{
                            fontSize: 13,
                            color: "#334155",
                            marginTop: 2,
                          }}
                        >
                          “{String(d.body).trim()}”
                        </div>
                      )}
                      <div style={metaStyle}>
                        {authorOf(d)}
                        {d.created_at
                          ? ` · ${formatDateTime(d.created_at) ?? ""}`
                          : ""}
                      </div>
                    </div>
                    {d.file_url && (
                      // An admin_notes file: a private admin-notes path, or
                      // the public tenant-documents URL older notes stored.
                      // Either way the link is signed on click (COL-436).
                      <button
                        type="button"
                        onClick={() =>
                          void openNoteAttachment(d.file_url, {
                            fileName: d.file_name,
                          })
                        }
                        style={{ ...viewBtnStyle, cursor: "pointer" }}
                      >
                        {t("admin.tenantTabs.viewLink", "View")}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {textNotes.length > 0 && (
            <>
              <div
                style={{
                  ...uppLabel,
                  marginTop: docs.length > 0 ? 16 : 0,
                  marginBottom: 8,
                }}
              >
                {t("admin.tenantTabs.notesHeader", "Notes")}
              </div>
              <div
                style={{ display: "flex", flexDirection: "column", gap: 10 }}
              >
                {textNotes.map((n) => (
                  <div key={n.id}>
                    <div style={{ fontSize: 13, color: "#334155" }}>
                      “{String(n.body).trim()}”
                    </div>
                    <div style={metaStyle}>
                      {authorOf(n)}
                      {n.created_at
                        ? ` · ${formatDateTime(n.created_at) ?? ""}`
                        : ""}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function KeyField({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  /** e.g. "Inherited from property" when the value isn't set on the room. */
  hint?: string | null;
}) {
  return (
    <div>
      <div style={uppLabel}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 600, marginTop: 4 }}>{value}</div>
      {hint && (
        <div
          style={{
            fontSize: 11,
            color: "#94A3B8",
            fontWeight: 500,
            marginTop: 2,
            fontStyle: "italic",
          }}
        >
          {hint}
        </div>
      )}
    </div>
  );
}

function ProtocolCard({
  title,
  subtitle,
  submitted,
  submittedAt,
  unlocked,
  expired,
  expiredLabel,
  canOverride,
  overrideLabel,
  canReopen,
  reopening,
  onReopen,
  reopened,
  manuallyClosed,
  closing,
  onClose,
  busy,
  onOverride,
  protocolId,
  protocolRef,
  protocolKind,
  hasPdf,
  onOpenPdf,
  recordedByName,
  recordedAt,
  adminNote,
  autoSubmittedAt,
  canRecord = false,
  onRecord,
  canReplace = false,
  onReplace,
}: {
  title: string;
  subtitle: string;
  submitted: boolean;
  submittedAt: string | null;
  /** The submitted protocol, when there is one — unlocks the summary + links. */
  protocolId?: string | null;
  protocolRef?: string | null;
  protocolKind?: "move_in" | "move_out";
  hasPdf?: boolean;
  onOpenPdf?: (kind: "move_in" | "move_out") => void;
  unlocked: boolean;
  /** The tenant's window has closed (auto-accepted/auto-completed) without a submission. */
  expired?: boolean;
  /** Badge text while `expired` — e.g. "Closed" (move-in) or "Window closed" (move-out). */
  expiredLabel?: string;
  canOverride: boolean;
  overrideLabel?: string;
  /**
   * Admin may let the tenant back in: while the window is `expired`, and on an
   * operator recording (`adminRecorded`). Also gates "Close protocol for tenant"
   * while `reopened`.
   */
  canReopen?: boolean;
  reopening?: boolean;
  onReopen?: () => void;
  /**
   * The protocol is currently open because an admin manually reopened it
   * (indefinite, not the old 48h grace) and it hasn't been re-closed since.
   * Mutually exclusive with the reopen button: while this is true the card
   * offers "Close protocol for tenant" instead of it, and no "Replace
   * documents…". (The recording and override buttons are separate controls with
   * their own gates.)
   */
  reopened?: boolean;
  /**
   * An admin closed a reopened protocol before the tenant submitted it.
   * Purely cosmetic (overrides the status label/description) — closing
   * never blocks/unblocks anything beyond clearing the reopen stamp.
   */
  manuallyClosed?: boolean;
  closing?: boolean;
  onClose?: () => void;
  busy: boolean;
  onOverride?: () => void;
  /**
   * Set when an admin filed this protocol on the tenant's behalf. Drives the
   * whole card away from language that would claim a tenant walkthrough.
   */
  recordedByName?: string | null;
  recordedAt?: string | null;
  adminNote?: string | null;
  /**
   * Set when the tick deemed this protocol submitted because the window
   * closed with no walkthrough. Distinct from an admin recording: nobody
   * filed anything here either, the window just closed.
   */
  autoSubmittedAt?: string | null;
  /** Admin/super-admin: may record the protocol on the tenant's behalf. */
  canRecord?: boolean;
  onRecord?: () => void;
  /**
   * Admin/super-admin: may swap the document on a protocol an operator
   * recorded. Only ever rendered for that case — a tenant's own signed
   * walkthrough is never replaced from here.
   */
  canReplace?: boolean;
  onReplace?: () => void;
}) {
  const { t } = useLanguage();
  // Reopening asks first: it retires an operator's recording as "this kind is
  // done" and the tenant may be emailed reminders on the next tick.
  const [reopenConfirmOpen, setReopenConfirmOpen] = useState(false);
  /*
   * There is deliberately NO "confirmed manually" state here.
   *
   * This card used to derive one from `bookings.move_in_confirmed_at`, which
   * is the tenant-journey Active gate's evidence column ("this tenant has
   * moved in") — NOT a record of an admin confirming a protocol. Nothing
   * distinguishes the writers: the migration backfills stamped it from
   * `actual_move_in_date` / `move_in_date` / `start_date`, TenantEditPanel
   * stamps it when a super-admin types an actual move-in date, and
   * `useCreateBooking` stamps it for a tenant created already-active. So the
   * card claimed "Confirmed manually · marked complete manually <date>" for
   * bookings no admin had ever touched — and, because that branch sat above
   * `expired` and `unlocked` below, it also made "Awaiting tenant",
   * "Auto-accepted" and "Closed" unreachable for those rows: the badge read
   * complete while the tenant's window was still open, and kept reading
   * complete after it closed.
   *
   * The admin override that used to write that column directly is gone —
   * "Mark move-in complete" now opens the record-on-behalf dialog, which
   * files a real protocol and lands on `adminRecorded` ("Recorded by
   * operator"). Every genuine completion therefore has a protocol row behind
   * it, and the states below are derived from the protocol and the window
   * alone. Do not reintroduce a booking-column shortcut.
   */
  // An operator recording is done, but with a caveat, so it gets neither the
  // green "Submitted" of a real walkthrough nor a pending colour.
  const hasRecording = !!recordedByName || !!recordedAt;
  const adminRecorded = submitted && hasRecording;
  /**
   * An admin has let the tenant back in over an operator's recording.
   *
   * A recording filed BEFORE the reopen no longer stands (`isSupersededByReopen`
   * ranks by the moment it was filed), so the caller's `submitted` is false and
   * the card would otherwise fall through to a bare "Awaiting tenant" with no
   * mention of what is still on file. The recording and its PDF stay exactly
   * where they are — closing the reopen puts the card back on "Recorded by
   * operator".
   */
  const reopenedOverRecording = !submitted && !!reopened && hasRecording;
  // Same caveat, different actor: the tick deemed it submitted because the
  // window closed, not because an admin filed it. Checked after adminRecorded
  // so the two never both claim the card — an admin recording always wins if
  // both happened to be set.
  const autoSubmitted = submitted && !adminRecorded && !!autoSubmittedAt;
  /**
   * Collapsible, like every other section in the profile — and open by default
   * only while the handover is still outstanding.
   *
   * Same rule as the Contract section (`open = state !== "signed"`): a step that
   * is finished is a record to look up, and leaving it expanded costs a screen
   * of the tab; a step still waiting on someone is what the tab is open for. The
   * header keeps the status chip either way, so a collapsed card still says
   * where it stands.
   *
   * "Finished" means the document is actually on file. A row that claims
   * submitted with no PDF is the force-staged shape that blocks the deposit
   * bundle, and the repair action lives in this body — so that card stays open.
   *
   * A reopened placeholder counts as outstanding for the same reason: the step
   * is waiting on the tenant again, and the only control that undoes the
   * reopen ("Close protocol for tenant") lives in this body.
   */
  const [open, setOpen] = useState(
    submitted ? !hasPdf || (!!reopened && autoSubmitted) : true
  );
  /**
   * An admin has let the tenant back in, and the only thing on file is the
   * tick's deemed-accepted placeholder.
   *
   * That row carries `status = 'submitted'`, so without this the card kept
   * reading as filed after a reopen and the tenant's own card offered no way
   * in — the reopen looked like it worked from this side and did nothing on
   * theirs. The placeholder stays on file (it is the record of what happened,
   * and the deposit bundle needs it); the step simply goes back to waiting on
   * the tenant, which is what the reopen was for.
   *
   * Cannot collide with `adminRecorded`: `autoSubmitted` already requires
   * that to be false. An operator recording that a reopen has retired is a
   * different state with its own handling — see `reopenedOverRecording`.
   */
  const supersededByReopen = !!reopened && autoSubmitted;
  const statusChip =
    supersededByReopen || reopenedOverRecording
      ? CHIP.violet
      : adminRecorded || autoSubmitted
        ? CHIP.amber
        : submitted
          ? CHIP.green
          : expired
            ? CHIP.amber
            : unlocked
              ? CHIP.violet
              : CHIP.slate;
  const statusLabel =
    supersededByReopen || reopenedOverRecording
      ? t("admin.tenantTabs.protocol.awaitingTenant", "Awaiting tenant")
      : adminRecorded
        ? t(
            "admin.tenantTabs.protocol.recordedByOperator",
            "Recorded by operator"
          )
        : autoSubmitted
          ? // "Closed", not "Auto-submitted": the badge names the state of the
            // step — the tenant's window is shut — which is what an admin
            // scanning this tab needs. The contractual consequence is spelled
            // out in the body below, on the tenant's own card, and in the mail
            // sent when the window lapsed.
            t("admin.tenantTabs.protocol.closed", "Closed")
          : submitted
            ? t("admin.tenantTabs.protocol.submitted", "Submitted")
            : expired
              ? manuallyClosed
                ? t("admin.tenantTabs.protocol.closed", "Closed")
                : (expiredLabel ??
                  t("admin.tenantTabs.protocol.windowClosed", "Window closed"))
              : unlocked
                ? t(
                    "admin.tenantTabs.protocol.awaitingTenant",
                    "Awaiting tenant"
                  )
                : t("admin.tenantTabs.protocol.locked", "Locked");
  // What the walkthrough actually found. Only fetched once a protocol exists,
  // so an awaiting/locked card costs nothing.
  const { data: summary } = useProtocolSummary(protocolId ?? undefined);
  const desc = reopenedOverRecording
    ? t(
        "admin.tenantTabs.protocol.descReopenedRecording",
        "Reopened by an admin \u00b7 the tenant can complete the walkthrough themselves. The operator's recording stays on file until they submit."
      )
    : supersededByReopen
      ? `${t("admin.tenantTabs.protocol.descReopenedPrefix", "Reopened by an admin \u00b7 the tenant can complete the walkthrough again. The room was deemed accepted")} ${formatDateTime(autoSubmittedAt) ?? ""} ${t("admin.tenantTabs.protocol.descReopenedSuffix", "when the original window closed, and that record stays on file until they submit.")}`
      : adminRecorded
        ? // Never "signed by tenant & operator" here — that sentence would be a
          // false statement about a protocol nobody walked through.
          `${t("admin.tenantTabs.protocol.descRecordedPrefix", "Recorded")} ${formatDateTime(recordedAt) ?? ""} ${t("admin.tenantTabs.protocol.descRecordedBy", "by")} ${
            recordedByName ||
            t("admin.tenantTabs.protocol.anOperator", "an operator")
          } ${t("admin.tenantTabs.protocol.descRecordedSuffix", "on the tenant's behalf \u00b7 no tenant walkthrough or signature.")}${
            adminNote ? ` — "${adminNote}"` : ""
          }`
        : autoSubmitted
          ? // Same rule as the admin-recorded branch: no tenant ever walked through
            // this one, so it must not read like they did.
            `${t("admin.tenantTabs.protocol.descAutoSubmittedPrefix", "The window closed")} ${formatDateTime(autoSubmittedAt) ?? ""} ${t("admin.tenantTabs.protocol.descAutoSubmittedSuffix", "with no walkthrough \u00b7 the room is deemed accepted as per the rental contract, and a record of that is on file. No tenant walkthrough or signature. Reopen to let them complete one.")}`
          : submitted
            ? `${t("admin.tenantTabs.protocol.descSubmittedPrefix", "Submitted")} ${formatDateTime(submittedAt) ?? ""} ${t("admin.tenantTabs.protocol.descSubmittedSuffix", "\u00b7 signed by tenant & operator.")}`
            : expired
              ? manuallyClosed
                ? t(
                    "admin.tenantTabs.protocol.descManuallyClosed",
                    "An admin closed this protocol for the tenant before it was submitted. They can no longer complete this walkthrough unless it's reopened."
                  )
                : t(
                    "admin.tenantTabs.protocol.descExpired",
                    "The protocol window has closed. The tenant can no longer complete this walkthrough unless reopened."
                  )
              : unlocked
                ? t(
                    "admin.tenantTabs.protocol.descUnlocked",
                    "Tenant can now complete the walkthrough. You can also record it on their behalf."
                  )
                : t(
                    "admin.tenantTabs.protocol.descLocked",
                    "Available once the previous step is complete."
                  );
  return (
    <>
      <CollapsibleCard
        iconBg="#F1ECFE"
        iconColor="#8B5CF6"
        icon={<ClipboardIcon />}
        title={title}
        subtitle={subtitle}
        right={<span style={statusChip}>{statusLabel}</span>}
        open={open}
        onToggle={() => setOpen((v) => !v)}
      >
        <div style={{ padding: "18px 24px 20px" }}>
          {/* Wraps so every action stays reachable on a narrow viewport: the
              card clips its overflow, and with up to three nowrap buttons beside
              the description the last one fell off the edge at 390px. */}
          <div
            style={{
              ...innerBox,
              padding: "16px 18px",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 14,
            }}
          >
            <div
              style={{
                flex: "1 1 240px",
                fontSize: 13.5,
                color: "#475569",
                fontWeight: 500,
              }}
            >
              {desc}
            </div>
            {/*
             * Close and Reopen never render together: Close needs `reopened`,
             * Reopen needs `!reopened`. (`reopened` and `expired` also cannot
             * both be true — computeWindowStatus's "reopened" branch always
             * sets isExpired: false.)
             */}
            {reopened && canReopen && onClose && (
              <button
                onClick={onClose}
                disabled={busy}
                style={{
                  background: "#fff",
                  color: "#B91C1C",
                  border: "1px solid #FCA5A5",
                  borderRadius: 11,
                  padding: "10px 15px",
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: busy ? "default" : "pointer",
                  opacity: busy ? 0.7 : 1,
                  whiteSpace: "nowrap",
                }}
              >
                {closing
                  ? t(
                      "admin.tenantTabs.protocol.closingEllipsis",
                      "Closing\u2026"
                    )
                  : t(
                      "admin.tenantTabs.protocol.closeProtocolForTenant",
                      "Close protocol for tenant"
                    )}
              </button>
            )}
            {!reopened &&
              (expired || adminRecorded) &&
              canReopen &&
              onReopen && (
                <button
                  onClick={() => setReopenConfirmOpen(true)}
                  disabled={busy}
                  style={{
                    background: "#fff",
                    color: "#B45309",
                    border: "1px solid #FBBF24",
                    borderRadius: 11,
                    padding: "10px 15px",
                    fontWeight: 700,
                    fontSize: 13,
                    cursor: busy ? "default" : "pointer",
                    opacity: busy ? 0.7 : 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  {reopening
                    ? t(
                        "admin.tenantTabs.protocol.openingEllipsis",
                        "Opening\u2026"
                      )
                    : t(
                        "admin.tenantTabs.protocol.openProtocolForTenant",
                        "Open protocol for tenant"
                      )}
                </button>
              )}
            {/* Needs a document on file: a recording with none is the repair
                case, and "Record protocol…" below is what fixes it. */}
            {adminRecorded &&
              hasPdf &&
              !reopened &&
              canReplace &&
              onReplace && (
                <button
                  onClick={onReplace}
                  disabled={busy}
                  style={{
                    background: "#fff",
                    color: "#0F4C4A",
                    border: "1px solid #CBD5E1",
                    borderRadius: 11,
                    padding: "10px 15px",
                    fontWeight: 700,
                    fontSize: 13,
                    cursor: busy ? "default" : "pointer",
                    opacity: busy ? 0.7 : 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  {t(
                    "admin.tenantTabs.protocol.replaceProtocolEllipsis",
                    "Replace documents\u2026"
                  )}
                </button>
              )}
            {canRecord && onRecord && (
              <button
                onClick={onRecord}
                disabled={busy}
                style={{
                  background: "#fff",
                  color: "#0F4C4A",
                  border: "1px solid #CBD5E1",
                  borderRadius: 11,
                  padding: "10px 15px",
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: busy ? "default" : "pointer",
                  opacity: busy ? 0.7 : 1,
                  whiteSpace: "nowrap",
                }}
              >
                {t(
                  "admin.tenantTabs.protocol.recordProtocolEllipsis",
                  "Record protocol\u2026"
                )}
              </button>
            )}
            {canOverride && onOverride && (
              <button
                onClick={onOverride}
                disabled={busy}
                style={{
                  background: "#12B5A8",
                  color: "#fff",
                  border: "none",
                  borderRadius: 11,
                  padding: "10px 15px",
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: busy ? "default" : "pointer",
                  opacity: busy ? 0.7 : 1,
                  whiteSpace: "nowrap",
                }}
              >
                {overrideLabel ??
                  t(
                    "admin.tenantTabs.protocol.markSubmitted",
                    "Mark submitted"
                  )}
              </button>
            )}
          </div>

          {/* What the tenant actually reported, and the way through to it. Without
            this the card stated that a walkthrough happened and then made an
            admin leave the profile to learn anything about it — so the protocol
            may as well not have existed here. */}
          {protocolId && (
            <div
              style={{
                marginTop: 12,
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 10,
              }}
            >
              {protocolRef && (
                <span
                  style={{
                    fontFamily:
                      "ui-monospace, SFMono-Regular, Menlo, monospace",
                    fontSize: 12,
                    color: "#94A3B8",
                  }}
                >
                  {protocolRef}
                </span>
              )}
              {summary && (
                <span
                  style={{ fontSize: 12.5, color: "#475569", fontWeight: 500 }}
                >
                  {summary.photos}{" "}
                  {t("admin.tenantTabs.protocol.photoUnit", "photo")}
                  {summary.photos === 1 ? "" : "s"} {"\u00b7"} {summary.items}{" "}
                  {t("admin.tenantTabs.protocol.itemUnit", "item")}
                  {summary.items === 1 ? "" : "s"}
                  {summary.damaged > 0 || summary.wear > 0
                    ? ` (${[
                        summary.damaged
                          ? `${summary.damaged} ${t("admin.tenantTabs.protocol.damagedUnit", "damaged")}`
                          : null,
                        summary.wear
                          ? `${summary.wear} ${t("admin.tenantTabs.protocol.wearUnit", "wear")}`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(", ")})`
                    : ""}{" "}
                  {"\u00b7"} {summary.defects}{" "}
                  {t("admin.tenantTabs.protocol.defectUnit", "defect")}
                  {summary.defects === 1 ? "" : "s"}
                </span>
              )}
              {/* A high-severity defect is the one thing on this card that should
                stop an admin scrolling past it. */}
              {!!summary?.highSeverityDefects && (
                <span
                  style={{
                    ...CHIP.rose,
                    fontSize: 11.5,
                  }}
                >
                  {summary.highSeverityDefects}{" "}
                  {t("admin.tenantTabs.protocol.highSeverity", "high severity")}
                </span>
              )}
              <span style={{ flex: 1 }} />
              <Link
                to={`/admin/protocols/${protocolId}`}
                style={{
                  fontSize: 12.5,
                  fontWeight: 700,
                  color: "#0F4C4A",
                  textDecoration: "none",
                  border: "1px solid #D8DEE4",
                  borderRadius: 10,
                  padding: "7px 12px",
                  whiteSpace: "nowrap",
                }}
              >
                {t("admin.tenantTabs.protocol.viewProtocol", "View protocol")}
              </Link>
              {hasPdf && onOpenPdf && protocolKind && (
                <button
                  onClick={() => onOpenPdf(protocolKind)}
                  style={{
                    fontSize: 12.5,
                    fontWeight: 700,
                    color: "#0F4C4A",
                    background: "#fff",
                    border: "1px solid #D8DEE4",
                    borderRadius: 10,
                    padding: "7px 12px",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("admin.tenantTabs.protocol.openPdf", "Open PDF")}
                </button>
              )}
            </div>
          )}
        </div>
      </CollapsibleCard>

      {/* Reopening is a decision, not a toggle: it lets the tenant back in with no
        deadline, retires an operator's recording as "this step is done", and
        the tenant may be emailed reminders on the next tick. */}
      <AlertDialog open={reopenConfirmOpen} onOpenChange={setReopenConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(
                "admin.tenantTabs.protocol.reopenConfirmTitle",
                "Reopen this protocol for the tenant?"
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {`${t(
                "admin.tenantTabs.protocol.reopenConfirmBody",
                "The tenant can complete the walkthrough themselves again, with no deadline, and may receive reminder emails. You can close it for them again at any time."
              )}${
                adminRecorded
                  ? ` ${t(
                      "admin.tenantTabs.protocol.reopenConfirmRecordedNote",
                      "The operator's recording stays on file until the tenant submits their own."
                    )}`
                  : ""
              }`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>
              {t("admin.tenantTabs.cancelButton", "Cancel")}
            </AlertDialogCancel>
            <AlertDialogAction disabled={busy} onClick={() => onReopen?.()}>
              {t(
                "admin.tenantTabs.protocol.reopenConfirmAction",
                "Reopen protocol"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/* -------------------- small presentational helpers -------------------- */

function SectionHead({
  iconBg,
  iconColor,
  icon,
  title,
}: {
  iconBg: string;
  iconColor: string;
  icon: ReactNode;
  title: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        marginBottom: 18,
      }}
    >
      <SectionIcon bg={iconBg} color={iconColor}>
        {icon}
      </SectionIcon>
      <h3
        style={{
          margin: 0,
          fontSize: 17,
          fontWeight: 700,
          letterSpacing: "-.01em",
        }}
      >
        {title}
      </h3>
    </div>
  );
}

function InfoCell({
  label,
  value,
  icon,
  href,
  onClick,
  borderRight,
  borderBottom,
  optional,
}: {
  label: string;
  value: string;
  icon: ReactNode;
  /** When set (and value present), the value becomes a link (e.g. mailto:). */
  href?: string;
  /** When set (and value present), the value becomes a button (e.g. phone → modal). */
  onClick?: () => void;
  borderRight?: boolean;
  borderBottom?: boolean;
  /**
   * A field that is legitimately empty for most records (collected only since a
   * recent form change). An empty one reads "Not specified" in neutral grey
   * instead of the amber Missing chip, which is reserved for gaps to chase.
   */
  optional?: boolean;
}) {
  const { t } = useLanguage();
  const missing = !value || value.trim() === "" || value.trim() === "—";
  const unasked = missing && !!optional;
  return (
    <div
      style={{
        padding: "18px 20px",
        borderRight: borderRight ? "1px solid #F0F1F3" : undefined,
        borderBottom: borderBottom ? "1px solid #F0F1F3" : undefined,
        background: missing && !unasked ? "#FFFBEB" : undefined,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            color:
              missing && !unasked
                ? "hsl(var(--destructive))"
                : "hsl(var(--petrol))",
          }}
        >
          <span style={{ display: "flex", flexShrink: 0 }}>{icon}</span>
          <span style={uppLabel}>{label}</span>
        </div>
        {unasked ? (
          <div
            style={{
              marginTop: 3,
              marginLeft: 20,
              fontSize: 15,
              fontWeight: 600,
              color: "#94A3B8",
            }}
          >
            {t("admin.tenantTabs.notSpecified", "Not specified")}
          </div>
        ) : missing ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              marginTop: 3,
              marginLeft: 20,
            }}
          >
            <span style={{ fontSize: 15, fontWeight: 600, color: "#CBD5E1" }}>
              —
            </span>
            <span style={chip("#FEF6E7", "#B45309", "#F7E4BD")}>
              {t("admin.tenantTabs.missingChip", "Missing")}
            </span>
          </div>
        ) : onClick ? (
          <button
            type="button"
            onClick={onClick}
            aria-label={`${label}: ${value} — ${t("admin.tenantTabs.openContactOptions", "open contact options")}`}
            style={{
              display: "block",
              padding: 0,
              border: "none",
              background: "none",
              cursor: "pointer",
              textAlign: "left",
              fontSize: 15,
              fontWeight: 600,
              marginTop: 3,
              marginLeft: 20,
              maxWidth: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: "#0B7268",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.textDecoration = "underline";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.textDecoration = "none";
            }}
          >
            {value}
          </button>
        ) : href ? (
          <a
            href={href}
            style={{
              display: "block",
              fontSize: 15,
              fontWeight: 600,
              marginTop: 3,
              marginLeft: 20,
              overflow: "hidden",
              textOverflow: "ellipsis",
              color: "#0B7268",
              textDecoration: "none",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.textDecoration = "underline";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.textDecoration = "none";
            }}
          >
            {value}
          </a>
        ) : (
          <div
            style={{
              fontSize: 15,
              fontWeight: 600,
              marginTop: 3,
              marginLeft: 20,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {value}
          </div>
        )}
      </div>
    </div>
  );
}

function LeaseCell({
  label,
  value,
  href,
  borderRight,
}: {
  label: string;
  value: string;
  /** When set, the value becomes a link to this in-app route. */
  href?: string;
  borderRight?: boolean;
}) {
  const valueStyle: React.CSSProperties = {
    fontSize: 15,
    fontWeight: 700,
    marginTop: 6,
    lineHeight: 1.3,
  };
  return (
    <div
      style={{
        padding: "18px 20px",
        borderRight: borderRight ? "1px solid #F0F1F3" : undefined,
      }}
    >
      <div style={uppLabel}>{label}</div>
      {href ? (
        <Link
          to={href}
          style={{
            ...valueStyle,
            display: "inline-block",
            color: "#0B7268",
            textDecoration: "none",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.textDecoration = "underline";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.textDecoration = "none";
          }}
        >
          {value}
        </Link>
      ) : (
        <div style={valueStyle}>{value}</div>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  color,
  sub,
}: {
  label: string;
  value: string;
  color?: string;
  /** Optional detail line under the figure. */
  sub?: ReactNode;
}) {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #ECEEF1",
        borderRadius: 18,
        padding: "18px 20px",
      }}
    >
      <div style={uppLabel}>{label}</div>
      <div
        style={{
          fontSize: 24,
          fontWeight: 800,
          marginTop: 6,
          letterSpacing: "-.02em",
          color,
        }}
      >
        {value}
      </div>
      {sub}
    </div>
  );
}

/**
 * COL-472: "EUR 350.00 rent · EUR 40.00 late fees" under a figure that now
 * includes Mahngebühren, so an admin can see what the total is made of.
 */
function RentFeeSplit({
  rentCents,
  feeCents,
  billedCents,
  otherCents = 0,
}: {
  rentCents: number;
  feeCents: number;
  /** Open non-rent rows (e.g. an unpaid deposit), shown as their own part. */
  otherCents?: number;
  /** Set on a part-paid month: reads "EUR 350.00 of EUR 650.00 rent". */
  billedCents?: number;
}) {
  const { t } = useLanguage();
  return (
    <div
      style={{
        fontSize: 12,
        color: "#94A3B8",
        fontWeight: 500,
        marginTop: 6,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {/* Rent settled, fees not: the fees are the whole figure. */}
      {(rentCents > 0 || billedCents != null) && (
        <>
          {centsToEUR(rentCents)}
          {billedCents != null && (
            <>
              {" "}
              {t("admin.tenantTabs.ofBilled", "of")} {centsToEUR(billedCents)}
            </>
          )}{" "}
          {t("admin.tenantTabs.rentWord", "rent")} ·{" "}
        </>
      )}
      {otherCents > 0 && (
        <>
          {centsToEUR(otherCents)}{" "}
          {t("admin.tenantTabs.otherOpenWord", "other")} ·{" "}
        </>
      )}
      <span style={{ color: "#B45309", fontWeight: 600 }}>
        {centsToEUR(feeCents)} {t("admin.tenantTabs.dunningFees", "late fees")}
      </span>
    </div>
  );
}

/** Cubic-ease count-up between value changes (browser rAF; app-only). */
function useCountUp(target: number, durationMs = 600): number {
  const [val, setVal] = useState(target);
  const fromRef = useRef(target);
  const rafRef = useRef<number | null>(null);
  useEffect(() => {
    const from = fromRef.current;
    const to = target;
    if (from === to) return;
    let start: number | null = null;
    const step = (ts: number) => {
      if (start == null) start = ts;
      const t = Math.min(1, (ts - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(Math.round(from + (to - from) * eased));
      if (t < 1) rafRef.current = requestAnimationFrame(step);
      else fromRef.current = to;
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target, durationMs]);
  return val;
}

/**
 * "Next due" summary card (feedback #11): shows the next unpaid/scheduled rent,
 * count-up animated, with a chip comparing it to the previous month's rent.
 */
function NextDueCard({
  row,
  prevAmountCents,
  settlement,
  feeCents = 0,
}: {
  row: any | null;
  prevAmountCents: number | null;
  /** COL-472: Mahngebühren carried by this month (0 when it carries none). */
  feeCents?: number;
  /** COL-417: expected / received / remaining for `row`, when it is known. */
  settlement?: {
    expectedCents: number;
    receivedCents: number;
    remainingCents: number;
    isPartial: boolean;
  } | null;
}) {
  const { t, language } = useLanguage();
  const billed = row ? row.amount_cents || 0 : 0;
  // COL-417: the headline is what is still collectable. On a part-paid month
  // the billed figure is no longer the ask, and an admin reading "next due"
  // is deciding what to chase, not re-reading the invoice.
  const partial = Boolean(settlement?.isPartial);
  const rentTarget = partial ? (settlement?.remainingCents ?? billed) : billed;
  const owed = owedWithFees({
    rentOpenCents: rentTarget,
    feesEur: feeCents / 100,
    carriesFees: true,
  });
  const display = useCountUp(owed.totalCents, 600);
  // Compared against the previous month's BILLED rent, so the chip keeps
  // meaning "the rent changed" rather than "some of it has been paid" — which
  // the received line right below already says.
  const diff =
    !partial && row && prevAmountCents != null
      ? billed - prevAmountCents
      : null;
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #ECEEF1",
        borderRadius: 18,
        padding: "18px 20px",
      }}
    >
      <div style={uppLabel}>
        {t("admin.tenantTabs.nextDueLabel", "Next due")}
      </div>
      {row ? (
        <>
          <div
            style={{
              fontSize: 24,
              fontWeight: 800,
              marginTop: 6,
              letterSpacing: "-.02em",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {centsToEUR(display)}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 6,
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontSize: 12, color: "#94A3B8", fontWeight: 500 }}>
              {monthLabel(row.due_date, language)}
            </span>
            {partial && settlement && owed.feeCents === 0 && (
              <span style={{ fontSize: 12, color: "#1D4ED8", fontWeight: 600 }}>
                {t("admin.tenantTabs.ofBilled", "of")}{" "}
                {centsToEUR(settlement.expectedCents)}{" "}
                {t("admin.tenantTabs.stillOpen", "still open")}
              </span>
            )}
            {/* Only surface the comparison when the amount actually changed. */}
            {owed.feeCents === 0 && diff != null && diff !== 0 && (
              <span
                title={t(
                  "admin.tenantTabs.vsPreviousMonth",
                  "vs previous month"
                )}
                style={diff > 0 ? CHIP.amber : CHIP.green}
              >
                {diff > 0
                  ? `↑ ${centsToEUR(diff)}`
                  : `↓ ${centsToEUR(Math.abs(diff))}`}
              </span>
            )}
          </div>
          {owed.feeCents > 0 && (
            <RentFeeSplit
              rentCents={owed.rentCents}
              feeCents={owed.feeCents}
              billedCents={
                partial && settlement ? settlement.expectedCents : undefined
              }
            />
          )}
        </>
      ) : (
        <div
          style={{
            fontSize: 24,
            fontWeight: 800,
            marginTop: 6,
            color: "#CBD5E1",
          }}
        >
          —
        </div>
      )}
    </div>
  );
}

/**
 * Small copy/call modal for a contact value (phone). Avoids a raw tel: link,
 * which pops the OS "Open FaceTime?" prompt on desktop. Overlays the profile
 * dialog (absolute inset, matching the handover-note modal).
 */
function ContactModal({
  label,
  value,
  onClose,
}: {
  label: string;
  value: string;
  onClose: () => void;
}) {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);
  const valueRef = useRef<HTMLDivElement>(null);
  const tel = `tel:${value.replace(/[^\d+]/g, "")}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API blocked (non-HTTPS / older browser): select the number
      // text so it can be copied manually.
      const el = valueRef.current;
      if (el) {
        const range = document.createRange();
        range.selectNodeContents(el);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
    }
  };
  return (
    <div
      onClick={onClose}
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 40,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(9,15,28,.55)",
        backdropFilter: "blur(2px)",
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 400,
          background: "#fff",
          borderRadius: 20,
          boxShadow: "0 30px 80px -20px rgba(9,15,28,.5)",
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "22px 24px" }}>
          <div style={uppLabel}>{label}</div>
          <div
            ref={valueRef}
            style={{
              fontSize: 26,
              fontWeight: 800,
              letterSpacing: "-.01em",
              marginTop: 8,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {value}
          </div>
          <div
            style={{
              display: "flex",
              gap: 10,
              marginTop: 18,
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={() => void copy()}
              style={{
                background: "#12B5A8",
                color: "#fff",
                border: "none",
                borderRadius: 11,
                padding: "9px 16px",
                fontWeight: 700,
                fontSize: 13.5,
                cursor: "pointer",
              }}
            >
              {copied
                ? t("admin.tenantTabs.copiedCheck", "Copied \u2713")
                : t("admin.tenantTabs.copyNumberButton", "Copy number")}
            </button>
            <a
              href={tel}
              style={{
                background: "#fff",
                color: "#0E7490",
                border: "1px solid #E7E9EC",
                borderRadius: 11,
                padding: "9px 16px",
                fontWeight: 700,
                fontSize: 13.5,
                textDecoration: "none",
              }}
            >
              {t("admin.tenantTabs.callButton", "Call")}
            </a>
            <button
              onClick={onClose}
              style={{
                background: "none",
                color: "#64748B",
                border: "none",
                borderRadius: 11,
                padding: "9px 12px",
                fontWeight: 600,
                fontSize: 13.5,
                cursor: "pointer",
                marginLeft: "auto",
              }}
            >
              {t("admin.tenantTabs.closeButton", "Close")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function docStatusChip(status: string): React.CSSProperties {
  const s = status.toLowerCase();
  if (s === "approved" || s === "signed") return CHIP.green;
  if (s === "rejected") return chip("#FEECEC", "#B91C1C", "#F7CFCF");
  if (s === "sent" || s === "pending") return CHIP.amber;
  return CHIP.slate2;
}

/* -------------------- inline icons (lucide geometry) -------------------- */

function svg(path: ReactNode, size = 19) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {path}
    </svg>
  );
}
const UserIcon = ({ size = 19 }: { size?: number }) =>
  svg(
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21v-1a7 7 0 0 1 14 0v1" />
    </>,
    size
  );
const CakeIcon = ({ size = 18 }: { size?: number }) =>
  svg(
    <>
      <path d="M12 2a3 3 0 0 0-3 3a3 3 0 0 0 6 0a3 3 0 0 0-3-3Z" />
      <path d="M19 8v3M5 8v3M12 15v5M8 20h8" />
    </>,
    size
  );
const GenderIcon = ({ size = 18 }: { size?: number }) =>
  svg(
    <>
      <path d="M12 15v7M9 19h6" />
      <circle cx="12" cy="9" r="6" />
    </>,
    size
  );
const FlagIcon = ({ size = 18 }: { size?: number }) =>
  svg(
    <>
      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
      <path d="M4 22v-7" />
    </>,
    size
  );
const LandmarkIcon = ({ size = 18 }: { size?: number }) =>
  svg(
    <>
      <path d="M3 22h18M6 18v-7M10 18v-7M14 18v-7M18 18v-7" />
      <path d="M12 2 20 7H4z" />
    </>,
    size
  );
const MailIcon = ({ size = 18 }: { size?: number }) =>
  svg(
    <>
      <rect x="2" y="4" width="20" height="16" rx="3" />
      <path d="m3 6 9 6 9-6" />
    </>,
    size
  );
const PhoneIcon = ({ size = 18 }: { size?: number }) =>
  svg(
    <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.1-8.6A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.4-1.2a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2Z" />,
    size
  );
const BuildingIcon = () =>
  svg(
    <>
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <path d="M9 22v-4h6v4M8 6h.01M12 6h.01M16 6h.01M8 10h.01M12 10h.01M16 10h.01" />
    </>
  );
const CardIcon = () =>
  svg(
    <>
      <rect x="2" y="5" width="20" height="14" rx="3" />
      <path d="M2 10h20" />
    </>
  );
const WalletIcon = () =>
  svg(
    <>
      <rect x="3" y="4" width="18" height="16" rx="3" />
      <path d="M3 9h18" />
    </>
  );
const EuroIcon = () =>
  svg(<path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />);
const ClockIcon = () =>
  svg(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  );
const KeyIcon = () =>
  svg(
    <>
      <circle cx="8" cy="15" r="4" />
      <path d="m10.5 12.5 6.5-6.5 3 3M14 7l3 3" />
    </>
  );
const CalendarIcon = () =>
  svg(
    <>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M3 10h18M8 2v4M16 2v4" />
    </>
  );
const ExitIcon = () =>
  svg(
    <>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
    </>,
    20
  );
const BellIcon = () =>
  svg(
    <>
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </>
  );
const RefreshIcon = () =>
  svg(
    <>
      <path d="M21 12a9 9 0 1 1-3-6.7L21 8" />
      <path d="M21 3v5h-5" />
    </>,
    18
  );
const ClipboardIcon = () =>
  svg(
    <>
      <path d="M9 11l3 3 8-8" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </>
  );
const CheckIcon = () => svg(<path d="M20 6 9 17l-5-5" />, 16);
const SearchIcon = () =>
  svg(
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.2-3.2" />
    </>,
    15
  );
const PinIcon = ({ size = 13 }: { size?: number }) =>
  svg(
    <>
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
      <circle cx="12" cy="10" r="3" />
    </>,
    size
  );
const FileIcon = ({ size = 19 }: { size?: number }) =>
  svg(
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
    </>,
    size
  );
